import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import DaletChar from '../components/DaletChar';
import { useAuth } from '../auth/AuthContext';
import { usePractice } from '../context/PracticeContext';
import axios from '../utils/axiosInstance';
import moment from 'moment';
import { getCallReasonConfig, getCallReasonLabel, CALL_REASON_CONFIG } from '../utils/callReasonColors';
import { escapeRowKey, escapeLabelFromKey } from '../utils/escapeReasonColors';
import DateRangeDrawer from '../components/DateRangeDrawer';
import { formatDeRange, formatDeRangeFull, isoDay, PERIOD_OPTIONS, PERIOD_VALUES, resolvePeriodRange } from '../utils/dateRange';
import { usePracticeScope } from '../hooks/usePracticeScope';
import PracticeScopeToggle from '../components/PracticeScopeToggle';
import ReceptionStatusWidget from '../components/ReceptionStatusWidget';
import { fetchPracticeTasks, mergeTaskLists } from '../utils/tasksData';
import useSupportStatus from '../hooks/useSupportStatus';
import { TaskSupportIcon } from '../components/support/supportIcons';
import { realValue } from '../components/support/supportAdapter';
import { conversationCopyText, openTrackerTicketWindow } from '../utils/taskLinks';

// API base URL from environment variable
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Stable identity for the not-yet-loaded member map: it is a useMemo dependency,
// so a fresh {} per render would re-run the derivations every render.
const NO_MEMBERS_BY_PRACTICE = {};

// Shape check for the deep-link params (?practice=&session=&task=).
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// One practice's assignable members. exclude_creator=true drops the practice's
// super-admin creator (the platform admin) from the assignee list for everyone —
// the platform admin should never be offered as a task assignee.
const fetchPracticeMembers = async (practiceId) => {
  const response = await axios.get(`${API_BASE_URL}/practices/${practiceId}/members?exclude_creator=true`);
  return (response.data || []).map((m) => ({
    id: m.user_id,
    username: m.username,
    firstName: m.first_name || '',
    lastName: m.last_name || '',
    // Owners can untick "Zuweisbar" for someone who should read the task list but
    // never be assigned work. Missing flag (older backend) = true.
    assignable: m.assignable !== false,
  }));
};

// German translations for call reasons (shared with DashboardPage via callReasonColors.js)
const getGermanCallReason = (reason) => getCallReasonLabel(reason);

const formatCallReasons = (reasons) =>
  Array.isArray(reasons) && reasons.length > 0
    ? reasons.map(getGermanCallReason).join(', ')
    : '';

// Render call reasons as non-editable pill badges using the shared color map.
const renderCallReasonBadges = (reasons) => {
  if (!Array.isArray(reasons) || reasons.length === 0) return null;
  return reasons.map((reason, idx) => {
    const cfg = getCallReasonConfig(reason);
    return (
      <span
        key={`${reason}-${idx}`}
        className="grund-badge"
        style={{ backgroundColor: cfg.bg, color: cfg.fg }}
      >
        {cfg.label}
      </span>
    );
  });
};

// Single source of truth for task formatting (snake_case → camelCase)
const formatTask = (task) => {
  const names = task.caller_name || [];
  const isExistingPatient = Array.isArray(names) && names.includes('__pvs__');
  const cleanNames = isExistingPatient ? names.filter(n => n !== '__pvs__') : names;

  return {
    id: task.id,
    // Which practice this task belongs to. Always present on the wire; read while
    // pooling several practices, to label the row and to scope BOTH its assignee
    // picker and its phone cluster to its own tenant.
    practiceId: task.practice_id,
    callerName: cleanNames,  // List of name variations: ["Max Müller", "Max Mueller"]
    isExistingPatient,       // true when caller was identified in PVS
    phoneNumber: task.phone_number,
    callerIdNumber: task.caller_id_number,  // Raw caller ID; read-only, shown only when it differs from booked phone
    pvsMobileNumber: task.pvs_mobile_number || null,  // Mobile used as the SMS/WhatsApp target — spoken by the caller or adopted from the PVS profile (null when it just repeats phone_number)
    description: task.description,
    status: task.status,
    createdAt: task.created_at || '',
    dueDate: task.due_date ? new Date(task.due_date).toISOString().substr(0, 10) : '',
    callerBirthdate: task.caller_birthdate,
    callerEmail: task.caller_email,
    callerWhatsappOptin: typeof task.caller_whatsapp_optin === 'boolean' ? task.caller_whatsapp_optin : null,
    hasVerordnung: task.has_verordnung,
    verordnungType: task.verordnung_type,
    verordnungSessions: task.verordnung_sessions,
    verordnungIssueDate: task.verordnung_issue_date,
    insuranceType: task.insurance_type,
    isBlanko: task.is_blanko === true,  // Blankorezept preset therapy; read-only (never written back)
    appointmentPreference: task.appointment_preference,  // Concise "Terminwunsch"; read-only (never written back)
    assigneeId: task.assignee_id,
    _rawCallerName: task.caller_name || [],  // Preserve raw names (with __pvs__) for API writes
    sessionId: task.session_id,
    conversationQuality: task.conversation_quality,
    perceivedLatencyMean: task.perceived_latency_mean,
    perceivedLatencyMax: task.perceived_latency_max,
    callReasons: task.call_reasons,
    // SmartAB handover of the underlying call (both null when it never escaped).
    escapeCode: task.escape_code,
    escapeReason: task.escape_reason,
    hasConsent: task.has_consent,
    // True when the task has failed actions the backend can replay (derived
    // from the hidden action_log) — shows the admin-only retry button.
    hasFailedActions: typeof task.has_failed_actions === 'boolean' ? task.has_failed_actions : null,
    // Same, caller SMS / E-Mail only — shows the staff "Benachrichtigungen erneut senden" button.
    hasUnsentNotification: typeof task.has_unsent_notification === 'boolean' ? task.has_unsent_notification : null,
    // One of them is a caller e-mail whose address is not one: the button asks for it first.
    unsentEmailNeedsAddress: task.unsent_email_needs_address === true,
    // Metadata of the messages this call actually sent ([{id, channel, line}],
    // derived from the same hidden action_log). Bodies are NOT here — they are
    // fetched per task from /tasks/{id}/sent-messages when the popup opens.
    sentMessages: Array.isArray(task.sent_messages) ? task.sent_messages : null,
    // Stored DOC-IQ form PDFs of this call ([{form_id, doc_id, type, filename_label, line}]), null = none.
    docIqDocuments: Array.isArray(task.doc_iq_documents) ? task.doc_iq_documents : null,
    // Late-listener window: lines whose worker request is still in flight
    // (spinner instead of ⚠️) and whether that is all that is open. NOT merged
    // with ?? in mergePreservingStats: null is the "window closed" verdict.
    processingLines: Array.isArray(task.processing_lines) ? task.processing_lines : null,
    deskbotProcessing: task.deskbot_processing === true,
    // Tasks v2, all read-only and sent on every payload (list, PUT, PATCH, WS).
    // NOT merged with ?? in mergePreservingStats: a heal that removes the
    // failure to-do must not bring it back from the old copy.
    todos: Array.isArray(task.todos) ? task.todos : [],
    callLog: Array.isArray(task.call_log) ? task.call_log : [],
    // A "Verpasster Anruf" row; stays true after staff change its status.
    missedRow: task.missed_row === true,
    // Automation score of the row's call in %, sent on the task list for a super_admin only.
    automationPct: typeof task.automation_pct === 'number' ? task.automation_pct : null,
  };
};

// When the row's call came in: its own call's start ("dieses Gespräch"), on a
// missed-call row its first call's start (the backend sorts the lines by start),
// else the creation time (a task added by hand). The date column, the "Anrufe
// heute" caption and the list order all read it, so a call shows one time.
const callStartOf = (task) => {
  const log = task.callLog || [];
  const own = task.missedRow ? log[0] : log.find(e => e.kind === 'self');
  return own?.at || task.createdAt;
};
const callStartMs = (task) => Date.parse(callStartOf(task)) || 0;

// Byte-identical to summary_renderer.HINWEIS_CHECK: the backend appends it to the
// prose of a task with a failed PVS action. While Deskbot is still processing
// that action it is shown as PROCESSING_HINWEIS instead (display only — the
// stored description keeps it, so it is there the moment the window closes).
const HINWEIS_CHECK = '[HINWEIS: Bitte überprüfen, dass die Aufgabe wirklich erledigt wurde]';
const PROCESSING_HINWEIS = '[HINWEIS: Deskbot bearbeitet diese Aufgabe noch]';
// The backend window is PVS_LATE_WIRE_WINDOW (5 min) from the request, which
// precedes task creation. The client cap only matters when the closing
// broadcast is lost (WS drop, backend restart) — never spin forever.
const PROCESSING_MAX_MS = 6 * 60 * 1000;
const processingDeadline = (task) =>
  task.createdAt ? new Date(task.createdAt).getTime() + PROCESSING_MAX_MS : 0;
const isProcessingWindowOpen = (task) => Date.now() < processingDeadline(task);
// In-flight lines of a task ([] once the window closed).
const inFlightLines = (task) => (task.processingLines && isProcessingWindowOpen(task)) ? task.processingLines : [];
// Every open PVS failure is still in flight. Only over 'pending' — a status
// staff (or forwarding) set is never relabelled.
const isDeskbotProcessing = (task) =>
  task.deskbotProcessing && task.status === 'pending' && isProcessingWindowOpen(task);
// The badge shows the existing 'processing_by_deskbot' status meanwhile. Display
// only: the stored status stays 'pending' (what it becomes on failure), so
// filters, dashboard and statistics are unaffected.
const shownStatus = (task) => (isDeskbotProcessing(task) ? 'processing_by_deskbot' : task.status);

// --- Editing an unsent SMS / E-Mail in the message viewer --------------------
// Mirror of notification_service._ascii_sms (the backend folds again at send):
// umlauts transliterate, other marks drop off their letter, any other non-ASCII
// character is removed. Drives only the SMS counter.
const SMS_FOLD = { 'ä': 'ae', 'ö': 'oe', 'ü': 'ue', 'Ä': 'Ae', 'Ö': 'Oe', 'Ü': 'Ue', 'ß': 'ss', '→': '->' };
const foldSms = (text) => [...text.replace(/[äöüÄÖÜß→]/g, c => SMS_FOLD[c]).normalize('NFKD')]
  .filter(c => c.charCodeAt(0) < 128).join('');
// GSM-7 counts [ ] { } \ ^ ~ | twice; one SMS holds 160, a longer one 153 per part.
const smsLength = (text) => text.length + (text.match(/[[\]{}\\^~|]/g) || []).length;
const smsParts = (len) => (len <= 160 ? 1 : Math.ceil(len / 153));
// Every link of the stored text must survive an edit (the backend refuses otherwise).
const linksKept = (stored, draft) => (stored.match(/https?:\/\/\S+/g) || []).every(l => draft.includes(l));

// --- To-dos ("Zu tun") ------------------------------------------------------
// The server renders and decides every to-do (routers/tasks.py, task_todos.py).
// The helpers below only mirror its two status rules for the optimistic paint;
// the PATCH/PUT response replaces the result.
const CLOSED_TASK_STATUSES = new Set(['completed', 'completed_by_deskbot']);
// The failure to-do is in_flight while every failure it covers still waits for
// its answer; meanwhile it shows as "Deskbot bearbeitet diese Aufgabe noch".
// The first answer (or the client cap) shows it.
const isTodoShown = (task, td) => !td.in_flight || !isProcessingWindowOpen(task);
// Rule 1 (tick): the last open item ticked closes the task, an untick on a
// closed task reopens it. An in-flight failure counts as open.
const applyTodoTick = (task, todoId, done, byName) => {
  const at = new Date().toISOString();
  const todos = task.todos.map(td => (td.id !== todoId ? td : done
    ? { ...td, state: 'done', closed_by: byName || null, closed_at: at, closed_how: 'tick' }
    : { ...td, state: 'open', closed_by: null, closed_at: null, closed_how: null }));
  let { status } = task;
  if (done && !todos.some(td => td.state === 'open')
    && !CLOSED_TASK_STATUSES.has(status) && status !== 'Deleted' && status !== 'verpasster_anruf') {
    status = 'completed';
  } else if (!done && CLOSED_TASK_STATUSES.has(status)) {
    status = 'pending';
  }
  return { ...task, todos, status };
};
// Delete (Rule 1 too): the item leaves the list; when only ticked items remain,
// every box is checked and an open task completes. With none left it stays.
const applyTodoDelete = (task, todoId) => {
  const todos = task.todos.filter(td => td.id !== todoId);
  let { status } = task;
  if (todos.length && !todos.some(td => td.state === 'open')
    && !CLOSED_TASK_STATUSES.has(status) && status !== 'Deleted' && status !== 'verpasster_anruf') {
    status = 'completed';
  }
  return { ...task, todos, status };
};
// Rule 2 (status change): closing the task leaves its to-dos as they are (staff
// tick what was really done); reopening it restores the items an earlier close
// ticked "with the task" (rows closed before 07.10).
const todosAfterStatusChange = (todos, oldStatus, newStatus) => {
  if (!todos || !todos.length) return todos;
  if (CLOSED_TASK_STATUSES.has(oldStatus) && !CLOSED_TASK_STATUSES.has(newStatus) && newStatus !== 'Deleted') {
    return todos.map(td => (td.closed_how !== 'with_task' ? td
      : { ...td, state: 'open', closed_by: null, closed_at: null, closed_how: null }));
  }
  return todos;
};

// Significant digits of a phone number: formatting stripped, leading +/0/00
// country-or-trunk prefix dropped, so "0160 972 34567", "+4916097234567" and
// "004916097234567" all reduce to comparable digit strings. Shared by
// callerIdDiffers (display) and the clustering keys below.
const phoneSignificantDigits = (s) => {
  if (!s) return '';
  let d = String(s).replace(/[\s\-().+]/g, '');
  if (d.startsWith('00')) d = d.slice(2); else if (d.startsWith('0')) d = d.slice(1);
  return d.replace(/\D/g, '');
};

// True when the raw caller ID is meaningfully different from the booked phone
// number (compares the last up-to-9 significant digits, ignoring formatting and
// leading 0/00/+ country prefixes). Used to surface "Anrufnummer"
// only when it adds information. Empty caller ID => never show.
const callerIdDiffers = (callerId, booked) => {
  const a = phoneSignificantDigits(callerId), b = phoneSignificantDigits(booked);
  if (!a) return false; if (!b) return true;
  const n = Math.min(a.length, b.length, 9); if (n === 0) return true; return a.slice(-n) !== b.slice(-n);
};

// Merge an incoming formatted task onto an existing one while preserving
// call_statistics-derived fields (conversation_quality, perceived_latency_*,
// call_reasons, escape_code/escape_reason) that the backend's task_updated
// payload / PUT response does NOT include. Without this, those fields would be
// wiped from the row until the next full refetch.
const mergePreservingStats = (existing, formattedIncoming) => ({
  ...existing,
  ...formattedIncoming,
  conversationQuality: formattedIncoming.conversationQuality ?? existing.conversationQuality,
  perceivedLatencyMean: formattedIncoming.perceivedLatencyMean ?? existing.perceivedLatencyMean,
  perceivedLatencyMax: formattedIncoming.perceivedLatencyMax ?? existing.perceivedLatencyMax,
  callReasons: formattedIncoming.callReasons ?? existing.callReasons,
  escapeCode: formattedIncoming.escapeCode ?? existing.escapeCode,
  escapeReason: formattedIncoming.escapeReason ?? existing.escapeReason,
  hasConsent: formattedIncoming.hasConsent ?? existing.hasConsent,
  hasFailedActions: formattedIncoming.hasFailedActions ?? existing.hasFailedActions,
  hasUnsentNotification: formattedIncoming.hasUnsentNotification ?? existing.hasUnsentNotification,
  automationPct: formattedIncoming.automationPct ?? existing.automationPct,
});

// Small automation ring for the admin Stats column (same idea as the dashboard ring).
const AutomationRing = ({ pct }) => {
  const r = 8;
  const c = 2 * Math.PI * r;
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
      <circle cx="10" cy="10" r={r} fill="none" stroke="#e5e7eb" strokeWidth="3" />
      <circle cx="10" cy="10" r={r} fill="none" stroke="var(--color-main)" strokeWidth="3"
        strokeDasharray={`${(c * Math.max(0, Math.min(100, pct))) / 100} ${c}`} transform="rotate(-90 10 10)" />
    </svg>
  );
};
const fmtAutomationPoints = (v) => String(Math.round((v || 0) * 10) / 10).replace('.', ',');

// Build a complete PUT /tasks/{id} payload that round-trips ALL preserved
// fields from the task object. The backend's PUT endpoint treats missing
// fields as None and writes them unconditionally, so partial payloads would
// null out any field not included. Callers pass `overrides` for the fields
// they want to change; everything else is pulled from `task`.
const buildPreservedTaskPayload = (task, overrides = {}) => ({
  caller_name: task._rawCallerName || task.callerName,
  phone_number: task.phoneNumber,
  description: task.description,
  status: task.status,
  due_date: task.dueDate ? new Date(task.dueDate).toISOString() : null,
  assignee_id: task.assigneeId || null,
  caller_birthdate: task.callerBirthdate || null,
  caller_email: task.callerEmail || null,
  caller_whatsapp_optin: typeof task.callerWhatsappOptin === 'boolean' ? task.callerWhatsappOptin : null,
  has_verordnung: typeof task.hasVerordnung === 'boolean' ? task.hasVerordnung : null,
  verordnung_type: task.verordnungType || null,
  verordnung_sessions: task.verordnungSessions || null,
  verordnung_issue_date: task.verordnungIssueDate || null,
  insurance_type: task.insuranceType || null,
  session_id: task.sessionId || null,
  conversation_quality: task.conversationQuality ?? null,
  ...overrides,
});

// Single source of truth for task status definitions.
// Order here drives the inline status-change dropdown and the 1-4 keyboard
// shortcuts (1 = first userSelectable, 2 = second, etc.).
// "Weitergeleitet" is set automatically by Deskbot on call forwarding —
// not a user-selectable choice.
const STATUS_CONFIG = [
  { value: 'completed', label: 'Abgeschlossen', className: 'completed', userSelectable: true, filterable: true },
  { value: 'in-progress', label: 'In Bearbeitung', className: 'in-progress', userSelectable: true, filterable: true },
  { value: 'unreachable', label: 'Nicht erreicht', className: 'unreachable', userSelectable: true, filterable: true },
  { value: 'pending', label: 'Ausstehend', className: 'pending', userSelectable: true, filterable: true },
  { value: 'weitergeleitet', label: 'Weitergeleitet', className: 'weitergeleitet', userSelectable: false, filterable: true },
  { value: 'completed_by_deskbot', label: '𐤃 Abgeschlossen', className: 'completed', userSelectable: false, filterable: true },
  // 'in-progress' styling on purpose: the task is NOT done. The 𐤃 in the label
  // carries the Deskbot attribution, the colour must not read as completed.
  { value: 'processing_by_deskbot', label: '𐤃 In Bearbeitung', className: 'in-progress', userSelectable: true, superAdminOnly: true, filterable: true },
  // Set by Deskbot only: calls that ended without a concern. Open ("Offen"); the
  // "Verpasste Anrufe ausblenden" box hides it. Staff may move a row out of it.
  { value: 'verpasster_anruf', label: 'Verpasster Anruf', className: 'missed', userSelectable: false, filterable: true },
  { value: 'Deleted', label: 'Gelöscht', className: 'deleted', userSelectable: false, filterable: false },
];

// Hides `superAdminOnly` statuses from everyone else. Applied at EVERY site that
// enumerates STATUS_CONFIG — inline status menu, filter list, edit-modal select
// and the 1-4 keyboard shortcuts — so the four can never drift apart.
const visibleTo = (isSuper) => (s) => !!s && (!s.superAdminOnly || isSuper);
const statusConfigFor = (value) => STATUS_CONFIG.find(s => s.value === value);

// Filter dropdown shows statuses in workflow order (open → in flight → done),
// independent from STATUS_CONFIG which is keyed by inline-action priority.
const FILTER_ORDER = [
  'pending',
  'verpasster_anruf',
  'unreachable',
  'weitergeleitet',
  'processing_by_deskbot',
  'in-progress',
  'completed_by_deskbot',
  'completed',
];

// Shared style for filter <select> elements — replaces native chevron with a
// custom SVG so we control its right-edge spacing (native chevrons sit flush
// against the container border, which looks cramped).
const FILTER_SELECT_STYLE = {
  width: '100%',
  padding: '8px 28px 8px 12px',
  borderRadius: 6,
  border: '1px solid #ccc',
  background: '#fff',
  fontSize: '0.95rem',
  cursor: 'pointer',
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'><path d=\'M2 4l4 4 4-4\' stroke=\'%23555\' stroke-width=\'1.5\' fill=\'none\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/></svg>")',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 10px center'
};

// Shared label row for every filter. The fixed height matches the Status row,
// which also holds the Offen/Alle toggle, so all label texts sit on one line.
const FILTER_LABEL_STYLE = {
  display: 'flex',
  alignItems: 'center',
  height: 24,
  marginBottom: 6,
  fontWeight: 500,
  fontSize: '0.9rem',
  color: '#555'
};

// "Patienten hervorheben" filter options: the codes tasks.insurance_type stores,
// plus 'Blanko' for a task whose therapy is a Blankorezept preset row (tasks.is_blanko).
const PATIENT_HIGHLIGHT_OPTIONS = ['GKV', 'PKV', 'Blanko', 'BG', 'Entlassmanagement', 'Selbstzahler'];

// German status translations (derived from STATUS_CONFIG)
const getGermanStatus = (status) => {
  return STATUS_CONFIG.find(s => s.value === status)?.label || status;
};

// Render a status label with enlarged dalet character
const renderStatusLabel = (label, daletProps = {}) => {
  if (typeof label === 'string' && label.includes('𐤃')) {
    const parts = label.split('𐤃');
    return <>{parts[0]}<DaletChar {...daletProps} />{parts[1]}</>;
  }
  return label;
};

// Get CSS class for a status badge
const getStatusClassName = (status) => {
  return STATUS_CONFIG.find(s => s.value === status)?.className || '';
};

// Open vs terminal buckets — drives the Offen/Alle quick-filter, the Offen
// default for new users, and cluster visible-task selection. "weitergeleitet"
// is included because the practice may still want to follow up even after
// Deskbot transferred the call, "verpasster_anruf" because the caller may need a
// call back.
const OPEN_STATUSES = new Set(['pending', 'in-progress', 'unreachable', 'weitergeleitet', 'processing_by_deskbot',
                               'verpasster_anruf']);
const isOpenStatus = (status) => OPEN_STATUSES.has(status);

// The "Offen" preset, per user. It MUST drop the statuses this user cannot see:
// `statusFilters` is PERSISTED (tasksPageFilter in localStorage), so every
// existing user already has the old 4-element array stored. Comparing that
// against a hardcoded 5-element set would unlight the Offen pill, raise a Reset
// button and render a chip row for all of them. Derived per user it holds one
// status fewer for members than for a super_admin.
const openSetFor = (isSuper) => Array.from(OPEN_STATUSES).filter(v => visibleTo(isSuper)(statusConfigFor(v)));

// "Alle abschließen" sweeps a phone cluster to 'completed'. The two terminal
// statuses are already done, and 'processing_by_deskbot' is excluded because a
// manual sweep would silently discard the pending auto-upgrade to
// '𐤃 Abgeschlossen' — and with it the task's KPI credit.
// A missed-call row is swept with the rest: staff deal with the caller, not the row.
const BULK_COMPLETE_EXCLUDED = new Set(['completed', 'completed_by_deskbot', 'processing_by_deskbot']);
const isBulkCompletable = (status) => !BULK_COMPLETE_EXCLUDED.has(status);

// Phone numbers that should never participate in clustering
const isClusterablePhone = (phone) => {
  if (!phone) return false;
  const trimmed = String(phone).trim();
  // Must contain at least one digit to be a real, clusterable number. Excludes
  // '' / '-' and placeholders like "Nicht angegeben" (caller gave no number) so
  // unrelated callers don't collapse into one bogus cluster keyed on the label.
  return trimmed !== '' && trimmed !== '-' && /\d/.test(trimmed);
};

// The number a task row stands for: the given number, else the caller ID.
const taskPhone = (task) => (isClusterablePhone(task.phoneNumber) ? task.phoneNumber
  : (isClusterablePhone(task.callerIdNumber) ? task.callerIdNumber : null));

// A number only earns a place in the clustering graph once it is long enough to
// identify a person. Half-transcribed fragments ("0160" → significant "160") are
// NOT identities: because clustering is transitive, letting one in would chain
// every caller who mumbled the same mobile prefix into one bogus cluster.
// Display is deliberately unaffected — the practice still sees the partial
// number on the card via isClusterablePhone.
const CLUSTER_MIN_SIGNIFICANT_DIGITS = 7;

// Clustering identity of one number: its last 9 significant digits, so a spoken
// "0160 972 34567" and a Caller-ID "+4916097234567" resolve to the same key.
// '' (falsy) = not a usable identity.
const clusterNumberKey = (phone) => {
  const d = phoneSignificantDigits(phone);
  return d.length >= CLUSTER_MIN_SIGNIFICANT_DIGITS ? d.slice(-9) : '';
};

// Every number a task can be reached on: the GIVEN phone, the raw caller ID and
// the SMS/WhatsApp mobile. A task is not one number but a set — which is what
// lets a call that volunteered a new number still group with earlier calls known
// only by their caller ID (and vice versa for an anonymous caller who gives one).
//
// Every key is namespaced by the task's practice. While a single practice is on
// screen that prefix is constant and so provably changes nothing; while several
// are pooled it is what keeps one caller who rang TWO practices from collapsing
// into a single cluster — whose "alle abschließen" would then complete tasks in
// two tenants from one click, and whose alt-name list would mix both practices'
// spellings of the caller. (It does shift WHICH key wins as the union-find
// representative, which expandedClusters is keyed on; that is the cosmetic,
// self-correcting re-keying already described below.)
const taskClusterNumbers = (task) => {
  const keys = [];
  for (const raw of [task.phoneNumber, task.callerIdNumber, task.pvsMobileNumber]) {
    const key = clusterNumberKey(raw);
    if (!key) continue;
    const scoped = `${task.practiceId || ''}:${key}`;
    if (!keys.includes(scoped)) keys.push(scoped);
  }
  return keys;
};

// Two tasks share a cluster when their number sets intersect — transitively, so
// a task carrying both a caller ID and a given number links the tasks keyed on
// either one. Union-find over the number keys; each task then adopts its
// component's representative.
//
// Deliberately NOT a "try the given number, else fall back to the caller ID"
// chain: that is order-dependent and non-transitive (a task holding only the
// caller ID drops out of the cluster as soon as another task claims the given
// number), so cluster membership would shift as rows load. Union always points
// the larger root at the smaller, so the representative is the lexicographically
// smallest number in the component — independent of task order, though NOT of the
// component's contents: a newly pushed task carrying a smaller number re-keys the
// whole cluster, which collapses it if it was expanded (expandedClusters is keyed
// on the representative). Cosmetic and self-correcting on the next click.
const buildClusterIndex = (taskList) => {
  const parent = new Map();
  const find = (x) => {
    while (parent.get(x) !== x) { parent.set(x, parent.get(parent.get(x))); x = parent.get(x); }
    return x;
  };
  const union = (a, b) => {
    const ra = find(a), rb = find(b);
    if (ra !== rb) parent.set(ra < rb ? rb : ra, ra < rb ? ra : rb);
  };

  const numbersByTask = new Map();
  for (const task of taskList) {
    const numbers = taskClusterNumbers(task);
    if (numbers.length === 0) continue;
    numbersByTask.set(task.id, numbers);
    for (const n of numbers) if (!parent.has(n)) parent.set(n, n);
    for (let i = 1; i < numbers.length; i++) union(numbers[0], numbers[i]);
  }

  const index = new Map();
  for (const [id, numbers] of numbersByTask) index.set(id, find(numbers[0]));
  return index;
};

// Cluster expand/collapse animation timing (mirrored for enter and exit)
const CLUSTER_ANIM_DURATION_MS = 150;     // 0.15s — keep in sync with CSS animation duration
const CLUSTER_STAGGER_MS = 30;            // per-row delay multiplier
const CLUSTER_STAGGER_MAX_IDX = 8;        // cap so deep lists don't stretch the cascade
const CLUSTER_EXIT_TOTAL_MS =
  CLUSTER_ANIM_DURATION_MS + CLUSTER_STAGGER_MS * CLUSTER_STAGGER_MAX_IDX; // ~390ms total cascade

// Status-change pulse cleanup window (cluster-mode reorder animation duration)
const STATUS_PULSE_CLEANUP_MS = 1300;

const TasksPage = () => {
  const { keycloak } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // Session 10d: practiceId comes from PracticeContext — replaces the
  // keycloak.sub → userId → practiceId resolution chain that previously lived here.
  const {
    practiceId, isUserSuperAdmin, practiceRole, currentPractice, loading: practiceLoading,
    availablePractices, switchPractice, allPracticesMode, setAllPracticesMode,
    isUserAdmin: userIsAdmin,
  } = usePractice();
  // Note DELETION is allowed only for super_admin + the practice owner;
  // members/task_managers can still add & edit notes but not delete them
  // (frontend-only UI gate — the backend does not enforce this, by decision).
  const canDeleteNotes = isUserSuperAdmin || practiceRole === 'owner';
  const queryClient = useQueryClient();

  // ─── "Alle Praxen" scope ──────────────────────────────────────────────────
  // The SAME hook and the SAME shared toggle state the Dashboard uses, so the
  // choice made on one page is already made on the other. `forTasks` admits
  // task_manager memberships: that role is 403'd everywhere except task routes,
  // so this is the one view it can actually open, and a task_manager of several
  // practices pools them here like anyone else.
  const scope = usePracticeScope({ forTasks: true });
  const { activeKey, activeIds, scopedKey, allPractices, practiceNameById } = scope;

  // ─── Deep link from the support team: ?practice=&session=&task= ───────────
  // Built by the backend for the tracker. Shows exactly that call's tasks,
  // whatever their age: its own query key and a server-side session_id filter
  // instead of the Zeitraum window, and the restored filters do not apply.
  // Never persisted; the chip in the heading clears it.
  const deepLink = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const pick = (name) => {
      const value = (params.get(name) || '').trim();
      return UUID_RE.test(value) ? value : null;
    };
    const link = { practice: pick('practice'), session: pick('session'), task: pick('task') };
    return link.practice && (link.session || link.task) ? link : null;
  }, [location.search]);
  const linkActive = !!deepLink && deepLink.practice === practiceId && !allPracticesMode;
  // Waiting for the linked practice to be selected: the regular list is held
  // back so it does not load (and flash) first.
  const linkPending = !!deepLink && !linkActive;
  const linkAppliedRef = useRef(null);
  useEffect(() => {
    if (!deepLink || practiceLoading) return;
    if (deepLink.practice === practiceId) {
      if (!allPracticesMode) {
        linkAppliedRef.current = location.search;
        return;
      }
      // Arrived while pooling: the link names one practice, so show that one.
      if (linkAppliedRef.current !== location.search) {
        setAllPracticesMode(false);
        return;
      }
    } else if (linkAppliedRef.current !== location.search
      && availablePractices.some((p) => p.practice_id === deepLink.practice)) {
      switchPractice(deepLink.practice);
      return;
    }
    // A practice this user cannot open, or the user switched practice / turned
    // the pool on after the link was applied: drop the link. The backend gates
    // access either way.
    navigate('/tasks', { replace: true });
  }, [deepLink, practiceLoading, practiceId, allPracticesMode, availablePractices,
    switchPractice, setAllPracticesMode, navigate, location.search]);

  // Support chat: link status fetched ONCE for the page, never per row (rows come
  // from an inline render function, where a hook would change the hook count).
  // The icon writes to the practice on screen, so it is off while pooling.
  const { enabled: supportEnabled } = useSupportStatus(practiceId);
  const showSupportIcon = supportEnabled && !allPractices;
  // DeskBot staff can also open a tracker ticket about a call (the chat icon
  // writes as the practice). Pooled rows carry their practice, so this one stays
  // on while pooling.
  const showTicketIcon = !!isUserSuperAdmin;
  // Each extra action button widens the column by one button, with a fixed width
  // so it fits on laptop screens too; without them the layout stays as it was.
  const extraActions = (showSupportIcon ? 1 : 0) + (showTicketIcon ? 1 : 0);
  const actionsWidth = 110 + extraActions * 38;

  // View preferences (filters + view mode) are isolated PER USER AND PER PRACTICE.
  // The Keycloak subject is the stable per-user id; practiceId scopes the value
  // so switching practices never carries over the previous practice's filters.
  // Deliberately NOT namespaced by the pool: the filters carry across the "Alle
  // Praxen" toggle, so flipping it keeps the view you had set up. Giving the pool
  // its own namespace would also have to re-key the LOAD effect below (which runs
  // on [practiceId, prefScope]); leaving that half-wired writes prefs under a key
  // nothing ever reads back, which silently resets filters on the next mount.
  const prefScope = keycloak?.subject || 'anon';
  const filterKey = (base) => `${base}:${prefScope}:${practiceId || 'none'}`;
  // Holds the saved prefs + one-shot Dashboard handoff claimed for the current
  // {practiceId, prefScope}. See the load effect below for why storage cannot be
  // read inline there.
  const claimRef = useRef({ depKey: null, prefs: null, handoffRaw: null });
  const [showForm, setShowForm] = useState(false);
  const [currentTask, setCurrentTask] = useState({
    id: '',
    callerName: [],  // Array of name variations
    phoneNumber: '',
    description: '',
    status: 'pending',
    dueDate: new Date().toISOString().substr(0, 10)
  });
  // View preferences (status/Grund/assignee/day/viewMode) are isolated PER USER
  // AND PER PRACTICE — see the load effect below. The lazy initializers only
  // provide plain defaults; the scoped values are read in once practiceId is
  // known and re-synced on every practice switch (this page does NOT remount on
  // a switch, so a load effect — not the initializer — is the sync point).
  const [statusFilters, setStatusFilters] = useState([]);
  // Standing preference "Verpasste Anrufe ausblenden" (persisted, unlike the
  // status filter). An explicit 'verpasster_anruf' selection still shows them.
  const [hideMissed, setHideMissed] = useState(false);
  // "taskId:todoId" of the to-do whose × asks "Löschen?" (second click deletes).
  const [todoDeleteArmed, setTodoDeleteArmed] = useState(null);
  const [statusFilterOpen, setStatusFilterOpen] = useState(false);
  const statusFilterRef = useRef(null);
  // Grund (call-reason) multi-select filter — mirrors statusFilters pattern.
  const [callReasonFilters, setCallReasonFilters] = useState([]);
  const [callReasonFilterOpen, setCallReasonFilterOpen] = useState(false);
  const callReasonFilterRef = useRef(null);
  // Übergabegrund (SmartAB escape) filter. It has no dropdown: only the Dashboard's
  // SmartAB drill-through sets it; its chip or Zurücksetzen clears it.
  const [escapeFilters, setEscapeFilters] = useState([]);
  const [assigneeFilters, setAssigneeFilters] = useState([]);
  const [assigneeFilterOpen, setAssigneeFilterOpen] = useState(false);
  const assigneeFilterRef = useRef(null);
  // Patienten hervorheben: multi-select on the task's insurance type or Blanko.
  const [insuranceFilters, setInsuranceFilters] = useState([]);
  const [insuranceFilterOpen, setInsuranceFilterOpen] = useState(false);
  const insuranceFilterRef = useRef(null);
  const [dayFilter, setDayFilter] = useState('7');
  const [dayFilterOpen, setDayFilterOpen] = useState(false);
  const dayFilterRef = useRef(null);
  // Custom Zeitraum ('range'): committed dates + whether the drawer is unfolded.
  const [dayRange, setDayRange] = useState({ from: '', to: '' });
  const [dayRangeOpen, setDayRangeOpen] = useState(false);
  // Admin-only retry of failed actions: ids of tasks with a retry run in
  // flight (button spinner). Cleared by the runner's final broadcast
  // (retry_active=false) or the safety timeout below.
  const [retryingTaskIds, setRetryingTaskIds] = useState(() => new Set());
  const retrySafetyTimersRef = useRef({});
  // "Benachrichtigungen erneut senden" on a task whose unsent e-mail has no valid
  // address: the task whose address field is open, its input, and the refusal text.
  const [emailFixTaskId, setEmailFixTaskId] = useState(null);
  const [emailFixValue, setEmailFixValue] = useState('');
  const [emailFixError, setEmailFixError] = useState('');
  // Per-action retry menu ("Einzeln senden ▾"): the task whose menu is open,
  // its fetched failed-action entries (null = loading), and the trigger rect
  // for the portal-rendered panel (anchored like the caller popover).
  const [retryMenuTaskId, setRetryMenuTaskId] = useState(null);
  const [retryMenuEntries, setRetryMenuEntries] = useState(null);
  const [retryMenuRect, setRetryMenuRect] = useState(null);
  // Single ref (only one menu can be open); doubles as the staleness token for
  // the entries fetch — see openRetryMenu.
  const retryMenuTriggerRef = useRef(null);
  // Sent-message viewer: the task whose stored messages are open, the fetched
  // list (null = still loading) and the id of the message the tab strip shows.
  const [sentMessageTask, setSentMessageTask] = useState(null);
  const [sentMessageList, setSentMessageList] = useState(null);
  const [sentMessageActiveId, setSentMessageActiveId] = useState(null);
  // Its editor for sendable messages: drafts and notices by message id, the
  // message this popup sent (its lock is no conflict), the discard bar.
  const [sentMessageDrafts, setSentMessageDrafts] = useState({});
  const [sentMessageNotes, setSentMessageNotes] = useState({});
  const [sentMessageSendingId, setSentMessageSendingId] = useState(null);
  const [sentMessageConfirmClose, setSentMessageConfirmClose] = useState(false);
  // The task where a DOC-IQ PDF download ("PDF an <PVS>" note) failed, or null.
  const [docIqFailedTaskId, setDocIqFailedTaskId] = useState(null);
  // Staleness token for the content fetch (same idiom as retryMenuTriggerRef):
  // open A then B quickly and A's late response must not paint into B's popup.
  const sentMessageTokenRef = useRef(0);
  // The pill that opened the popup — focus goes back to it on close.
  const sentMessageReturnFocusRef = useRef(null);
  const sentMessageModalRef = useRef(null);

  // ─── React Query-backed task list ───────────────────────────────────────
  // The task list is the page's primary data and must be BOTH:
  //   • cached-on-revisit (instant paint when returning to the tab), and
  //   • live (WebSocket pushes + optimistic mutations render immediately).
  // We achieve this by making React Query the single source of truth (key
  // includes practiceId + dayFilter, matching the two filter dimensions the
  // backend honors), and exposing a `setTasks` wrapper that writes straight
  // into the query cache. Because the wrapper accepts both a value and a
  // functional updater — exactly like useState's setter — every existing
  // setTasks(...) call site (form save, inline edits, status/assignee change,
  // delete, WebSocket handlers, polling) keeps working unchanged.
  // The range belongs IN the key, not just the 'range' token — a bare token makes
  // every custom range share one cache entry, so switching ranges would paint the
  // previous range's tasks. Presets keep the 3-element shape (same rule as the
  // dashboard key): react-query hashes the whole array, so a 4th element added
  // unconditionally would miss any exact-key read of the preset entry.
  // scopedKey files a pool under its own head ('tasks-all'), so a partial-key
  // invalidate for one mode never sweeps the other's entries, and both modes keep
  // their cached lists, so toggling paints instantly.
  // A deep link files its rows under the same head, so the socket-open catch-up
  // (which invalidates scopedKey('tasks')) refreshes it like any other window.
  const taskKey = useMemo(
    () => (linkActive
      ? [...scopedKey('tasks'), 'link', deepLink.session ? `session:${deepLink.session}` : `task:${deepLink.task}`]
      : dayFilter === 'range'
        ? [...scopedKey('tasks'), dayFilter, `${dayRange.from}_${dayRange.to}`]
        : [...scopedKey('tasks'), dayFilter]),
    [scopedKey, dayFilter, dayRange, linkActive, deepLink]
  );
  // Keep the live key in a ref so the stable `setTasks` wrapper (and the
  // WebSocket handler, whose effect only re-runs on practiceId) always writes
  // to the CURRENT cache key — even after dayFilter changes without a remount.
  const taskKeyRef = useRef(taskKey);
  taskKeyRef.current = taskKey;
  // Same reason as taskKeyRef: the WebSocket effect re-runs only on practiceId, so
  // reading dayFilter/dayRange inside its handler would see a stale closure. Holds
  // the committed custom window, or null whenever the filter is a rolling preset.
  const dayWindowRef = useRef(null);
  dayWindowRef.current = (dayFilter === 'range' && dayRange.from && dayRange.to) ? dayRange : null;

  // Reports a pool that came up short — a practice whose fetch failed — so the
  // toggle can say so instead of the list quietly missing a tenant's tasks.
  const [taskScopeLoad, setTaskScopeLoad] = useState(null);

  // The deep link's rows: the whole call through the session_id filter or, for a
  // voicemail or manual task (no session), the one task by id. The by-id route
  // answers for any practice the user belongs to, so the practice is checked here.
  const fetchLinkedTasks = async () => {
    if (deepLink.session) {
      return (await fetchPracticeTasks(practiceId, { session_id: deepLink.session })).map(formatTask);
    }
    let { data } = await axios.get(`${API_BASE_URL}/tasks/${deepLink.task}`);
    // A missed-call row absorbed into a later call is hidden; show that task.
    if (data?.merged_into_task_id) {
      ({ data } = await axios.get(`${API_BASE_URL}/tasks/${data.merged_into_task_id}`));
    }
    return data && data.practice_id === practiceId && data.status !== 'Deleted' ? [formatTask(data)] : [];
  };

  // The active list, however it happens to be scoped: one practice, or every
  // practice in the pool merged newest-first. BOTH fetch paths — the react-query
  // fetcher below and the WebSocket-down fallback poll — go through here, for the
  // same reason they already share taskWindowParams: they must not diverge.
  const fetchActiveTaskList = async () => {
    if (linkActive) {
      setTaskScopeLoad(null);
      return fetchLinkedTasks();
    }
    const params = taskWindowParams();
    if (!allPractices) {
      setTaskScopeLoad(null);
      return (await fetchPracticeTasks(practiceId, params)).map(formatTask);
    }
    // One request per practice rather than a bulk endpoint — each keeps its own
    // membership check and its own RLS arming, so no query anywhere reads across
    // tenants (the same reason the Dashboard aggregate fans out).
    // allSettled, not all: one practice failing must not blank the others.
    const settled = await Promise.allSettled(
      activeIds.map((id) => fetchPracticeTasks(id, params))
    );
    const loaded = settled.filter((r) => r.status === 'fulfilled').map((r) => r.value);
    if (loaded.length === 0) {
      throw new Error('Keine Praxis konnte geladen werden');
    }
    setTaskScopeLoad({ loadedCount: loaded.length, scopeCount: activeIds.length });
    return mergeTaskLists(loaded).map(formatTask);
  };

  const taskQuery = useQuery(
    taskKey,
    fetchActiveTaskList,
    {
      enabled: activeIds.length > 0 && !linkPending,
      // Override the global default (off) for the task list specifically: when
      // the user returns to a tab that was idle/slept, pull the current list so
      // calls that arrived in the meantime show up without a manual reload.
      // staleTime (3 min) still gates it, so quick tab-switches don't refetch.
      refetchOnWindowFocus: true,
      onSuccess: (formattedTasks) => {
        // Seed knownTaskIds on first successful load so the new-task highlight
        // and notifications only fire for genuinely new tasks afterwards.
        if (knownTaskIds.current.size === 0) {
          formattedTasks.forEach(task => knownTaskIds.current.add(task.id));
        }
        // Re-merge any task_created pushes that arrived while this load was in flight.
        // react-query wholesale-replaces the cache with `formattedTasks` on resolve, so a
        // snapshot taken just before those tasks committed would drop them — and their ids
        // are already "known", so the WS path can't re-add them (missing until a manual
        // refresh). Prepend only the ones the snapshot is missing.
        if (pendingWsInsertsRef.current.length > 0) {
          const snapshotIds = new Set(formattedTasks.map(t => t.id));
          const missing = pendingWsInsertsRef.current.filter(t => !snapshotIds.has(t.id));
          pendingWsInsertsRef.current = [];
          if (missing.length > 0) {
            // `prev` is the snapshot just written by react-query, and `missing` is
            // exactly the tasks it lacks — so a plain prepend is correct (no re-dedup).
            queryClient.setQueryData(taskKeyRef.current, (prev) => [...missing, ...(prev || formattedTasks)]);
          }
        }
        setErrorMessage('');
      },
      onError: (error) => {
        console.error('Error fetching tasks:', error);
        // 404 = new practice without tasks yet — treat as empty, not an error.
        if (error.response && error.response.status === 404) {
          console.log('No tasks found for practice, starting with empty list');
          queryClient.setQueryData(taskKey, []);
        } else {
          setErrorMessage('Failed to load tasks. Please try again later.');
        }
      },
    }
  );

  // All render/read paths use `tasks`; it always reflects the live query cache.
  const tasks = useMemo(() => taskQuery.data || [], [taskQuery.data]);

  // The processing cap (isProcessingWindowOpen) is read at render time, so
  // re-render once the earliest open window passes it — otherwise a lost
  // closing broadcast would keep the spinner until an unrelated re-render.
  // The tick re-runs the effect, which arms the next window's timer.
  const [processingTick, setProcessingTick] = useState(0);
  useEffect(() => {
    const now = Date.now();
    const next = tasks
      .filter(t => t.processingLines || t.deskbotProcessing || (t.todos || []).some(td => td.in_flight))
      .map(processingDeadline)
      .filter(d => d > now);
    if (!next.length) return undefined;
    // Capped: above 2^31-1 ms (a client clock weeks behind) setTimeout fires at once.
    const timer = setTimeout(() => setProcessingTick(n => n + 1), Math.min(Math.min(...next) - now + 50, 2 ** 31 - 1));
    return () => clearTimeout(timer);
  }, [tasks, processingTick]);

  // Phone-cluster membership for the whole loaded list. Built from `tasks`, not
  // `filteredTasks`, so the bulk-complete actions and the grouped rows agree on
  // who belongs together even while a filter hides part of a cluster.
  const clusterIndex = useMemo(() => buildClusterIndex(tasks), [tasks]);
  // '' for a task with no usable number — every call site already treats a falsy
  // key as "stands alone", so the empty string keeps that contract.
  const clusterKeyOf = useCallback((task) => (task && clusterIndex.get(task.id)) || '', [clusterIndex]);

  // Drop-in replacement for the old useState setter. Writes to the query cache
  // and supports BOTH setTasks(array) and setTasks(prev => next) so no call
  // site needs to change. Treats an empty cache as [] for functional updaters.
  const setTasks = useCallback((updater) => {
    queryClient.setQueryData(taskKeyRef.current, (prev) => {
      const current = prev || [];
      return typeof updater === 'function' ? updater(current) : updater;
    });
  }, [queryClient]);
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef(null);
  // Two-click delete confirmation: tracks the task ID currently in confirm state.
  const [confirmingDeleteId, setConfirmingDeleteId] = useState(null);
  const confirmDeleteTimerRef = useRef(null);
  // Inline description editing
  const [editingDescriptionId, setEditingDescriptionId] = useState(null);
  const [descriptionDraft, setDescriptionDraft] = useState('');
  // Inline practice-note (📌) editing: { taskId, index } — index -1 = adding new.
  const [editingNote, setEditingNote] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');
  // Captured pixel height of the read-only div at the moment of click —
  // used to render the textarea at exactly the same height to prevent
  // a "page jump" caused by browser auto-scrolling a freshly-focused element.
  const [descriptionInitialHeight, setDescriptionInitialHeight] = useState(null);
  // Where to place the caret on edit start — derived from click coordinates.
  const [descriptionInitialCaret, setDescriptionInitialCaret] = useState(null);
  // Generic single-line inline edit for Name / Phone / Birthdate fields.
  // Key: `${taskId}:${field}` (field = 'callerName' | 'phoneNumber' | 'callerBirthdate')
  const [editingFieldKey, setEditingFieldKey] = useState(null);
  // Set during mousedown when the click is dismissing an inline editor; used to
  // suppress the slide-over that would otherwise be triggered by the same click.
  const suppressRowClickRef = useRef(false);
  const [editingFieldDraft, setEditingFieldDraft] = useState('');
  // Keyboard navigation: which row is currently "active" for shortcuts
  const [activeRowId, setActiveRowId] = useState(null);
  const [showShortcutHelp, setShowShortcutHelp] = useState(false);
  // Click-toggle popover on caller-cell for full caller data (User-icon trigger).
  const [openCallerPopoverId, setOpenCallerPopoverId] = useState(null);
  // Rect of the trigger button used to position the portal-rendered popover.
  // Stored as the bounding rect at the time of open; recomputed on scroll/resize.
  const [callerPopoverRect, setCallerPopoverRect] = useState(null);
  const callerPopoverTriggerRefs = useRef({});
  // Admin Stats column: the automation ring opens its step list in a popover, same portal
  // pattern as the caller popover. Details are fetched once per task on the first click.
  const [openAutomationId, setOpenAutomationId] = useState(null);
  const [automationRect, setAutomationRect] = useState(null);
  const automationTriggerRefs = useRef({});
  const [automationDetails, setAutomationDetails] = useState({});
  // Tracks which item was just copied (shows "Kopiert" briefly).
  // Key format: `${task.id}:phone|name|birthdate`
  const [copiedItem, setCopiedItem] = useState(null);
  const copiedItemTimerRef = useRef(null);
  // Default 'flat'; every visit opens on it (not restored from storage).
  const [viewMode, setViewMode] = useState('flat');
  const [expandedClusters, setExpandedClusters] = useState(() => new Set());
  const [exitingClusters, setExitingClusters] = useState(() => new Set());
  const exitTimersRef = useRef(new Map());
  // Pending status-pulse cleanup timers, keyed by taskId. Mirrors exitTimersRef
  // so we can cancel pending timers on unmount or rapid re-toggle of the same task.
  const statusPulseTimersRef = useRef(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [editingStatusId, setEditingStatusId] = useState(null);
  const [statusDropdownPos, setStatusDropdownPos] = useState({ top: 0, left: 0 });
  const [editingAssigneeId, setEditingAssigneeId] = useState(null);
  const [assigneeDropdownPos, setAssigneeDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const [showConversationModal, setShowConversationModal] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [showCallerDataModal, setShowCallerDataModal] = useState(false);
  const [selectedCallerData, setSelectedCallerData] = useState(null);
  const dueDateRef = useRef(null);

  // Voicemail audio playback state
  const [audioPlaying, setAudioPlaying] = useState(false);
  const audioRef = useRef(null);

  // Call recording playback state
  const [taskRecordings, setTaskRecordings] = useState([]);
  const [recordingPlaying, setRecordingPlaying] = useState(null); // recording ID currently playing
  const recordingAudioRef = useRef(null);

  // Full conversation recording state (MixMonitor)
  const [fullRecording, setFullRecording] = useState(null);
  const [fullRecordingPlaying, setFullRecordingPlaying] = useState(false);
  const fullRecordingAudioRef = useRef(null);
  const [fullRecordingCurrentTime, setFullRecordingCurrentTime] = useState(0);
  const [fullRecordingDuration, setFullRecordingDuration] = useState(0);
  // Audio playback speed (WhatsApp-style: 1× → 1.5× → 2× → 1×). Persisted.
  const [playbackSpeed, setPlaybackSpeed] = useState(() => {
    const stored = parseFloat(localStorage.getItem('audioPlaybackSpeed'));
    return [1, 1.5, 2].includes(stored) ? stored : 1;
  });
  const cyclePlaybackSpeed = () => {
    const next = playbackSpeed === 1 ? 1.5 : playbackSpeed === 1.5 ? 2 : 1;
    setPlaybackSpeed(next);
    localStorage.setItem('audioPlaybackSpeed', String(next));
    if (fullRecordingAudioRef.current) fullRecordingAudioRef.current.playbackRate = next;
    if (recordingAudioRef.current) recordingAudioRef.current.playbackRate = next;
  };
  const isDraggingRef = useRef(false); // Use ref to avoid stale closure in ontimeupdate

  // Smart polling state - track new tasks for animation
  const [newTaskIds, setNewTaskIds] = useState(new Set());
  const knownTaskIds = useRef(new Set());
  // task_created pushes that land WHILE the initial list load is still in flight are
  // parked here (the load can't be cancelled without emptying the board), then re-merged
  // by the load's onSuccess so a pre-insert server snapshot can't silently drop them.
  const pendingWsInsertsRef = useRef([]);

  // Status-change animation (cluster mode only): map of taskId -> newStatus
  const [statusChangedIds, setStatusChangedIds] = useState(new Map());

  // Practice members from Keycloak — read-only, so served directly from the
  // React Query cache (see the members useQuery below). Declared via useMemo
  // off the query data further down; `allMembers` / `assignableUsers` live there.

  // WebSocket state for real-time task updates
  const [wsConnected, setWsConnected] = useState(false);
  // One socket PER PRACTICE in the active scope (a single entry in the ordinary
  // case, one per pooled practice while aggregating) — /ws/tasks/{id} is
  // practice-scoped and the backend broadcasts per practice, so a pool needs one
  // subscription each. Keyed by practice id.
  const wsRefs = useRef(new Map());
  const reconnectTimeoutRefs = useRef(new Map());
  const reconnectAttemptsRefs = useRef(new Map());
  const wsOpenIdsRef = useRef(new Set());
  // True once the live update path can't reach the backend (socket down AND the
  // fallback poll is failing) → drives a non-blocking "stale" banner so an
  // empty board is never silently mistaken for "all caught up".
  const [connectionLost, setConnectionLost] = useState(false);


  // Check if a message is a voicemail (has the [Voicemail-Nachricht] prefix)
  const isVoicemailMessage = (message) => {
    return message.role === 'user' &&
           message.content &&
           message.content.includes('[Voicemail-Nachricht]');
  };

  // A caller turn the live transcription missed carries a recovery marker in the stored
  // text (same rule as langgraph_service._strip_recovery_markers): "* text" / "- text" when
  // the turn was transcribed from its audio, "[text]" when it was found in the call recording
  // afterwards. Returns the text without the marker plus its caption, or null.
  const recoveredTurn = (message) => {
    if (message.role !== 'user' || typeof message.content !== 'string') return null;
    const s = message.content.trim();
    if (s.length > 2 && s.startsWith('[') && s.endsWith(']')) {
      const text = s.slice(1, -1).trim();
      return text && !isVoicemailMessage(message)
        ? { text, caption: 'Nachträglich aus der Gesprächsaufnahme erkannt' }
        : null;
    }
    if (s.startsWith('* ') || s.startsWith('- ')) {
      const text = s.slice(2).trim();
      return text ? { text, caption: 'Live-Transkription war leer, Text aus der Aufnahme' } : null;
    }
    return null;
  };

  // Get recording for a specific user message turn number
  const getRecordingForTurn = (turnNumber) => {
    return taskRecordings.find(r => r.turn_number === turnNumber);
  };

  // Calculate user message turn number (1-based index of user messages)
  // Skip silence markers ("...") since no recording is saved for those
  const getUserTurnNumber = (messages, currentIndex) => {
    let turnCount = 0;
    for (let i = 0; i <= currentIndex; i++) {
      const msg = messages[i];
      if (msg.role === 'user') {
        // Skip silence/timeout markers - no recording saved for these
        const content = msg.content?.trim() || '';
        if (content === '...' || content === '') {
          continue;
        }
        turnCount++;
      }
    }
    return turnCount;
  };

  // Professional SVG icons matching Layout.js style
  const actionIcons = {
    edit: (
      <svg width="18" height="18" fill="none" stroke="#6c757d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
      </svg>
    ),
    user: (
      // Modern filled person silhouette
      <svg width="18" height="18" viewBox="0 0 24 24" fill="#1b3f65" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="8" r="4"/>
        <path d="M4 20.5c0-3.59 3.58-6.5 8-6.5s8 2.91 8 6.5v.5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-.5z"/>
      </svg>
    ),
    chat: (
      // Audio-wave icon — represents the call recording / transcript
      <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <line x1="3" y1="12" x2="3" y2="12" />
        <line x1="7" y1="9" x2="7" y2="15" />
        <line x1="11" y1="5" x2="11" y2="19" />
        <line x1="15" y1="8" x2="15" y2="16" />
        <line x1="19" y1="11" x2="19" y2="13" />
      </svg>
    ),
    delete: (
      <svg width="18" height="18" fill="none" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <polyline points="3,6 5,6 21,6"/>
        <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"/>
        <line x1="10" y1="11" x2="10" y2="17"/>
        <line x1="14" y1="11" x2="14" y2="17"/>
      </svg>
    ),
    email: (
      <svg width="18" height="18" fill="none" stroke="#2F6BD7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
        <polyline points="22,6 12,13 2,6"/>
      </svg>
    ),
    copy: (
      <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
      </svg>
    ),
    phoneCall: (
      <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <path d="M22 16.92V19a2 2 0 0 1-2.18 2A19.72 19.72 0 0 1 3 5.18 2 2 0 0 1 5 3h2.09a2 2 0 0 1 2 1.72c.13 1.05.37 2.06.72 3.03a2 2 0 0 1-.45 2.11l-.27.27a16 16 0 0 0 6.29 6.29l.27-.27a2 2 0 0 1 2.11-.45c.97.35 1.98.59 3.03.72A2 2 0 0 1 22 16.92z"/>
      </svg>
    ),
    mailSend: (
      // Paper plane — the "E-Mail schreiben" (mailto:) action on the caller's
      // e-mail line. 14px/currentColor like `phoneCall`/`copy` next to it, and
      // a deliberately different silhouette from the ✉ `mailTag` that leads the
      // same line, so tag and action don't read as the same thing twice.
      <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <line x1="22" y1="2" x2="11" y2="13"/>
        <polygon points="22,2 15,22 11,13 2,9 22,2"/>
      </svg>
    ),
    mobile: (
      // Smartphone outline — marks the PVS-profile mobile number. Inline SVG
      // (not the 📱 emoji) so it stays crisp under the dimmed caller-phone--pvs
      // line; sized 1em to align with the emoji sibling tags (🗣️ / 📞).
      <svg width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <rect x="7" y="2" width="10" height="20" rx="2.5"/>
        <line x1="10.5" y1="18.5" x2="13.5" y2="18.5"/>
      </svg>
    ),
    mailTag: (
      // Envelope tag for the caller's e-mail line. Same 1em/currentColor
      // treatment as `mobile` (see above) — deliberately NOT `email`, which is
      // the 18px blue action icon for "Per E-Mail weiterleiten".
      <svg width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <rect x="2" y="4" width="20" height="16" rx="2"/>
        <polyline points="22,6 12,13 2,6"/>
      </svg>
    ),
    eye: (
      // The read affordance on an action note that has a stored message.
      // Channel-NEUTRAL on purpose: the note's caption already says SMS /
      // E-Mail, so a ✉ would say it twice. Drawn for ~9px: lighter stroke than
      // the stock 24px eye, and a filled pupil because a stroked ring's hole
      // closes up at this size and reads as a blot.
      <svg width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <path d="M12 5.5c-4.4 0-8.1 2.6-10 6.5 1.9 3.9 5.6 6.5 10 6.5 4.4 0 8.1-2.6 10-6.5-1.9-3.9-5.6-6.5-10-6.5z"/>
        <circle cx="12" cy="12" r="2.25" fill="currentColor" stroke="none"/>
      </svg>
    ),
    // The eye's twin on a message that can still be sent: opening edits it.
    // currentColor + 1em like the eye (`edit` above hardcodes its grey).
    pen: (
      <svg width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <path d="M12 20h9"/>
        <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/>
      </svg>
    ),
    // Ask the DeskBot team about this task (support chat); `chat` above is the
    // call transcript.
    supportChat: <TaskSupportIcon />,
    // DeskBot staff: a tracker ticket about this call.
    ticket: (
      <svg width="18" height="18" fill="none" stroke="#2F6BD7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <path d="M3 8a2 2 0 0 0 2-2h14a2 2 0 0 0 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 0-2 2H5a2 2 0 0 0-2-2v-2a2 2 0 0 0 0-4z"/>
        <path d="M9 6v12" strokeDasharray="2 2.5"/>
      </svg>
    )
  };

  // While the assignee dropdown is open, keep it anchored to its trigger during scroll.
  useEffect(() => {
    if (!editingAssigneeId) return;
    const reposition = () => {
      const trigger = document.querySelector(`tr[data-task-id="${editingAssigneeId}"] .assignee-trigger`);
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const dropdownHeight = 220;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < dropdownHeight + 8;
      setAssigneeDropdownPos({
        top: openUpward ? rect.top - dropdownHeight : rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    };
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [editingAssigneeId]);

  // While the status dropdown is open, keep it anchored to its badge during scroll.
  useEffect(() => {
    if (!editingStatusId) return;
    const reposition = () => {
      const badge = document.querySelector(`tr[data-task-id="${editingStatusId}"] .status-badge`);
      if (!badge) return;
      const rect = badge.getBoundingClientRect();
      const dropdownHeight = 144;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < dropdownHeight + 8;
      setStatusDropdownPos({
        top: openUpward ? rect.top - dropdownHeight : rect.bottom + 4,
        left: rect.left,
      });
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [editingStatusId]);

  // Auto-focus search field when navigated here via global Cmd/Ctrl+K from another page.
  useEffect(() => {
    if (location.state?.focusSearch) {
      const focusIt = () => {
        const el = searchInputRef.current;
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus();
        el.select?.();
      };
      // Defer one frame so the input is mounted before focusing.
      requestAnimationFrame(focusIt);
      // Clear the state so refresh / back-nav doesn't re-trigger.
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  // Deep link with task=: scroll that row into view once it is on screen (same
  // idiom as the search focus above). It stays marked while the link is active.
  const linkedTaskId = linkActive ? deepLink.task : null;
  const linkScrolledRef = useRef(null);
  useEffect(() => {
    if (!linkedTaskId || isLoading || linkScrolledRef.current === location.search) return;
    if (!tasks.some((t) => t.id === linkedTaskId)) return;
    linkScrolledRef.current = location.search;
    requestAnimationFrame(() => {
      document.querySelector(`tr[data-task-id="${linkedTaskId}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [linkedTaskId, isLoading, tasks, location.search]);

  // Leaving link mode: live pushes meanwhile went into the link's own entry, so
  // mark the regular windows stale before one of them is shown again.
  const clearDeepLink = () => {
    queryClient.invalidateQueries(scopedKey('tasks'), { refetchActive: false });
    navigate('/tasks', { replace: true });
  };

  // Session 10d: practice resolution moved to PracticeContext.
  // Surface a friendly error and stop the spinner when the context settles
  // with no accessible practice — otherwise isLoading stays true forever and
  // the error message is unreachable.
  useEffect(() => {
    if (!practiceLoading && !practiceId) {
      setErrorMessage('Keine Praxis gefunden. Bitte konfigurieren Sie Ihre Praxis in den Einstellungen.');
      setIsLoading(false);
    } else if (practiceId) {
      setErrorMessage('');
    }
  }, [practiceLoading, practiceId]);

  // ─── Load per-user, per-practice view preferences ──────────────────────────
  // Runs on mount AND on every practice switch (keyed on [practiceId, prefScope]).
  // Because this page does NOT remount on a practice switch, this effect is what
  // re-syncs the filters/view to the practice being opened — isolating one
  // practice's (and one user's) choices from another's. Reads the 7 scoped keys
  // back into state, falling back to the same plain defaults the initializers use.
  // Then it consumes a one-shot Dashboard handoff (a KPI/Grund click-through that
  // pre-filters Tasks) which, being transient, overrides the just-loaded prefs.
  useEffect(() => {
    if (!practiceId) return;
    const readScoped = (base, fallback) => {
      try {
        const stored = localStorage.getItem(filterKey(base));
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed)) return parsed;
        }
      } catch { /* ignore invalid JSON */ }
      return fallback;
    };
    // Claim BOTH the saved prefs and the transient handoff into a ref keyed by
    // this effect's own deps, instead of reading localStorage directly.
    //
    // This effect body is not idempotent and React 18 StrictMode invokes it twice
    // on mount in dev. Two things went wrong when it read storage each time:
    // the handoff was consumed by run 1 so run 2 overwrote the applied filters
    // with defaults (every drill-down landed on "Alle"); and the persist effects
    // below, which run in the same flush while the load effect's setState calls
    // are still queued, write the INITIAL values back to storage, so run 2 read
    // those clobbered values and the user's saved Zeitraum evaporated on ordinary
    // navigation. Reading the claim makes run 2 replay run 1 exactly. A later
    // practice/user switch carries a different depKey and re-reads for real.
    const depKey = `${practiceId}|${prefScope}`;
    if (claimRef.current.depKey !== depKey) {
      claimRef.current = {
        depKey,
        prefs: {
          callReason: readScoped('tasksPageCallReasonFilter', []),
          escape: readScoped('tasksPageEscapeFilter', []),
          assignee: readScoped('tasksPageAssigneeFilter', []),
          insurance: readScoped('tasksPageInsuranceFilter', []),
          day: localStorage.getItem(filterKey('tasksPageDayFilter')),
          dayRange: localStorage.getItem(filterKey('tasksPageDayCustomRange')),
          hideMissed: (() => {
            try { return localStorage.getItem(filterKey('tasksPageHideMissed')) === '1'; } catch { return false; }
          })(),
        },
        // Removed as soon as it is claimed, so it can never outlive this mount
        // and re-fire on a later manual visit to the page.
        handoffRaw: localStorage.getItem('tasksPageHandoff'),
      };
      localStorage.removeItem('tasksPageHandoff');
    }
    const claimed = claimRef.current.prefs;
    // Status and view are NOT restored: every visit opens on "Alle" + "Flach".
    setStatusFilters([]);
    setHideMissed(claimed.hideMissed);
    setCallReasonFilters(claimed.callReason);
    setEscapeFilters(claimed.escape);
    setAssigneeFilters(claimed.assignee);
    setInsuranceFilters(claimed.insurance);
    const storedDay = claimed.day;
    // A restored 'range' is only usable with its dates. If they're missing or
    // malformed, fall back to '7' — otherwise the page restores into a window it
    // can't resolve and shows an empty list with no obvious way back.
    // Shape-check, not just truthiness: two arbitrary strings would sail past
    // validateRange (their day count is NaN, which fails every comparison) and
    // reach the backend as a 422. Used for BOTH the restored range and the
    // Dashboard handoff below.
    const isDay = (v) => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);
    const isValidDayRange = (r) => isDay(r?.from) && isDay(r?.to) && r.from <= r.to;
    let restoredRange = { from: '', to: '' };
    if (storedDay === 'range') {
      try {
        const parsed = JSON.parse(claimed.dayRange || 'null');
        if (isValidDayRange(parsed)) {
          restoredRange = { from: parsed.from, to: parsed.to };
        }
      } catch { /* ignore invalid JSON */ }
    }
    setDayRange(restoredRange);
    setDayFilter(storedDay === 'range' && !restoredRange.from ? '7' : (storedDay || '7'));
    setViewMode('flat');

    // One-shot Dashboard → Tasks handoff: a transient, NOT practice-scoped key the
    // Dashboard writes before navigating here to pre-filter the list. Applied on
    // top of the loaded prefs.
    //
    try {
      const handoffRaw = claimRef.current.handoffRaw;
      if (handoffRaw) {
        const handoff = JSON.parse(handoffRaw);
        if (Array.isArray(handoff?.statusFilters)) setStatusFilters(handoff.statusFilters);
        if (Array.isArray(handoff?.callReasonFilters)) setCallReasonFilters(handoff.callReasonFilters);
        if (Array.isArray(handoff?.escapeFilters)) setEscapeFilters(handoff.escapeFilters);
        if (Array.isArray(handoff?.assigneeFilters)) setAssigneeFilters(handoff.assigneeFilters);
        if (Array.isArray(handoff?.insuranceFilters)) setInsuranceFilters(handoff.insuranceFilters);
        // Every drill-through hands over the Dashboard's Zeitraum so the list
        // opens on the window the clicked count was computed over. Both pages
        // resolve a token through the shared resolvePeriodRange, so the same
        // token means the same days. Anything unrecognised is ignored; a 'range'
        // must pass the same shape check as a restored one, since an unresolvable
        // window would show an empty list with no obvious way back.
        const handoffDay = handoff?.dayFilter;
        if (handoffDay === 'range') {
          if (isValidDayRange(handoff?.dayRange)) {
            setDayRange({ from: handoff.dayRange.from, to: handoff.dayRange.to });
            setDayFilter('range');
          }
        } else if (PERIOD_VALUES.includes(handoffDay)) {
          setDayFilter(handoffDay);
        }
        // Grund drill-down resets status to the default "Alle" view.
        if (handoff?.resetStatus) setStatusFilters([]);
      }
    } catch { /* ignore malformed handoff */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId, prefScope]);

  // Persist filters to localStorage under the per-user, per-practice key.
  // Guarded on practiceId so we never write under the 'none' scope. Deps are the
  // STATE ONLY (never practiceId): on a practice switch the load effect above
  // sets the new values first — adding practiceId here would re-fire the persist
  // effect with the OLD practice's value and write it under the NEW practice's key.
  useEffect(() => {
    if (practiceId) localStorage.setItem(filterKey('tasksPageCallReasonFilter'), JSON.stringify(callReasonFilters));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callReasonFilters]);

  useEffect(() => {
    if (practiceId) localStorage.setItem(filterKey('tasksPageEscapeFilter'), JSON.stringify(escapeFilters));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [escapeFilters]);

  useEffect(() => {
    if (practiceId) localStorage.setItem(filterKey('tasksPageAssigneeFilter'), JSON.stringify(assigneeFilters));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assigneeFilters]);

  useEffect(() => {
    if (practiceId) localStorage.setItem(filterKey('tasksPageInsuranceFilter'), JSON.stringify(insuranceFilters));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insuranceFilters]);

  useEffect(() => {
    if (!practiceId) return;
    localStorage.setItem(filterKey('tasksPageDayFilter'), dayFilter);
    localStorage.setItem(filterKey('tasksPageDayCustomRange'), JSON.stringify(dayRange));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayFilter, dayRange]);

  useEffect(() => {
    if (!practiceId) return;
    try { localStorage.setItem(filterKey('tasksPageHideMissed'), hideMissed ? '1' : '0'); } catch { /* storage blocked */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideMissed]);

  // Assignable users = members of the CURRENT practice. Any practice member can
  // load this (the members endpoint is membership-gated, not super_admin-gated),
  // and it's correctly scoped to the practice — unlike the old
  // /users/keycloak-users/ which was super_admin-only and returned every realm
  // user across all practices. first/last names come from Keycloak via the
  // members endpoint.
  // Cached via React Query so switching tabs/practices serves the member list
  // from cache instantly and refreshes in the background. Read-only data.
  const membersQuery = useQuery(
    scopedKey('practiceMembers'),
    async () => {
      // One request per practice, same fan-out as the task list: each carries its
      // own membership check, so nothing reads across tenants. allSettled keeps a
      // practice whose member list fails from blanking the others' pickers.
      const settled = await Promise.allSettled(activeIds.map(fetchPracticeMembers));
      const byPractice = {};
      settled.forEach((r, i) => {
        byPractice[activeIds[i]] = r.status === 'fulfilled' ? r.value : [];
      });
      return byPractice;
    },
    {
      enabled: activeIds.length > 0,
      onError: (error) => {
        console.error('Error fetching assignable users:', error);
        // Silent fail - dropdown will just be empty
      },
    }
  );
  // Keyed by practice, because WHO may be assigned is a per-practice question
  // even while several practices are pooled — see assignableFor.
  // No useMemo needed: react-query's data identity is stable between fetches and
  // the fallback is a module constant, so this expression is already stable.
  const membersByPractice = membersQuery.data || NO_MEMBERS_BY_PRACTICE;

  // Two lists off the same fetch: `allMembers` resolves DISPLAY names (a task
  // already assigned to someone later marked non-assignable must still show
  // their name, not a raw UUID), `assignableUsers` feeds the top-bar filter.
  //
  // Deduped by user id: the same person in two practices is ONE Keycloak user, so
  // their id collapses naturally. Where their "Zuweisbar" flag differs BETWEEN
  // practices, assignable wins — the filter offers whoever can hold a task
  // somewhere in the pool, and the per-row picker still applies that practice's
  // own flag.
  const allMembers = useMemo(() => {
    const byId = new Map();
    Object.values(membersByPractice).forEach((list) => {
      (list || []).forEach((m) => {
        const seen = byId.get(m.id);
        if (!seen || (!seen.assignable && m.assignable)) byId.set(m.id, m);
      });
    });
    return Array.from(byId.values());
  }, [membersByPractice]);
  const assignableUsers = useMemo(() => allMembers.filter(u => u.assignable), [allMembers]);

  // The assignee picker for ONE row, scoped to that row's own practice. Assigning
  // practice A's member to practice B's task is a cross-tenant write, and
  // update_task does not currently reject it — so the offer must never be made.
  // Single-practice mode returns the one list unchanged.
  const assignableFor = useCallback((task) => {
    if (!allPractices) return assignableUsers;
    return (membersByPractice[task?.practiceId] || []).filter(u => u.assignable);
  }, [allPractices, assignableUsers, membersByPractice]);

  // The task fetch itself now lives in the `taskQuery` useQuery above (cached +
  // background-refreshed). Mirror its fetch state onto the existing isLoading
  // flag, but only show the spinner when there's no cached data yet — a cached
  // revisit (e.g. switching back to this tab within staleTime) paints instantly.
  useEffect(() => {
    if (!practiceId) return;
    setIsLoading(linkPending || (taskQuery.isFetching && (taskQuery.data === undefined)));
  }, [taskQuery.isFetching, taskQuery.data, practiceId, linkPending]);

  // Fallback polling: only when WebSocket is disconnected
  useEffect(() => {
    if (activeIds.length === 0 || wsConnected) return; // Skip if WebSocket is connected

    console.log('[TASKS] WebSocket disconnected, using fallback polling');

    const pollForNewTasks = async () => {
      try {
        // Same scoped fetch the query uses — one practice or the whole pool.
        const formattedTasks = await fetchActiveTaskList();

        // Poll reached the backend → connection is healthy, clear any banner.
        setConnectionLost(false);

        // Find new tasks (IDs we haven't seen before)
        const newIds = formattedTasks
          .filter(task => !knownTaskIds.current.has(task.id))
          .map(task => task.id);

        if (newIds.length > 0) {
          // Mark new tasks for animation
          setNewTaskIds(new Set(newIds));

          // Add new IDs to known set
          newIds.forEach(id => knownTaskIds.current.add(id));

          // Update tasks list
          setTasks(formattedTasks);

          // Clear animation class after animation completes
          setTimeout(() => {
            setNewTaskIds(new Set());
          }, 1000);
        }
      } catch (error) {
        // The socket is already down (this poll only runs while disconnected) and
        // now the REST fallback failed too → surface a non-blocking banner instead
        // of failing completely silently (the bug that hid weekend calls). Token
        // refresh / re-login is handled centrally in App.js, not here.
        setConnectionLost(true);
        console.log('Polling error (silent):', error.message);
      }
    };

    // Poll every 15 seconds
    const intervalId = setInterval(pollForNewTasks, 15000);

    // Cleanup on unmount or when dependencies change
    return () => clearInterval(intervalId);
    // setTasks is a stable useCallback (writes to the RQ cache) — intentionally
    // omitted so the poll interval only re-arms on the real inputs below.
    // activeKey covers both the practice and the pool that fetchActiveTaskList reads;
    // linkActive/deepLink switch it to (or from) the deep link's rows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey, dayFilter, dayRange, wsConnected, linkActive, deepLink]);

  // WebSocket connection for real-time task updates
  useEffect(() => {
    // One subscription per practice on screen: the pool while aggregating,
    // otherwise just the selected practice.
    if (!keycloak?.token || activeIds.length === 0) return;

    // Read the ref maps once. They are created by useRef and never reassigned, so
    // these aliases are the same objects throughout — including in the cleanup,
    // which is what lets the cleanup close exactly the sockets this run opened.
    const sockets = wsRefs.current;
    const reconnectTimers = reconnectTimeoutRefs.current;
    const reconnectAttempts = reconnectAttemptsRefs.current;
    const openIds = wsOpenIdsRef.current;

    // Scoped to THIS run, deliberately not a ref. React runs the cleanup and the
    // next effect body in one synchronous commit, while `close()` dispatches
    // onclose asynchronously — so a shared "intentionally closing" ref is already
    // back to false by the time a DEPARTING practice's socket reports closed, and
    // that socket reconnects itself. It then never appears in activeIds, so
    // syncConnected can never be satisfied (the fallback poll runs forever beside
    // a healthy socket) and its pushes land in the list of a practice it does not
    // belong to. A per-run flag cannot be reset by a later run, so the departing
    // sockets stay closed.
    let cancelled = false;

    // wsConnected gates the fallback poll, so it may only be true while EVERY
    // socket is open — one silent practice in a pool must still be polled for.
    const syncConnected = () => setWsConnected(openIds.size === activeIds.length);

    const connectWebSocket = (pid) => {
      // Prevent multiple connections (same pattern as CallStateContext)
      const existing = sockets.get(pid);
      if (existing && (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING)) {
        console.log('[WS-TASKS] Connection already exists, skipping');
        return;
      }

      // This run has been torn down — do not (re)open anything for it.
      if (cancelled) {
        console.log('[WS-TASKS] Effect cancelled, not connecting');
        return;
      }

      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      // Anchor both edits (see CallStateContext): unanchored `.replace('/api','')`
      // corrupts hosts starting with "api" (api.deskbot.io). Scheme at START, /api at END.
      const wsBase = apiUrl
        .replace(/^http/, 'ws')
        .replace(/\/api\/?$/, '');
      const wsUrl = `${wsBase}/ws/tasks/${pid}?token=${encodeURIComponent(keycloak.token)}`;

      console.log('[WS-TASKS] Connecting:', wsUrl);
      const socket = new WebSocket(wsUrl);
      sockets.set(pid, socket);

      socket.onopen = () => {
        console.log('[WS-TASKS] Connected');
        openIds.add(pid);
        syncConnected();
        setConnectionLost(false);
        reconnectAttempts.set(pid, 0);
        // Catch-up refetch on EVERY socket open — deliberately unconditional.
        // Whenever we (re)gain the push channel we resync, because we can never
        // know what was broadcast while it was closed: the backend drops the event
        // outright ("No connections for practice <id>, skipping broadcast") and
        // nothing replays it.
        // This was previously gated on "only if the socket dropped during THIS
        // mount", which missed the most common case: navigating away (e.g. to the
        // Call Monitor) unmounts TasksPage and closes the socket, so the gate flag
        // — a per-mount ref — was back to false on return. The mount refetch can't
        // cover it either: refetchOnMount:true means "refetch if STALE" and
        // staleTime is 3 min (index.js), so returning inside that window paints
        // from cache with zero network calls, and a task created in the gap stayed
        // invisible until a manual reload. (Prod 2026-07-28: task da7bf620 created
        // 16:01:03 UTC with no socket open, user back 2s later, no GET fired.)
        // invalidateQueries sets isInvalidated — the first term of isStaleByTime —
        // so it bypasses staleTime, and the partial key matches every cached
        // dayFilter variant for this scope. Cost: one extra GET of a small list
        // per socket open — two if the stored dayFilter isn't the '7' default, since
        // the initial-render '7' key (restored only later, in the practiceId-gated
        // effect below) is invalidated as well and refetches once on the next mount.
        // Do not re-add a gate here.
        queryClient.invalidateQueries(scopedKey('tasks'));
      };

      socket.onmessage = (event) => {
        try {
          const { type, data } = JSON.parse(event.data);
          console.log('[WS-TASKS] Event:', type);

          if (type === 'task_created') {
            // Reuse same formatTask function
            const formattedTask = formatTask(data);

            // A live push always falls inside a rolling preset (their window ends
            // now), but a custom range can END IN THE PAST. Keep such a task OUT
            // of the cache — the date window is enforced server-side, so inserting
            // it would put it at the top of a list the next refetch drops it from.
            // The notification below still fires, matching how the client-side
            // status/Grund filters behave. Compare LOCAL days: created_at is UTC,
            // so slicing its first 10 chars would bucket a 00:30 Berlin call into
            // the previous day — the opposite side from the backend's filter.
            const win = dayWindowRef.current;
            const createdDay = formattedTask.createdAt ? isoDay(new Date(formattedTask.createdAt)) : '';
            const inWindow = !win || (createdDay >= win.from && createdDay <= win.to);

            // Only add if not already known (prevent duplicates)
            if (!knownTaskIds.current.has(data.id)) {
              knownTaskIds.current.add(data.id);
              // Protect this push from a concurrent task-list fetch whose server snapshot
              // predates the task: on resolve react-query wholesale-replaces the cache,
              // and since the id is now "known" the WS path can't re-add it → the task
              // stays missing until a manual refresh.
              if (inWindow && queryClient.getQueryData(taskKeyRef.current) !== undefined) {
                // List already cached → the in-flight fetch is a refetch (window-focus,
                // or the socket-open catch-up above — which now also fires on a fresh
                // mount). Cancel it so its stale result can't overwrite the optimistic
                // insert.
                queryClient.cancelQueries(taskKeyRef.current);
                setTasks(prev => [formattedTask, ...prev]);
              } else if (inWindow) {
                // Initial load still in flight (cache empty) — it CANNOT be cancelled
                // without leaving the board empty until a refetch. Park the task so the
                // load's onSuccess re-merges it after the snapshot lands.
                pendingWsInsertsRef.current.push(formattedTask);
              } else {
                // Out of the committed custom window: nothing is written here, but the
                // OTHER cached windows (the presets, whose window ends now) are all
                // missing a task the server would return. Mark them invalidated —
                // refetchActive:false makes this mark-only, zero requests — so
                // switching back to a preset inside staleTime refetches instead of
                // painting a stale list the WS path can no longer repair (the id is
                // already "known", so the branch above will never fire for it again).
                queryClient.invalidateQueries(scopedKey('tasks'), { refetchActive: false });
              }
              setNewTaskIds(new Set([data.id]));
              setTimeout(() => setNewTaskIds(new Set()), 1000);

            }
          } else if (type === 'task_updated') {
            // Merge updated fields onto existing task. The backend's task_updated
            // payload (and the PUT response that triggers it) does NOT include
            // call_statistics-derived fields (call_reasons, perceived_latency_*,
            // conversation_quality), so a full replacement would wipe them from
            // the row until the next full refetch. Preserve those fields when
            // the incoming payload has them as null/undefined.
            setTasks(prev => prev.map(t => {
              if (t.id !== data.id) return t;
              return mergePreservingStats(t, formatTask(data));
            }));
            // Admin retry runner: its final broadcast carries retry_active=false —
            // stop the retry button's spinner for that task.
            if (data.retry_active === false) {
              stopRetrySpinner(data.id);
            }
          } else if (type === 'task_deleted') {
            setTasks(prev => prev.filter(t => t.id !== data.id));
            knownTaskIds.current.delete(data.id);
            // Its message popup closes (functional update: this closure is stale);
            // the next open resets the rest of the popup state.
            setSentMessageTask(prev => (prev && prev.id === data.id ? null : prev));
          }
        } catch (e) {
          console.error('[WS-TASKS] Parse error:', e);
        }
      };

      socket.onclose = () => {
        console.log('[WS-TASKS] Disconnected');
        // Bail BEFORE touching openIds: the cleanup already cleared it, and for a
        // practice that survives into the next run this late event would otherwise
        // delete the marker the NEW socket's onopen just added.
        if (cancelled) {
          console.log('[WS-TASKS] Effect cancelled, not reconnecting');
          return;
        }
        openIds.delete(pid);
        syncConnected();

        // Exponential backoff reconnect, counted per practice so one flapping
        // socket cannot stretch another's delay.
        const attempts = reconnectAttempts.get(pid) || 0;
        const delay = Math.min(1000 * Math.pow(2, attempts), 30000);
        reconnectAttempts.set(pid, attempts + 1);
        console.log(`[WS-TASKS] Reconnecting in ${delay}ms (attempt ${attempts + 1})`);
        reconnectTimers.set(pid, setTimeout(() => connectWebSocket(pid), delay));
      };

      socket.onerror = (err) => console.error('[WS-TASKS] Error:', err);
    };

    activeIds.forEach(connectWebSocket);

    return () => {
      cancelled = true; // This run is over; its sockets must not resurrect.
      // Drop any pushes buffered for the scope we're leaving so they can't be
      // re-merged into the next one's list (this ref outlives the switch).
      pendingWsInsertsRef.current = [];
      sockets.forEach((socket) => socket.close());
      sockets.clear();
      reconnectTimers.forEach((timer) => clearTimeout(timer));
      reconnectTimers.clear();
      reconnectAttempts.clear();
      openIds.clear();
      setWsConnected(false);
    };
    // Intentionally keyed on activeKey only (the practices on screen) so the sockets
    // don't reconnect on every token refresh / render, nor when the scope lookup
    // resolves. keycloak.token is read fresh at connect time; setTasks is a stable
    // useCallback writing to the live RQ cache key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey]);

  const handleAddNew = () => {
    setCurrentTask({
      id: '',
      callerName: [],  // Array of name variations
      phoneNumber: '',
      callerBirthdate: '',
      description: '',
      status: 'pending',
      dueDate: new Date().toISOString().substr(0, 10)
    });
    setShowForm(true);
  };

  // A 409 from a task write: the row was absorbed into a later call, deleted,
  // or has no to-dos. Show the server's German text and refetch the list, so a
  // merged row disappears and its call shows up on the task it now belongs to.
  // Returns false for any other error, which the caller then handles as before.
  const handleTaskConflict = (error) => {
    if (error?.response?.status !== 409) return false;
    setErrorMessage(error.response.data?.detail || 'Diese Aufgabe wurde inzwischen geändert.');
    queryClient.invalidateQueries(scopedKey('tasks'));
    return true;
  };

  const handleEdit = (task) => {
    // If no dueDate is set, fall back to createdAt date or today
    const dueDate = task.dueDate ||
      (task.createdAt ? new Date(task.createdAt).toISOString().substr(0, 10) : new Date().toISOString().substr(0, 10));
    // Edit PROSE ONLY in the modal textarea; stash notes + the machine action
    // block on transient fields, recomposed on submit. ('-' prose → empty.)
    const { prose, notes, actionRaw } = parseDescriptionZones(task.description);
    // _statusAtOpen: a tick while the panel is open can change the live status;
    // an untouched status field must not send the stale copy back (handleSubmit).
    setCurrentTask({ ...task, description: prose === '-' ? '' : prose, _descriptionNotes: notes, _descriptionActionRaw: actionRaw, dueDate, _statusAtOpen: task.status });
    setShowForm(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentTask(prev => ({ ...prev, [name]: value }));
  };

  // The window the Zeitraum control currently means, as inclusive calendar days.
  // Falls back to the 7-day default when a 'range' has no usable dates yet.
  const activeWindow = () => resolvePeriodRange(dayFilter, dayRange) || resolvePeriodRange('7');

  // Time-window query params for the task list. ALWAYS an explicit inclusive
  // range, which the backend resolves as whole Europe/Berlin days.
  //
  // The presets used to send a rolling `{days: N}` lookback instead
  // (created_at >= now() - N*24h). That made every preset label a lie — "Heute"
  // reached back to this hour yesterday, "Diese Woche" on a Monday reached into
  // Sunday — and, once the Dashboard began handing its Zeitraum over, it meant a
  // drill-through opened a WIDER window than the count that was clicked. Both
  // pages now resolve the same token through resolvePeriodRange, so the label,
  // the Dashboard number and this list all describe the same days.
  //
  // Both fetch paths (the react-query fetcher and the WebSocket-down fallback
  // poll) build params here so they can't diverge.
  const taskWindowParams = () => {
    const { from, to } = activeWindow();
    return { start_date: from, end_date: to };
  };

  // Highlight the search terms inside a string by wrapping matches in <mark>.
  // Returns the original text if no terms match. Used in name, phone, description.
  const highlightMatch = (text, terms) => {
    if (!text || !terms || terms.length === 0) return text;
    const str = String(text);
    const lower = str.toLowerCase();
    const ranges = [];
    for (const t of terms) {
      if (!t) continue;
      let i = 0;
      while ((i = lower.indexOf(t, i)) !== -1) {
        ranges.push([i, i + t.length]);
        i += t.length;
      }
    }
    if (ranges.length === 0) return text;
    ranges.sort((a, b) => a[0] - b[0]);
    const merged = [];
    for (const r of ranges) {
      if (merged.length && r[0] <= merged[merged.length - 1][1]) {
        merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], r[1]);
      } else merged.push([...r]);
    }
    const out = [];
    let cur = 0;
    merged.forEach(([s, e], idx) => {
      if (cur < s) out.push(<React.Fragment key={`t${idx}`}>{str.slice(cur, s)}</React.Fragment>);
      out.push(<mark key={`m${idx}`} className="search-mark">{str.slice(s, e)}</mark>);
      cur = e;
    });
    if (cur < str.length) out.push(<React.Fragment key="tail">{str.slice(cur)}</React.Fragment>);
    return <>{out}</>;
  };

  // --- Action-summary rendering -------------------------------------------
  // A phone-call Task's `description` is prose (the LLM summary of what the
  // caller wanted) optionally followed by a backend-appended "actions taken"
  // block: one status line per executed action, each starting with a status
  // glyph — ✅ done · ⚠️ needs staff attention · ❓/⏳ delivery unconfirmed.
  // We render that block as a quiet audit trail beneath the prose. See
  // backend/src/api/summary_renderer.py for the exact German line shapes.
  const NOTE_GLYPH = { ok: '✅', warn: '⚠️', pending: '❓', neutral: '📞', switch: '🔄',
                       processing: <span className="action-note-spinner" /> };

  // Sent-message viewer vocabulary. `channel` and `status` come from
  // /tasks/{id}/sent-messages; anything unmapped degrades to a neutral word
  // rather than showing a raw backend token.
  const MESSAGE_CHANNEL_DE = { sms: 'SMS', email: 'E-Mail', slack: 'Slack' };
  const MESSAGE_VIEW_LABEL = {
    sms: 'Gesendete SMS ansehen',
    email: 'Gesendete E-Mail ansehen',
    slack: 'Gesendete Slack-Nachricht ansehen',
  };
  // A message that can still be sent (`sendable`) opens editable, behind a pen.
  const MESSAGE_EDIT_LABEL = { sms: 'SMS bearbeiten und senden', email: 'E-Mail bearbeiten und senden' };
  const MESSAGE_STATUS_DE = {
    delivered: 'Zugestellt', sent: 'Versendet',
    failed: 'Nicht zugestellt', pending: 'Ausstehend',
  };

  // Short, scannable German captions for the known action labels. Matched by
  // prefix against the glyph-stripped line; the remainder becomes the detail.
  const ACTION_LABEL_RULES = [
    { test: 'Anrufer identifiziert per Telefonnummer und Geburtsdatum', caption: 'Identifiziert · Tel. + Geb.' },
    { test: 'Anrufer identifiziert per Telefonnummer', caption: 'Identifiziert · Tel.' },
    { test: 'Anrufer identifiziert per Geburtsdatum', caption: 'Identifiziert · Geb.' },
    { test: 'Anrufer identifiziert', caption: 'Identifiziert' },
    { test: 'Neuer Patient', caption: 'Neuer Patient' },
    // A call about several persons: one row per person other than the caller
    // (summary_renderer.CONCERNED_LINE_KEY).
    { test: 'Betroffene Person', caption: 'Betroffene Person' },
    { test: 'Buchung', caption: 'Buchung' },
    { test: 'Stornierung', caption: 'Stornierung' },
    { test: 'Verschiebung', caption: 'Verschiebung' },
    { test: 'Bestätigungs-SMS', caption: 'SMS' },
    { test: 'Formular-Link per SMS', caption: 'Formular-SMS' },
    // SMS to the practice's own number, and the DOC-IQ form reminder the day before
    // (literals: summary_renderer._SMS_CAPTIONS).
    { test: 'Praxis-SMS', caption: 'Praxis-SMS' },
    { test: 'Erinnerungs-SMS', caption: 'Erinnerungs-SMS' },
    // DOC-IQ intake form PDFs, one line each (doc_iq.py): "PDF an <PVS>: <Dokument>".
    // caption null = the line's own text up to its colon is the caption.
    { test: 'PDF an ', caption: null },
    // Short-notice therapist SMS; literal must match summary_renderer.THERAPIST_SMS_KEY
    { test: 'Therapeut-SMS', caption: 'Therapeut-SMS' },
    { test: 'Bestätigung per SMS', caption: 'SMS' },
    { test: 'Bestätigung per E-Mail', caption: 'E-Mail' },
    { test: 'SmartAB', caption: 'SmartAB' },
    // Why a call was forwarded (call_session.FORWARD_LINE_PREFIX), above Anrufergebnis.
    { test: 'Weiterleitung', caption: 'Weiterleitung' },
    { test: 'Anrufergebnis', caption: 'Anrufergebnis' },
    { test: 'Frequenz', caption: 'Frequenz' },
    { test: 'Anliegen', caption: 'Anliegen' },
  ];

  const ACTION_GLYPH_RE = /^\s*(✅|⚠️|⚠|❓|⏳|📞|🔄)\s*/;
  const isActionLine = (line) => ACTION_GLYPH_RE.test(line);

  // One glyph-led line -> { status, caption, detail }. Never throws.
  const classifyActionLine = (rawLine) => {
    const m = rawLine.match(ACTION_GLYPH_RE);
    let status = 'ok';
    let body = rawLine.trim();
    if (m) {
      const g = m[1];
      // '🔄' = the SmartAB handoff line: informational, not a success/failure —
      // 'switch' has no is-* colour rule, so it falls back to the neutral grey.
      status = g === '✅' ? 'ok' : (g === '⚠️' || g === '⚠') ? 'warn' : g === '📞' ? 'neutral' : g === '🔄' ? 'switch' : 'pending';
      body = rawLine.slice(m[0].length).trim();
    }
    let caption = '';
    let detail = body;
    for (const rule of ACTION_LABEL_RULES) {
      if (body.startsWith(rule.test)) {
        const head = rule.caption === null ? body.split(':')[0] : rule.test;
        caption = rule.caption ?? head.trim();
        detail = body.slice(head.length).replace(/^[:\s]+/, '');
        break;
      }
    }
    // Soften the backend's " | " field separator into a lighter middot.
    detail = detail.replace(/\s*\|\s*/g, ' · ');
    // `raw` is the untouched line. It is the join key against
    // action_log[].line for the sent-message viewer — everything else here is
    // lossy (glyph stripped, separators softened, caption split off).
    return { status, caption, detail, raw: rawLine.trim() };
  };

  // Split a task description into { prose, notes[] }. Everything before the
  // first action line is prose; each subsequent glyph-led line is a note.
  // Robust: odd input falls back to "all prose, no notes" (renders as before).
  const parseActionSummary = (raw) => {
    const text = String(raw || '');
    const lines = text.split('\n');
    let firstIdx = -1;
    for (let i = 0; i < lines.length; i++) {
      if (isActionLine(lines[i])) { firstIdx = i; break; }
    }
    if (firstIdx === -1) return { prose: text, notes: [] };
    const prose = lines.slice(0, firstIdx).join('\n').replace(/\s+$/, '');
    const notes = [];
    for (const line of lines.slice(firstIdx)) {
      if (!line.trim()) continue;
      if (isActionLine(line)) {
        notes.push(classifyActionLine(line));
      } else if (notes.length) {
        // Defensive: a stray non-glyph continuation line — fold into prev detail.
        notes[notes.length - 1].detail += ' ' + line.trim();
      }
    }
    return { prose, notes };
  };

  // The persons a call's appointments were for, read from the description's
  // "Neuer Patient" / "Betroffene Person" lines (as Frequenz / Anliegen are).
  // `others` = one entry per person whose name differs from the card name (the
  // contact); `cardGuardian` = the guardian on the card person's own line;
  // `cardBooked` = an action line also says "für <card name>" (the contact booked too).
  // Names compare case- and whitespace-blind.
  // Literal must match summary_renderer._guardian.
  const GUARDIAN_FIELD = 'Erziehungsberechtigte(r):';
  const parseCallPersons = (description, cardName) => {
    const nameKey = (s) => String(s || '').replace(/\s+/g, '').toLowerCase();
    const card = nameKey(cardName);
    const others = new Map();
    let cardGuardian = '';
    let cardBooked = false;
    for (const n of parseActionSummary(description).notes) {
      if (!cardBooked && card && n.detail.split(' · ').some(f => f.startsWith('für ') && nameKey(f.slice(4)) === card)) cardBooked = true;
      if (n.caption !== 'Neuer Patient' && n.caption !== 'Betroffene Person') continue;
      const [name, ...fields] = n.detail.split(' · ').map(f => f.trim());
      if (!name) continue;
      const birthdate = fields.find(f => /^\d{1,2}\.\d{1,2}\.\d{4}$/.test(f)) || '';
      const guardian = (fields.find(f => f.startsWith(GUARDIAN_FIELD)) || '').slice(GUARDIAN_FIELD.length).trim();
      const key = nameKey(name);
      if (key === card) cardGuardian = cardGuardian || guardian;
      else if (!others.has(key)) others.set(key, { name, birthdate, guardian });
    }
    return { others: [...others.values()], cardGuardian, cardBooked };
  };

  // THREE-zone description model: «prose» ⏎⏎ «📌 notes» ⏎⏎ «✅ action block».
  // Practice notes (`📌 `-led, human-authored) live BETWEEN the prose and the
  // machine action block — so the backend's watcher, which appends delivery
  // lines to the END, never touches them.
  const NOTE_LINE_RE = /^\s*📌\s?/;

  // Per-note authorship metadata is stored as a hidden suffix on the pin line:
  //   📌 <text> «by=<FirstName>|at=<ISO8601>»
  // The guillemets + pipe + equals are sentinels that never appear in German
  // practice notes and contain no whitespace runs, so they survive
  // composeDescription's `\s+ → ' '` collapse. A pin line WITHOUT a suffix is a
  // legacy note (author/ts null → no byline) and must round-trip byte-unchanged.
  const NOTE_META_RE = /\s*«by=([^|»]*)\|at=([^»]*)»\s*$/;

  // decodeNoteLine(strippedLine) → { text, author, ts }. `strippedLine` is a pin
  // line with the `📌 ` prefix already removed. author/ts are null when there is
  // no suffix (legacy) or the field is empty (unknown author). Never throws.
  const decodeNoteLine = (line) => {
    const s = String(line || '');
    const m = s.match(NOTE_META_RE);
    if (!m) return { text: s.trim(), author: null, ts: null };
    return { text: s.slice(0, m.index).trim(), author: m[1] || null, ts: m[2] || null };
  };

  // encodeNoteMeta({ author, ts }) → the '«by=…|at=…»' suffix, or '' when the
  // note has neither author nor ts (→ a legacy/unstamped note stays byte-exact).
  const encodeNoteMeta = (note = {}) => {
    if (!note.author && !note.ts) return '';
    return `«by=${note.author || ''}|at=${note.ts || ''}»`;
  };

  // parseDescriptionZones(raw) → { prose, notes: {text,author,ts}[], actionRaw }.
  //  - actionRaw: VERBATIM substring from the first action-glyph line to the end
  //    (byte-exact → the ✅ block + any watcher-appended delivery line round-trip).
  //  - notes: the 📌-led lines in the region before the action block, each
  //    decoded into { text, author, ts } (author/ts null for legacy no-meta notes).
  //  - prose: the remaining pre-block lines (trailing whitespace stripped).
  // Robust: never throws; unknown input degrades to all-prose / no-notes.
  const parseDescriptionZones = (raw) => {
    const text = String(raw || '');
    const lines = text.split('\n');
    let firstIdx = -1;
    for (let i = 0; i < lines.length; i++) {
      if (isActionLine(lines[i])) { firstIdx = i; break; }
    }
    let actionRaw = '';
    let headLines;
    if (firstIdx === -1) {
      headLines = lines;
    } else if (firstIdx === 0) {
      actionRaw = text;
      headLines = [];
    } else {
      // Offset of the first action line in the ORIGINAL string (join drops the
      // separating '\n', so add 1 back) → actionRaw is sliced byte-exact.
      const before = lines.slice(0, firstIdx).join('\n');
      actionRaw = text.slice(before.length + 1);
      headLines = lines.slice(0, firstIdx);
    }
    const notes = [];
    const proseLines = [];
    for (const line of headLines) {
      if (NOTE_LINE_RE.test(line)) notes.push(decodeNoteLine(line.replace(NOTE_LINE_RE, '')));
      else proseLines.push(line);
    }
    return { prose: proseLines.join('\n').replace(/\s+$/, ''), notes, actionRaw };
  };

  // Re-glue the three zones. Prose trimmed; each note is a { text, author, ts }
  // object — the .text is forced single-line (internal whitespace/newlines
  // collapsed to spaces) and empties dropped, then the encoded meta suffix (if
  // any) is appended; the action block is spliced back verbatim. A note with NO
  // meta emits exactly `📌 <text>` (byte-identical to the old string form), and a
  // NO-NOTE description composes byte-identical to the old 2-zone output.
  const composeDescription = (prose, notes = [], actionRaw = '') => {
    const p = String(prose || '').trim();
    const notesBlock = (notes || [])
      .map(n => {
        const raw = (n && typeof n === 'object') ? n.text : n;
        const text = String(raw || '').replace(/\s+/g, ' ').trim();
        if (!text) return '';
        const meta = (n && typeof n === 'object') ? encodeNoteMeta(n) : '';
        return `📌 ${text}${meta ? ' ' + meta : ''}`;
      })
      .filter(Boolean)
      .join('\n');
    return [p, notesBlock, actionRaw].filter(Boolean).join('\n\n') || '-';
  };

  // The confirmation-SMS target to surface on the 'SMS' action note: ONLY the
  // PVS-profile mobile — the one case where the exact target is known from a
  // dedicated field (populated from caller_sms_target when source == 'pvs_profile').
  // For spoken / caller-ID targets the number is already shown under the caller
  // name, and the true target (caller_sms_target) isn't exposed here, so we leave
  // the note as plain "gesendet" rather than risk a stale/landline number.
  const smsTargetForTask = (task) =>
    isClusterablePhone(task?.pvsMobileNumber) ? task.pvsMobileNumber : null;

  // ── Retry of failed actions (admin) / unsent notifications (staff) ─────
  // POST /tasks/{id}/retry-actions (super_admin-gated) returns 202; the backend
  // runner replays the failed PVS/SMS/email dispatches in dependency order and
  // broadcasts task_updated per flipped ⚠️→✅ line. The final broadcast carries
  // retry_active=false → spinner stops. POST /tasks/{id}/send-notifications
  // (membership-gated) runs the unsent SMS / E-Mail only, same lifecycle.
  const stopRetrySpinner = useCallback((taskId) => {
    setRetryingTaskIds(prev => {
      if (!prev.has(taskId)) return prev;
      const next = new Set(prev);
      next.delete(taskId);
      return next;
    });
    if (retrySafetyTimersRef.current[taskId]) {
      clearTimeout(retrySafetyTimersRef.current[taskId]);
      delete retrySafetyTimersRef.current[taskId];
    }
  }, []);

  // Idempotent: re-arming just refreshes the safety timer.
  const armRetrySpinner = (taskId) => {
    setRetryingTaskIds(prev => prev.has(taskId) ? prev : new Set(prev).add(taskId));
    // Safety: never spin forever if the final broadcast is lost (WS drop,
    // backend restart mid-run). Each replay may wait up to the 5-min wire
    // window on a slow serial worker, so allow a long full run.
    clearTimeout(retrySafetyTimersRef.current[taskId]);
    retrySafetyTimersRef.current[taskId] = setTimeout(() => stopRetrySpinner(taskId), 20 * 60 * 1000);
  };

  // entryIds narrows the replay to selected entries (per-action menu); absent
  // = the original retry-all button. endpoint 'send-notifications' = the staff
  // button: the task's unsent SMS / E-Mail only, membership-gated.
  // `body` overrides the request body (the staff address fix sends {email}).
  // Returns the error when the run did not start, else null.
  const handleRetryActions = async (task, entryIds = null, endpoint = 'retry-actions', body = undefined) => {
    if (retryingTaskIds.has(task.id)) return null;
    armRetrySpinner(task.id);
    try {
      await axios.post(`${API_BASE_URL}/tasks/${task.id}/${endpoint}`,
        body !== undefined ? body : (entryIds ? { entry_ids: entryIds } : undefined));
      return null;
    } catch (error) {
      console.error('Retry actions failed to start:', error);
      stopRetrySpinner(task.id);
      return error;
    }
  };

  // The unsent e-mail had no valid address: staff enter it, the backend checks it
  // with the send's own check (refusing with the German text), stores it on the
  // task and sends. Same lifecycle as above.
  const submitEmailFix = async (task) => {
    const error = await handleRetryActions(task, null, 'send-notifications', { email: emailFixValue.trim() });
    if (!error) {
      setEmailFixTaskId(null);
      setEmailFixValue('');
      setEmailFixError('');
      return;
    }
    const detail = error?.response?.data?.detail;
    setEmailFixError(typeof detail === 'string' ? detail : 'Senden fehlgeschlagen. Bitte erneut versuchen.');
  };

  // ── Per-action retry menu ("Einzeln senden ▾") ──────────────────────────
  // Entries come fresh from GET /tasks/{id}/failed-actions on every open (no
  // staleness); picking one posts {entry_ids: [id]} through the same retry
  // endpoint, so the spinner/broadcast lifecycle above covers the run.
  const closeRetryMenu = useCallback(() => {
    setRetryMenuTaskId(null);
    setRetryMenuEntries(null);
    setRetryMenuRect(null);
    retryMenuTriggerRef.current = null;
  }, []);

  const openRetryMenu = async (task, triggerEl) => {
    if (retryMenuTaskId === task.id) { closeRetryMenu(); return; }
    retryMenuTriggerRef.current = triggerEl;
    setRetryMenuTaskId(task.id);
    setRetryMenuEntries(null); // loading
    setRetryMenuRect(triggerEl.getBoundingClientRect());
    try {
      const res = await axios.get(`${API_BASE_URL}/tasks/${task.id}/failed-actions`);
      // Menu closed or switched to another task while loading — this response
      // belongs to a dead menu; applying it would show task A's entries on B.
      if (retryMenuTriggerRef.current !== triggerEl) return;
      setRetryMenuEntries(res.data.entries || []);
      // A run started elsewhere (other tab / other admin): reflect it so both
      // retry buttons disable; the runner's final broadcast stops the spinner.
      if (res.data.retry_active) {
        armRetrySpinner(task.id);
      }
    } catch (error) {
      console.error('Failed to load failed actions:', error);
      closeRetryMenu();
    }
  };

  // ── DOC-IQ form PDFs (the document name on each "PDF an <PVS>" line) ───────
  // The task payload lists them (docIqDocuments, each with its line); a download
  // reads our stored copy.
  const downloadDocIqDoc = async (task, doc) => {
    setDocIqFailedTaskId(prev => (prev === task.id ? null : prev));
    try {
      const res = await axios.get(
        `${API_BASE_URL}/tasks/practice/${task.practiceId}/doc-iq/forms/${doc.form_id}/documents/${doc.doc_id}`,
        { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${doc.filename_label}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      setDocIqFailedTaskId(task.id);
    }
  };

  // ── Sent-message viewer ───────────────────────────────────────────────────
  // Open the popup on one message of a task. The row already knows WHICH
  // messages exist (task.sentMessages metadata rides every row); the bodies are
  // fetched here, on demand, because they must not ride the whole task list.
  // A re-fetch (live change, see below) keeps the drafts: they live apart.
  const loadSentMessages = async (taskId) => {
    const token = ++sentMessageTokenRef.current;
    try {
      const res = await axios.get(`${API_BASE_URL}/tasks/${taskId}/sent-messages`);
      if (sentMessageTokenRef.current !== token) return;  // superseded by a later open
      setSentMessageList(res.data.messages || []);
    } catch (error) {
      console.error('Failed to load sent messages:', error);
      if (sentMessageTokenRef.current !== token) return;
      setSentMessageList(prev => prev || []);
    }
  };

  const resetSentMessageEditor = () => {
    setSentMessageDrafts({});
    setSentMessageNotes({});
    setSentMessageSendingId(null);
    setSentMessageConfirmClose(false);
  };

  const openSentMessage = (task, messageId, triggerEl) => {
    sentMessageReturnFocusRef.current = triggerEl || null;
    setSentMessageTask(task);
    setSentMessageActiveId(messageId);
    setSentMessageList(null);
    resetSentMessageEditor();
    loadSentMessages(task.id);
  };

  const closeSentMessage = () => {
    sentMessageTokenRef.current++;  // invalidate an in-flight fetch
    setSentMessageTask(null);
    setSentMessageList(null);
    setSentMessageActiveId(null);
    const trigger = sentMessageReturnFocusRef.current;
    sentMessageReturnFocusRef.current = null;
    // The row never unmounts on close, so the pill is still there to focus.
    if (trigger && document.contains(trigger)) trigger.focus();
  };

  // Editor of a sendable message: a draft exists only once staff typed.
  const sentMessageDraft = (m) => sentMessageDrafts[m.id] || { body: m.body || '', subject: m.subject || '', email: '' };
  const isSentMessageDirty = (m) => {
    const d = sentMessageDrafts[m.id];
    return !!d && (d.body !== (m.body || '') || d.subject !== (m.subject || '') || !!d.email);
  };
  const editSentMessage = (m, patch) =>
    setSentMessageDrafts(prev => ({ ...prev, [m.id]: { ...(prev[m.id] || sentMessageDraft(m)), ...patch } }));
  const dropSentMessageDraft = (id) => setSentMessageDrafts(prev => { const next = { ...prev }; delete next[id]; return next; });
  const setSentMessageNote = (id, text) => setSentMessageNotes(prev => ({ ...prev, [id]: text }));
  // ×, Esc, backdrop and "Abbrechen": unsent edits first ask via the discard bar.
  const requestCloseSentMessage = () => {
    if ((sentMessageList || []).some(m => m.sendable && isSentMessageDirty(m))) setSentMessageConfirmClose(true);
    else closeSentMessage();
  };
  // Esc on the discard bar means "Weiter bearbeiten".
  const escapeSentMessage = () => (sentMessageConfirmClose ? setSentMessageConfirmClose(false) : requestCloseSentMessage());

  // Sends ONLY this message, with the edited fields (POST send-notifications
  // {entry_id}). Same spinner lifecycle as the row button, so no other send of
  // the task starts until this one is done.
  const sendSentMessage = async (task, m) => {
    const d = sentMessageDraft(m);
    const req = { entry_id: m.id };
    if (d.body !== (m.body || '')) req.body = d.body;
    if (m.channel === 'email' && d.subject !== (m.subject || '')) req.subject = d.subject;
    if (m.needs_address) req.email = d.email.trim();
    setSentMessageNote(m.id, '');
    setSentMessageSendingId(m.id);
    const error = await handleRetryActions(task, null, 'send-notifications', req);
    if (error) {
      setSentMessageSendingId(null);
      const detail = error?.response?.data?.detail;
      setSentMessageNote(m.id, typeof detail === 'string' ? detail : 'Senden fehlgeschlagen. Bitte erneut versuchen.');
      return;
    }
    // The backend stored the edit before its 202: show the stored text, then drop the draft.
    await loadSentMessages(task.id);
    dropSentMessageDraft(m.id);
  };

  // Live sync of the open popup: the row metadata rides every task_updated, so a
  // changed line or sendable flag re-fetches the bodies. A message that stopped
  // being sendable without this popup's send (a colleague, the late PVS success)
  // locks with a notice. This popup's send is judged when its run ENDS (the
  // runner's final broadcast): a resend that fails the same way writes the same
  // line, so a line change would never show it.
  const liveSentMessages = sentMessageTask ? tasks.find(t => t.id === sentMessageTask.id)?.sentMessages : null;
  const liveSentKey = (liveSentMessages || []).map(m => `${m.id} ${m.line} ${m.sendable}`).join('|');
  const sentMessageRunning = !!sentMessageTask && retryingTaskIds.has(sentMessageTask.id);
  useEffect(() => {
    if (!sentMessageTask || !sentMessageList || !liveSentMessages) return;
    const live = (id) => liveSentMessages.find(l => l.id === id);
    sentMessageList.forEach(m => {
      if (m.sendable && live(m.id)?.sendable === false && m.id !== sentMessageSendingId) {
        setSentMessageNote(m.id, 'Diese Nachricht wurde inzwischen versendet.');
      }
    });
    if (sentMessageSendingId && !sentMessageRunning) {
      if (live(sentMessageSendingId)?.sendable) {
        setSentMessageNote(sentMessageSendingId, 'Senden fehlgeschlagen. Ihr Text ist gespeichert, Sie können es erneut versuchen.');
      }
      setSentMessageSendingId(null);
    }
    loadSentMessages(sentMessageTask.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveSentKey, sentMessageRunning]);

  // The quiet, secondary action-notes group. `terms` drives search highlighting
  // per segment (pass [] to disable, e.g. in the modal preview). `smsTarget`,
  // when set, is appended to the 'SMS' note detail ("gesendet · <number>").
  // `sentMessages` + `onOpenMessage` opt a call site into the "Ansehen" pill;
  // both default to null so a caller that passes neither renders exactly as
  // before.
  // `processingLines` = the task's in-flight lines (inFlightLines): their ⚠️
  // renders as a spinner until the worker's late verdict lands.
  const renderActionNotesGroup = (notes, { terms = [], showCaption = true, className = '', smsTarget = null,
                                           sentMessages = null, onOpenMessage = null, processingLines = [],
                                           docIqTask = null } = {}) => {
    // 'Frequenz' and 'Anliegen' are hidden metadata lines surfaced only in the
    // caller-info popover, 'Betroffene Person' only in the caller column's
    // "Termin für" block (never in the visible Aktionen receipt).
    const visible = notes.filter(n => !['Frequenz', 'Anliegen', 'Betroffene Person'].includes(n.caption));
    // The 'Anrufergebnis' outcome line always renders last, regardless of where
    // it sits in the description string (the async SMS-delivery watcher
    // re-appends its verdict to the tail, which would otherwise follow it).
    const ordered = [
      ...visible.filter(n => n.caption !== 'Anrufergebnis'),
      ...visible.filter(n => n.caption === 'Anrufergebnis'),
    ];
    if (!ordered.length) return null;
    return (
      <div className={`action-notes${className ? ` ${className}` : ''}`} data-no-row-click>
        {showCaption && <div className="action-notes-caption">Aktionen</div>}
        {ordered.map((n, i) => {
          // Append the confirmation-SMS target to the 'SMS' note (no emoji here —
          // the 📱 origin marker lives only under the caller's name).
          const detail = (n.caption === 'SMS' && smsTarget)
            ? (n.detail ? `${n.detail} · ${smsTarget}` : smsTarget)
            : n.detail;
          // Join on the VERBATIM line: action_log[].line is kept byte-identical
          // to the description line by both writers (pvs_tracker.apply_action_
          // success_to_task flips the glyph in place, and notification_service.
          // _write_sms_line_to_task explicitly re-points the entry's line).
          // Fails SOFT by design — should the two ever drift, the note simply
          // gets no pill; it can never get somebody else's message.
          const msg = (sentMessages && onOpenMessage && n.raw)
            ? sentMessages.find(m => m.line === n.raw)
            : null;
          const status = (n.status === 'warn' && processingLines.includes(n.raw)) ? 'processing' : n.status;
          // A DOC-IQ PDF line: its document name downloads our stored copy (same
          // verbatim-line join as the message pill above).
          const docIqDoc = docIqTask?.docIqDocuments?.find(d => d.line === n.raw);
          return (
          <div key={i} className={`action-note is-${status}`}
               title={status === 'processing' ? 'Deskbot bearbeitet diese Aktion noch' : undefined}>
            <span className="action-note-glyph" aria-hidden="true">{NOTE_GLYPH[status] || '•'}</span>
            <span className="action-note-body">
              {n.caption && <span className="action-note-label">{highlightMatch(n.caption, terms)}</span>}
              {detail && (docIqDoc ? (
                <button
                  type="button"
                  className="action-note-detail"
                  data-no-row-click
                  title="PDF herunterladen"
                  style={{ background: 'none', border: 'none', padding: 0, font: 'inherit',
                           textDecoration: 'underline', cursor: 'pointer' }}
                  onClick={(e) => { e.stopPropagation(); downloadDocIqDoc(docIqTask, docIqDoc); }}
                >
                  {highlightMatch(detail, terms)}
                </button>
              ) : <span className="action-note-detail">{highlightMatch(detail, terms)}</span>)}
            </span>
            {msg && (
              <button
                type="button"
                className={`action-note-view${msg.sendable ? ' is-edit' : ''}`}
                // TWO independent click traps, both required: data-no-row-click
                // only satisfies shouldOpenSlideover, while the inline prose
                // editor is opened by a separate handler on .description-readonly
                // that never consults it. Without stopPropagation the textarea
                // would open underneath the popup (the retry buttons below do
                // exactly the same pair).
                data-no-row-click
                aria-label={(msg.sendable ? MESSAGE_EDIT_LABEL : MESSAGE_VIEW_LABEL)[msg.channel] || 'Gesendete Nachricht ansehen'}
                title={(msg.sendable ? MESSAGE_EDIT_LABEL : MESSAGE_VIEW_LABEL)[msg.channel] || 'Gesendete Nachricht ansehen'}
                onClick={(e) => { e.stopPropagation(); onOpenMessage(msg.id, e.currentTarget); }}
              >
                <span className="action-note-view-icon" aria-hidden="true">{msg.sendable ? actionIcons.pen : actionIcons.eye}</span>
              </button>
            )}
          </div>
          );
        })}
        {docIqTask && docIqFailedTaskId === docIqTask.id && (
          <div className="action-note-detail" data-no-row-click style={{ marginTop: 4 }}>
            PDF konnte nicht geladen werden
          </div>
        )}
      </div>
    );
  };

  // The 'Anrufergebnis' outcome note for a task, parsed from its description
  // action block (or null if absent — e.g. tasks created before this shipped).
  // Powers the chat-modal footer badge.
  const getCallResultNote = (description) =>
    parseActionSummary(description).notes.find(n => n.caption === 'Anrufergebnis') || null;

  // Fallback captions for retry-menu rows whose stored line is missing. This
  // IS reachable: a doubly-failed Verschiebung (cancel+book pair) annotates
  // neither leg, so both entries arrive with line=null.
  const RETRY_OP_LABELS = {
    create_patient: 'Neuer Patient', book: 'Buchung', cancel: 'Stornierung',
    reschedule: 'Verschiebung', sms: 'SMS', email: 'E-Mail',
  };

  // The per-action retry menu panel. Portaled to document.body like the caller
  // popover — row CSS transforms trap position:fixed panels (see the dropdown
  // caveats near the .status-badge styles), so in-row fixed positioning is out.
  const renderRetryMenu = (task) => {
    const r = retryMenuRect;
    const entries = retryMenuEntries;
    const estimatedHeight = Math.max(44, (entries ? entries.length : 1) * 30 + 12);
    const spaceBelow = window.innerHeight - r.bottom;
    const openUpward = spaceBelow < estimatedHeight + 12 && r.top > spaceBelow;
    const style = openUpward
      ? { top: Math.max(8, r.top - estimatedHeight - 6), left: r.left }
      : { top: r.bottom + 6, left: r.left };
    // Keep horizontal position inside the viewport (max-width is 380px).
    const maxLeft = Math.max(8, window.innerWidth - 380 - 8);
    if (style.left > maxLeft) style.left = maxLeft;
    if (style.left < 8) style.left = 8;
    return createPortal(
      <div className="action-retry-menu" role="menu" style={style}>
        {entries === null ? (
          <div className="action-retry-menu-empty">Lädt…</div>
        ) : entries.length === 0 ? (
          <div className="action-retry-menu-empty">Keine fehlgeschlagenen Aktionen</div>
        ) : entries.map((entry) => {
          const note = entry.line ? classifyActionLine(entry.line) : {
            status: 'warn',
            caption: RETRY_OP_LABELS[entry.op_type] || entry.op_type,
            detail: [entry.appt_date, entry.appt_time].filter(Boolean).join(' '),
          };
          const blocked = !entry.retryable || retryingTaskIds.has(task.id);
          return (
            <button
              key={entry.id}
              type="button"
              role="menuitem"
              className={`action-retry-menu-item${blocked ? ' is-blocked' : ''}`}
              disabled={blocked}
              title={!entry.retryable ? entry.blocked_reason
                : retryingTaskIds.has(task.id) ? 'Erneutes Senden läuft bereits'
                : 'Diese Aktion erneut senden'}
              onClick={(e) => { e.stopPropagation(); closeRetryMenu(); handleRetryActions(task, [entry.id]); }}
            >
              <span className="action-note-glyph" aria-hidden="true">{NOTE_GLYPH[note.status] || '•'}</span>
              <span className="action-retry-menu-item-body">
                {note.caption && <span className="action-note-label">{note.caption}</span>}
                {note.detail && <span className="action-note-detail">{note.detail}</span>}
              </span>
              <span className="action-retry-menu-item-send" aria-hidden="true">↻</span>
            </button>
          );
        })}
      </div>,
      document.body
    );
  };

  // Caller column, from parseCallPersons: the guardian line (under the card name
  // or a "Termin für" person) and the "Termin für" block under the numbers;
  // "Weitere Termine für" when the contact booked for himself in the same call.
  const renderGuardianLine = (name) => (
    <div className="caller-guardian">
      Erziehungsberechtigt: <span className="caller-wrap-unit">{highlightMatch(name, searchTerms)}</span>
    </div>
  );
  const renderCallPersons = ({ others, cardBooked }) => (others.length ? (
    <div className="caller-persons">
      <div className="action-notes-caption">{cardBooked ? 'Weitere Termine für' : 'Termin für'}</div>
      {others.map(p => (
        <div key={p.name} className="caller-person">
          <span className="caller-person-name">{highlightMatch(p.name, searchTerms)}</span>
          {p.birthdate && <>{' '}<span className="caller-wrap-unit">· {highlightMatch(p.birthdate, searchTerms)}</span></>}
          {p.guardian && renderGuardianLine(p.guardian)}
        </div>
      ))}
    </div>
  ) : null);

  // Read-view renderer for the description cell: prose (primary) + the ✅ action
  // receipt. Practice 📌 notes render separately via renderTaskNotes (a sibling
  // of the clickable read box) so their controls don't collide with prose-edit.
  const placeholderSpan = <span style={{ color: '#bbb', fontStyle: 'italic' }}>Klick zum Hinzufügen</span>;
  const renderTaskDescription = (task, terms = searchTerms) => {
    const raw = task.description;
    if (!raw || raw === '-') return placeholderSpan;
    const { prose: storedProse, actionRaw } = parseDescriptionZones(raw);
    const prose = isDeskbotProcessing(task) ? storedProse.replace(HINWEIS_CHECK, PROCESSING_HINWEIS) : storedProse;
    const actionNotes = actionRaw ? parseActionSummary(actionRaw).notes : [];
    if (actionNotes.length === 0) {
      return prose ? highlightMatch(prose, terms) : placeholderSpan;
    }
    return (
      <>
        {prose && <div className="description-prose">{highlightMatch(prose, terms)}</div>}
        {renderActionNotesGroup(actionNotes, {
          terms, smsTarget: smsTargetForTask(task),
          sentMessages: task.sentMessages,
          onOpenMessage: (id, el) => openSentMessage(task, id, el),
          processingLines: inFlightLines(task),
          docIqTask: task,
        })}
        {isUserSuperAdmin && task.hasFailedActions === true && (
          <div className="action-retry-group" data-no-row-click>
            <button
              type="button"
              className={`action-retry-btn${retryingTaskIds.has(task.id) ? ' is-retrying' : ''}`}
              data-no-row-click
              disabled={retryingTaskIds.has(task.id)}
              onClick={(e) => { e.stopPropagation(); handleRetryActions(task); }}
              title="Fehlgeschlagene Aktionen erneut senden (nur Admin)"
            >
              <span className="action-retry-icon" aria-hidden="true">↻</span>
              {retryingTaskIds.has(task.id) ? 'Wird erneut gesendet…' : 'Aktionen erneut senden'}
            </button>
            <button
              type="button"
              className={`action-retry-btn action-retry-menu-trigger${retryMenuTaskId === task.id ? ' is-open' : ''}`}
              data-no-row-click
              disabled={retryingTaskIds.has(task.id)}
              onClick={(e) => { e.stopPropagation(); openRetryMenu(task, e.currentTarget); }}
              title="Einzelne fehlgeschlagene Aktion erneut senden (nur Admin)"
            >
              Einzeln senden
              <span className="action-retry-caret" aria-hidden="true">▾</span>
            </button>
            {retryMenuTaskId === task.id && retryMenuRect && renderRetryMenu(task)}
          </div>
        )}
        {/* Everyone but super_admin: the caller SMS / E-Mail only, never a PVS action.
            An e-mail without a valid address needs one first, so then super_admin gets it too. */}
        {(!isUserSuperAdmin || task.unsentEmailNeedsAddress) && task.hasUnsentNotification === true && (
          <div className="action-retry-group action-retry-group--wrap" data-no-row-click>
            <button
              type="button"
              className={`action-retry-btn${retryingTaskIds.has(task.id) ? ' is-retrying' : ''}`}
              data-no-row-click
              disabled={retryingTaskIds.has(task.id)}
              onClick={(e) => {
                e.stopPropagation();
                if (task.unsentEmailNeedsAddress) {
                  setEmailFixTaskId(prev => (prev === task.id ? null : task.id));
                  setEmailFixValue('');
                  setEmailFixError('');
                } else {
                  handleRetryActions(task, null, 'send-notifications');
                }
              }}
              title={task.unsentEmailNeedsAddress
                ? 'Die E-Mail-Adresse fehlt oder ist ungültig. Tragen Sie die richtige Adresse ein, dann wird die Bestätigung gesendet.'
                : 'Sendet die nicht versendete SMS bzw. E-Mail mit dem gespeicherten Text an den Patienten. Über „Text bearbeiten“ können Sie den Text vorher ändern.'}
            >
              <span className="action-retry-icon" aria-hidden="true">↻</span>
              {retryingTaskIds.has(task.id) ? 'Wird erneut gesendet…' : 'Benachrichtigungen erneut senden'}
            </button>
            {/* Opens the viewer's editor on the first message that can still be sent. */}
            {task.sentMessages?.some(m => m.sendable) && (
              <button
                type="button"
                className="action-retry-btn"
                data-no-row-click
                onClick={(e) => { e.stopPropagation(); openSentMessage(task, task.sentMessages.find(m => m.sendable).id, e.currentTarget); }}
                title="SMS bzw. E-Mail vor dem Senden ansehen und ändern."
              >
                <span className="action-retry-icon" aria-hidden="true">{actionIcons.pen}</span>
                Text bearbeiten
              </button>
            )}
          </div>
        )}
        {task.unsentEmailNeedsAddress && emailFixTaskId === task.id && !retryingTaskIds.has(task.id) && (
          <form
            className="action-email-fix"
            data-no-row-click
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); submitEmailFix(task); }}
          >
            <label className="action-email-fix-label" htmlFor={`email-fix-${task.id}`}>
              E-Mail-Adresse des Patienten
            </label>
            <div className="action-email-fix-row">
              <input
                id={`email-fix-${task.id}`}
                type="email"
                className="action-email-fix-input"
                value={emailFixValue}
                autoFocus
                placeholder="name@beispiel.de"
                onChange={(e) => { setEmailFixValue(e.target.value); setEmailFixError(''); }}
                onKeyDown={(e) => e.stopPropagation()}
              />
              <button type="submit" className="action-retry-btn">Senden</button>
            </div>
            {emailFixError && <div className="action-email-fix-error" role="alert">{emailFixError}</div>}
          </form>
        )}
      </>
    );
  };

  // The sent-message popup: what the bot actually sent, verbatim.
  //
  // Centered modal reusing the .shortcut-help-* shape (the page's cleanest
  // class-based modal), NOT anchored like .caller-popover / .action-retry-menu:
  // both of those re-anchor on a capturing scroll listener, and a panel that
  // scrolls internally would jitter against it. z-index 1500 clears the
  // slide-over's 1000, so it can be opened from the panel's own preview.
  const renderSentMessageModal = () => {
    const list = sentMessageList;
    // Fall back to the first message if the clicked id is gone (the log was
    // rewritten between the row's metadata and this fetch).
    const active = list ? (list.find(m => m.id === sentMessageActiveId) || list[0] || null) : null;
    const channelName = (m) => MESSAGE_CHANNEL_DE[m.channel] || 'Nachricht';
    const hasTabs = !!(list && list.length > 1);
    // Tab label = the note's own caption ("SMS", "Formular-SMS", "E-Mail",
    // "Therapeut-SMS"), so two messages on the SAME channel stay tellable
    // apart; the channel name is only the fallback.
    const tabLabel = (m) => (m.line ? classifyActionLine(m.line).caption : '') || channelName(m);

    // A message that can still be sent opens editable; it locks the moment the
    // live row says it no longer is (the re-fetch follows).
    const liveActive = active && liveSentMessages?.find(l => l.id === active.id);
    const editing = !!active?.sendable && liveActive?.sendable !== false;
    const draft = editing ? sentMessageDraft(active) : null;
    const isSms = active?.channel === 'sms';
    const running = sentMessageRunning;
    const smsLen = editing && isSms ? smsLength(foldSms(draft.body)) : 0;
    const smsNow = smsParts(smsLen);
    const smsBefore = editing && isSms ? smsParts(smsLength(active.body || '')) : 1;
    // What blocks the send (the backend refuses the same with its own text).
    const problem = !editing ? ''
      : !(isSms ? foldSms(draft.body) : draft.body).trim() ? 'Der Text darf nicht leer sein.'
      : !isSms && !draft.subject.trim() ? 'Bitte geben Sie einen Betreff ein.'
      : !linksKept(active.body || '', draft.body)
        ? 'Der Link wurde verändert oder entfernt. Stellen Sie ihn wieder her oder setzen Sie den Text zurück.'
      : active.needs_address && !draft.email.trim() ? 'Bitte geben Sie zuerst die E-Mail-Adresse des Patienten ein.'
      : '';
    const title = editing ? MESSAGE_EDIT_LABEL[active.channel] : 'Gesendete Nachricht';
    const note = active ? sentMessageNotes[active.id] : '';

    // Focus trap: the popup is the topmost layer, so Tab must not walk into the
    // slide-over or the table beneath it (the editor adds fields to the buttons).
    const trapTab = (e) => {
      if (e.key !== 'Tab' || !sentMessageModalRef.current) return;
      const items = Array.from(sentMessageModalRef.current.querySelectorAll('button:not([disabled]), textarea, input:not([disabled])'));
      if (!items.length) return;
      const first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };

    // Every meta row renders only when its field is present — an e-mail has a
    // Betreff, an SMS never does, and pre-timestamp entries have no send time.
    // Editing: the Betreff is an input, the Empfänger only when the e-mail
    // address needs fixing (the phone number never is).
    const metaRows = active ? [
      ['Absender', active.sender],
      ['Empfänger', editing && active.needs_address ? (
        <input type="email" className="sent-message-input" value={draft.email} placeholder="name@beispiel.de"
               aria-label="E-Mail-Adresse des Patienten" disabled={running}
               onChange={(e) => editSentMessage(active, { email: e.target.value })} />
      ) : active.recipient],
      ['Betreff', editing && !isSms ? (
        <input type="text" className="sent-message-input" value={draft.subject} aria-label="Betreff"
               disabled={running} onChange={(e) => editSentMessage(active, { subject: e.target.value })} />
      ) : active.subject],
      ['Gesendet am', formatNoteTimestampFull(active.sent_at)],
      ['Bearbeitet', active.edited_by && `${active.edited_by} · ${formatNoteTimestampFull(active.edited_at)}`],
    ].filter(([, value]) => value) : [];

    return (
      <div
        className="sent-message-backdrop"
        onClick={(e) => { if (e.target === e.currentTarget) requestCloseSentMessage(); }}
      >
        <div
          className="sent-message-modal"
          ref={sentMessageModalRef}
          role="dialog"
          aria-modal="true"
          aria-label={title}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={trapTab}
        >
          <div className="sent-message-header">
            <h3>{title}</h3>
            <button
              type="button"
              className="sent-message-close"
              onClick={requestCloseSentMessage}
              aria-label="Schließen"
              ref={(el) => { if (el && !el.dataset.autofocused) { el.dataset.autofocused = '1'; el.focus(); } }}
            >×</button>
          </div>
          {/* Tab strip only when there IS a choice. One call can send both an
              SMS and an e-mail, and N separate popups would make "what did we
              send this caller?" an N-click question. */}
          {hasTabs && (
            <div className="sent-message-tabs" role="tablist">
              {list.map(m => (
                <button
                  key={m.id}
                  type="button"
                  role="tab"
                  aria-selected={!!active && m.id === active.id}
                  className={`sent-message-tab${active && m.id === active.id ? ' is-active' : ''}`}
                  onClick={() => setSentMessageActiveId(m.id)}
                >{tabLabel(m)}</button>
              ))}
            </div>
          )}
          {/* role=tabpanel only alongside the tab strip: a tablist with no
              panel is an incomplete ARIA pattern, and so is a lone panel. */}
          <div className="sent-message-body" {...(hasTabs ? { role: 'tabpanel' } : {})}>
            {!list ? (
              <div className="sent-message-empty">Lädt…</div>
            ) : !active ? (
              <div className="sent-message-empty">Kein Inhalt gespeichert</div>
            ) : (
              <>
                <div className="sent-message-meta">
                  <div className="sent-message-meta-row">
                    <span className="sent-message-meta-label">Kanal</span>
                    <span className="sent-message-meta-value">
                      {channelName(active)}
                      {editing ? (
                        <span className="sent-message-status is-failed">Nicht versendet</span>
                      ) : MESSAGE_STATUS_DE[active.status] && (
                        <span className={`sent-message-status is-${active.status}`}>
                          {MESSAGE_STATUS_DE[active.status]}
                        </span>
                      )}
                    </span>
                  </div>
                  {metaRows.map(([label, value]) => (
                    <div key={label} className="sent-message-meta-row">
                      <span className="sent-message-meta-label">{label}</span>
                      <span className="sent-message-meta-value">{value}</span>
                    </div>
                  ))}
                </div>
                {editing ? (
                  <>
                    <p className="sent-message-lead">{isSms
                      ? 'Sie können den Text vor dem Senden ändern. In der SMS werden Umlaute als ae, oe, ue und ss gesendet, andere Sonderzeichen entfallen.'
                      : 'Sie können Betreff und Text vor dem Senden ändern.'}</p>
                    <textarea
                      className="sent-message-text sent-message-editor"
                      value={draft.body}
                      rows={isSms ? 6 : 12}
                      readOnly={running}
                      aria-label={isSms ? 'Text der SMS' : 'Text der E-Mail'}
                      onChange={(e) => editSentMessage(active, { body: e.target.value })}
                    />
                    {(problem || isSms) && (
                      <div className="sent-message-editor-meta">
                        <span className="sent-message-problem" role="alert">{problem}</span>
                        {isSms && (
                          <span className={`sent-message-counter${smsNow > smsBefore ? ' is-more' : ''}`}
                                title="Eine SMS fasst 160 Zeichen. Längere Texte werden als mehrere SMS gesendet und berechnet.">
                            {smsLen <= 160 ? `${smsLen} / 160 Zeichen · 1 SMS`
                              : `${smsLen} Zeichen · ${smsNow} SMS${smsNow > smsBefore ? ` statt ${smsBefore}` : ''}`}
                          </span>
                        )}
                      </div>
                    )}
                  </>
                ) : active.body
                  // React text child — escaped by construction.
                  ? <pre className="sent-message-text">{active.body}</pre>
                  : <div className="sent-message-empty">Kein Inhalt gespeichert</div>}
                {note && <div className="sent-message-note" role="status">{note}</div>}
              </>
            )}
          </div>
          {/* Outside the scrolling body, so "Senden" stays in view. The discard
              bar also shows on a read-only tab when another tab has edits. */}
          {(editing || sentMessageConfirmClose) && (
            <div className="sent-message-footer">
              {sentMessageConfirmClose ? (
                <>
                  <span className="sent-message-footer-text">Ihre Änderungen wurden noch nicht gesendet.</span>
                  <button type="button" className="sent-message-btn" onClick={() => setSentMessageConfirmClose(false)}>
                    Weiter bearbeiten
                  </button>
                  <button type="button" className="sent-message-btn is-danger" onClick={closeSentMessage}>Verwerfen</button>
                </>
              ) : (
                <>
                  {isSentMessageDirty(active) && !running && (
                    <button type="button" className="sent-message-reset" title="Gespeicherten Text wiederherstellen"
                            onClick={() => dropSentMessageDraft(active.id)}>Zurücksetzen</button>
                  )}
                  <button type="button" className="sent-message-btn" onClick={requestCloseSentMessage}>Abbrechen</button>
                  <button
                    type="button"
                    className="sent-message-btn is-primary"
                    disabled={!!problem || running}
                    onClick={() => sendSentMessage(sentMessageTask, active)}
                  >
                    {running && sentMessageSendingId === active.id
                      ? <><span className="sent-message-spin" aria-hidden="true">↻</span>Wird gesendet…</>
                      : <>{actionIcons.mailSend}{isSms ? 'SMS senden' : 'E-Mail senden'}</>}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Practice-authored 📌 notes for a task row: a compact, human "margin note"
  // group with inline edit + remove, plus a hover-revealed "+ Notiz" affordance.
  // Note-less rows render only the ghost add button (no space at rest).
  const renderNoteEditor = (taskId) => (
    <input
      ref={(el) => { if (el && !el.dataset.noteFocused) { el.dataset.noteFocused = '1'; el.focus(); } }}
      type="text"
      className="task-note-input"
      value={noteDraft}
      placeholder="Notiz…"
      maxLength={280}
      onChange={(e) => setNoteDraft(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); saveNoteInline(taskId); }
        else if (e.key === 'Escape') { e.preventDefault(); cancelNoteEditor(); }
      }}
      onBlur={() => saveNoteInline(taskId)}
      data-no-row-click
    />
  );
  const renderTaskNotes = (task, terms = searchTerms) => {
    const { notes } = parseDescriptionZones(task.description);
    const isAdding = editingNote && editingNote.taskId === task.id && editingNote.index === -1;
    return (
      <div className={`task-notes${notes.length === 0 && !isAdding ? ' task-notes--empty' : ''}`} data-no-row-click>
        {notes.map((note, i) => {
          const isEditing = editingNote && editingNote.taskId === task.id && editingNote.index === i;
          return (
            <div key={i} className="task-note">
              <span className="task-note-pin" aria-hidden="true">📌</span>
              {isEditing ? renderNoteEditor(task.id) : (
                <>
                  <div className="task-note-main">
                    {renderNoteByline(note, 'task-note-byline')}
                    <span
                      className="task-note-text"
                      title="Notiz bearbeiten"
                      onClick={(e) => { e.stopPropagation(); openNoteEditor(task.id, i, note.text); }}
                      data-no-row-click
                    >
                      {highlightMatch(note.text, terms)}
                    </span>
                  </div>
                  {canDeleteNotes && (
                    <button
                      type="button"
                      className="task-note-remove"
                      title="Notiz entfernen"
                      onClick={(e) => { e.stopPropagation(); removeNoteInline(task.id, i); }}
                      data-no-row-click
                    >✕</button>
                  )}
                </>
              )}
            </div>
          );
        })}
        {isAdding ? (
          <div className="task-note task-note--new">
            <span className="task-note-pin" aria-hidden="true">📌</span>
            {renderNoteEditor(task.id)}
          </div>
        ) : (
          <button
            type="button"
            className="task-note-add"
            title="Notiz hinzufügen"
            onClick={(e) => { e.stopPropagation(); openNoteEditor(task.id, -1, ''); }}
            data-no-row-click
          >+ Notiz</button>
        )}
      </div>
    );
  };

  // "14:32" for a moment today, "24.07." for an earlier day (browser-local).
  const formatClockOrDay = (ts) => {
    const d = ts ? new Date(ts) : null;
    if (!d || isNaN(d.getTime())) return '';
    return d.toDateString() === new Date().toDateString()
      ? d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  };
  const formatClock = (ts) => {
    const d = ts ? new Date(ts) : null;
    return d && !isNaN(d.getTime()) ? d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : '';
  };

  // "Zu tun": the task's to-dos as a sibling of the read box, in the Aktionen
  // ledger style, with a checkbox in the glyph column. Server order; nothing
  // is derived here. While the failure to-do is in flight, one
  // "Deskbot bearbeitet …" row stands in its place.
  const renderTaskTodos = (task, { terms = searchTerms, showCaption = true, className = '' } = {}) => {
    const todos = task.todos || [];
    if (!todos.length) return null;
    const shown = todos.filter(td => isTodoShown(task, td));
    // Meanwhile the receipt draws the covered lines with the spinner too.
    const heldBack = todos.some(td => td.state === 'open' && !isTodoShown(task, td));
    if (!shown.length && !heldBack) return null;
    const openCount = shown.filter(td => td.state === 'open').length;
    return (
      <div className={`action-notes todo-list${className ? ` ${className}` : ''}`}
           data-no-row-click onClick={(e) => e.stopPropagation()}>
        {showCaption && <div className="action-notes-caption">{openCount ? `Zu tun · ${openCount} offen` : 'Zu tun'}</div>}
        {heldBack && (
          <div className="action-note is-processing">
            <span className="action-note-glyph" aria-hidden="true">{NOTE_GLYPH.processing}</span>
            <span className="action-note-body">
              <span className="action-note-detail">Deskbot bearbeitet diese Aufgabe noch</span>
            </span>
          </div>
        )}
        {shown.map((td) => {
          const done = td.state === 'done';
          const who = done ? [td.closed_by, formatClockOrDay(td.closed_at)].filter(Boolean).join(' ') : '';
          const byline = !done ? ''
            : td.closed_how === 'with_task' ? `mit Aufgabe abgeschlossen${who ? `, ${who}` : ''}` : who;
          const armKey = `${task.id}:${td.id}`;
          const armed = todoDeleteArmed === armKey;
          return (
            <label key={td.id} className={`action-note todo-item${done ? ' is-done' : ''}`}
                   onMouseLeave={armed ? () => setTodoDeleteArmed(null) : undefined}>
              <span className="action-note-glyph">
                <input
                  type="checkbox"
                  className="todo-check"
                  checked={done}
                  onChange={() => handleTodoToggle(task.id, td.id, !done)}
                />
              </span>
              <span className="action-note-body">
                <span className="todo-text">{highlightMatch(td.text, terms)}</span>
                {byline && <span className="todo-byline" title={formatNoteTimestampFull(td.closed_at)}>{` · ${byline}`}</span>}
                {/* Not real work (e.g. a callback that turned out not to need a booking):
                    × right after the text arms, the second click deletes; leaving the
                    row disarms. */}
                <button
                  type="button"
                  className={`todo-delete${armed ? ' is-armed' : ''}`}
                  title={armed ? 'To-do löschen' : 'To-do löschen (kein echtes To-do)'}
                  aria-label={armed ? 'To-do jetzt löschen' : 'To-do löschen'}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (armed) handleTodoDelete(task.id, td.id);
                    else setTodoDeleteArmed(armKey);
                  }}
                  onBlur={armed ? () => setTodoDeleteArmed(null) : undefined}
                >
                  {armed ? 'Löschen?' : '×'}
                </button>
              </span>
            </label>
          );
        })}
      </div>
    );
  };

  // "Anrufe heute": the calls this row stands for (missed calls, and missed
  // calls absorbed into this task), with the staff state a missed row carried
  // when it was absorbed. Lines are fixed backend strings; only
  // "dieses Gespräch" (the task's own call) is worded here. Per-call artifacts
  // open through the call's own ids, never the row's.
  const renderCallLog = (task, terms = searchTerms) => {
    const lines = task.callLog || [];
    if (!(lines.some(e => e.kind !== 'self') || (task.missedRow && lines.length))) return null;
    const rowDay = callStartOf(task) ? new Date(callStartOf(task)) : null;
    const caption = !rowDay || isNaN(rowDay.getTime()) || rowDay.toDateString() === new Date().toDateString()
      ? 'Anrufe heute'
      : `Anrufe am ${rowDay.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}`;
    return (
      <div className="action-notes call-log" data-no-row-click onClick={(e) => e.stopPropagation()}>
        <div className="action-notes-caption">{caption}</div>
        {lines.map((e, i) => {
          const isSelf = e.kind === 'self';
          const ann = e.annotation;
          // resolveAssigneeName returns the raw id for an unknown member: no name then.
          const assignee = ann?.assignee_id && allMembers.some(u => u.id === ann.assignee_id)
            ? resolveAssigneeName(ann.assignee_id) : null;
          const annParts = ann ? [
            ann.status ? getGermanStatus(ann.status) : null,
            assignee,
            ...(ann.notes || []).filter(n => n?.text).map(n => `📌 ${n.text}${n.author ? ` (${n.author})` : ''}`),
          ].filter(Boolean) : [];
          const text = `${isSelf ? 'dieses Gespräch' : e.line}${annParts.length ? ` · ${annParts.join(' · ')}` : ''}`;
          return (
            <div key={e.id || i} className={`action-note call-log-line${isSelf ? ' is-self' : ''}`}>
              <span className="call-log-time">{formatClock(e.at)}</span>
              <span className="action-note-body">
                <span className="action-note-detail">{highlightMatch(text, terms)}</span>
              </span>
              {!isSelf && e.conversation_id && (
                <button
                  type="button"
                  className="action-note-view"
                  data-no-row-click
                  aria-label="Transkript"
                  title="Transkript"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    handleViewConversation(task.id, {
                      conversationId: e.conversation_id, sessionId: e.session_id, voicemailId: e.voicemail_id,
                    });
                  }}
                >
                  <span className="action-note-view-icon" aria-hidden="true">{actionIcons.chat}</span>
                </button>
              )}
              {!isSelf && e.voicemail_id && (
                <button
                  type="button"
                  className="action-note-view"
                  data-no-row-click
                  aria-label="Nachricht abspielen"
                  title="Nachricht abspielen"
                  onClick={(ev) => { ev.stopPropagation(); playVoicemailAudio(task.id, e.voicemail_id); }}
                >
                  <span className="action-note-view-icon" aria-hidden="true">{'▶︎'}</span>
                </button>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Normalize a phone number to its national-significant digits (no country
   // code, no leading 0). Lets "015773437511", "+49 15773 437511" and
   // "0049-15773 437511" all match each other.
  const normalizePhone = (input) => {
    let digits = String(input || '').replace(/\D/g, '');
    if (digits.startsWith('0049')) digits = digits.slice(4);
    else if (digits.startsWith('49') && digits.length > 10) digits = digits.slice(2);
    else if (digits.startsWith('0')) digits = digits.slice(1);
    return digits;
  };

  // Format birthdate as "29.06.1988" (with leading zeros), German style
  const formatBirthdate = (d) => {
    const date = new Date(d);
    if (isNaN(date.getTime())) return '';
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    return `${dd}.${mm}.${date.getFullYear()}`;
  };

  // The prescription issue date is a FREE-TEXT column, not a date: prod holds ISO
  // ("2026-07-27"), German numeric with a full, short or absent year ("4.7.2026",
  // "23.07.26", "16.7."), and plain words ("Heute", "23. Juli", "letzten Freitag").
  // new Date() reads the German forms as US month/day and fabricates a year for the
  // year-less ones ("04.07." -> 07.04.2001), so parse the numeric shapes by hand and
  // hand everything else back untouched. Never guesses a year that isn't in the value.
  const formatIssueDate = (v) => {
    const s = String(v ?? '').trim();
    if (!s) return '';
    const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (iso) return `${iso[3].padStart(2, '0')}.${iso[2].padStart(2, '0')}.${iso[1]}`;
    const de = s.match(/^(\d{1,2})\.\s*(\d{1,2})\.?\s*(\d{4}|\d{2})?\.?$/);
    if (de) {
      const day = Number(de[1]);
      const month = Number(de[2]);
      if (day < 1 || day > 31 || month < 1 || month > 12) return s;
      const dd = String(day).padStart(2, '0');
      const mm = String(month).padStart(2, '0');
      if (!de[3]) return `${dd}.${mm}.`;
      return `${dd}.${mm}.${de[3].length === 2 ? `20${de[3]}` : de[3]}`;
    }
    return s;
  };

  // --- Practice-note authorship byline ---------------------------------------
  // The signed-in user's first name — the author stamped onto a note at creation.
  // Falls back to preferred_username; '' when neither is known (→ unknown author,
  // rendered as date/time only).
  const currentUserFirstName = () => {
    const tp = (keycloak && keycloak.tokenParsed) || {};
    return String(tp.given_name || tp.preferred_username || '').trim();
  };

  // Compact, browser-local note date+time: "24.07. 14:32" (no year, no seconds).
  const formatNoteDateTime = (ts) => {
    if (!ts) return '';
    const date = new Date(ts);
    if (isNaN(date.getTime())) return '';
    const day = date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
    const time = date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    return `${day} ${time}`;
  };

  // Full timestamp for the byline tooltip: "24.07.2026, 14:32:45" (browser-local).
  const formatNoteTimestampFull = (ts) => {
    if (!ts) return '';
    const date = new Date(ts);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  };

  // Compact byline as a flat string ("Anna · 24.07. 14:32" / date-only / '') —
  // used as the byline's aria-label so screen readers read it as one phrase.
  const formatNoteByline = (author, ts) =>
    [String(author || '').trim(), formatNoteDateTime(ts)].filter(Boolean).join(' · ');

  // Render the two-tone byline sub-line (author span + datetime span) with a
  // full-timestamp tooltip. Returns null when a note carries no metadata
  // (legacy or a brand-new note not yet saved) → no byline is shown.
  const renderNoteByline = (note, className) => {
    if (!note) return null;
    const name = String(note.author || '').trim();
    const dt = formatNoteDateTime(note.ts);
    if (!name && !dt) return null;
    return (
      <div className={className} title={formatNoteTimestampFull(note.ts)} aria-label={formatNoteByline(note.author, note.ts)}>
        {name && <span className="note-byline-author">{name}</span>}
        {name && dt && <span className="note-byline-sep"> · </span>}
        {dt && <span className="note-byline-time">{dt}</span>}
      </div>
    );
  };

  // Stamp brand-new (`_new`) notes with author + timestamp at first persist. The
  // SINGLE source of authorship stamping, shared by BOTH save paths (inline row +
  // modal). Legacy / already-stamped notes have no `_new` flag → left untouched,
  // so they compose back byte-unchanged (never fabricate an author for them).
  const stampNewNotes = (notes) => (notes || []).map(n =>
    (n && typeof n === 'object' && n._new && String(n.text || '').trim() && !n.author && !n.ts)
      ? { ...n, author: currentUserFirstName(), ts: new Date().toISOString() }
      : n
  );

  // Strip whitespace for tel: URI; keep + and digits
  const phoneToTelHref = (phone) => `tel:${String(phone || '').replace(/[^\d+]/g, '')}`;

  // Builds a mailto: href for forwarding a task by email.
  const taskMailtoHref = (task) => {
    const subject = `Thema: ${(Array.isArray(task.callerName) ? task.callerName[0] : task.callerName) || 'Unbekannt'}${task.callReasons && task.callReasons.length > 0 ? ` - ${formatCallReasons(task.callReasons)}` : ''}`;
    const body =
      `Details:\n\n` +
      `Anrufername: ${(Array.isArray(task.callerName) ? task.callerName.join(' / ') : task.callerName) || 'Nicht angegeben'}\n` +
      `Telefonnummer: ${task.phoneNumber || 'Nicht angegeben'}\n` +
      `Zusammenfassung: ${task.description || 'Keine'}\n` +
      `Datum der Anfrage: ${task.createdAt ? new Date(task.createdAt).toLocaleString('de-DE') : 'Nicht angegeben'}`;
    return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  // Task icon → support window (components/support/SupportWindow.js), which shows
  // the open request about this task again or starts one showing its card.
  // Placeholders ("-", "k.A.", "anonym", ...) travel as null, not as a value.
  const openSupportForTask = (task) => {
    const name = Array.isArray(task.callerName) ? task.callerName[0] : task.callerName;
    let phone = null;
    if (isClusterablePhone(task.phoneNumber)) phone = task.phoneNumber;
    else if (isClusterablePhone(task.callerIdNumber)) phone = task.callerIdNumber;
    window.dispatchEvent(new CustomEvent('deskbot-support-open', {
      detail: {
        practiceId,
        taskId: task.id,
        taskCreatedAt: task.createdAt || null,
        callerName: realValue(name),
        phone,
      },
    }));
  };

  // Ticket icon (DeskBot staff) → the tracker's own ticket dialog in a small
  // window, filled in with the practice, the call time, the link back to this
  // task and the text the copy button under its conversation copies (the
  // recording time from that conversation, as there). The tracker page gets no
  // handle on this app's window (openTrackerTicketWindow).
  const openTrackerTicket = (task) => openTrackerTicketWindow(task, async () => {
    if (!task.sessionId) return ''; // the copy button needs a session too
    const recordedAt = await axios.get(`${API_BASE_URL}/tasks/${task.id}/conversation`)
      .then((response) => response.data?.created_at || null, () => null);
    return conversationCopyText({
      recordedAt,
      sessionId: task.sessionId,
      phoneNumber: taskPhone(task),
      practiceName: practiceNameById.get(task.practiceId) || currentPractice?.practice_name || null,
    });
  });

  const handleCopy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItem(key);
      if (copiedItemTimerRef.current) clearTimeout(copiedItemTimerRef.current);
      copiedItemTimerRef.current = setTimeout(() => setCopiedItem(null), 1500);
    } catch {
      // Clipboard API unavailable (older browsers / non-secure context)
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!practiceId) {
      setErrorMessage('Cannot save task: No practice found.');
      return;
    }
    
    // Prepare task data for API (use "-" for empty fields)
    let callerNameValue = currentTask.callerName && currentTask.callerName.length > 0 && currentTask.callerName[0].trim()
      ? currentTask.callerName
      : ["-"];
    // Preserve __pvs__ marker if original task had it
    if (currentTask._rawCallerName?.includes('__pvs__') && !callerNameValue.includes('__pvs__')) {
      callerNameValue = [...callerNameValue, '__pvs__'];
    }
    const phoneNumberValue = currentTask.phoneNumber && currentTask.phoneNumber.trim() ? currentTask.phoneNumber : "-";
    // currentTask.description holds the edited PROSE ONLY; splice the practice
    // notes + the preserved action block back in (receipt never edited away).
    // stampNewNotes assigns author + now to any modal-ADDED note (`_new`); legacy
    // and already-stamped notes keep their original author/ts (or none).
    const descriptionValue = composeDescription(currentTask.description, stampNewNotes(currentTask._descriptionNotes || []), currentTask._descriptionActionRaw || '');

    // Status left untouched in the panel: send the LIVE one (a to-do tick may
    // have closed or reopened the task meanwhile).
    const liveStatus = currentTask.id && currentTask.status === currentTask._statusAtOpen
      ? (tasks.find(t => t.id === currentTask.id)?.status ?? currentTask.status)
      : currentTask.status;
    const taskData = buildPreservedTaskPayload(currentTask, {
      caller_name: callerNameValue,
      phone_number: phoneNumberValue,
      description: descriptionValue,
      status: liveStatus,
      due_date: currentTask.dueDate ? new Date(currentTask.dueDate).toISOString() : null,
      assignee_id: currentTask.assigneeId || null,
      caller_birthdate: currentTask.callerBirthdate || null,
    });
    
    try {
      let savedTask;
      
      if (currentTask.id) {
        // Update existing task
        const response = await axios.put(`${API_BASE_URL}/tasks/${currentTask.id}`, taskData);
        console.log('Task updated:', response.data);

        // Preserve all existing fields (stats, isExistingPatient, etc.) + override with API response
        const existingTask = tasks.find(t => t.id === currentTask.id);

        savedTask = {
          ...mergePreservingStats(existingTask || {}, formatTask(response.data)),
          createdAt: response.data.created_at || existingTask?.createdAt || '',
        };

        // Update tasks state
        setTasks(tasks.map(task =>
          task.id === currentTask.id ? savedTask : task
        ));
      } else {
        // Create new task
        const response = await axios.post(`${API_BASE_URL}/tasks/`, taskData, {
          params: { practice_id: practiceId }
        });
        console.log('Task created:', response.data);

        savedTask = formatTask(response.data);

        // Add to top of tasks list if current filter matches the status
        if (statusFilters.length === 0 || statusFilters.includes(savedTask.status)) {
          setTasks([savedTask, ...tasks]);
        }
        // Track in knownTaskIds to prevent WebSocket duplicate
        knownTaskIds.current.add(savedTask.id);
      }
      
      setErrorMessage('');
    } catch (error) {
      console.error('Error saving task:', error);
      if (!handleTaskConflict(error)) {
        setErrorMessage(`Error saving task: ${error.response?.data?.detail || error.message}`);
      }
    }
    
    setShowForm(false);
  };

  // Generic save for inline single-line fields (Name, Phone, Birthdate).
  const saveFieldInline = async (taskId, field, rawValue) => {
    const taskBefore = tasks.find(t => t.id === taskId);
    if (!taskBefore) return;
    setEditingFieldKey(null);
    let newValue = rawValue;
    if (field === 'callerName') {
      newValue = rawValue.trim();
      if (!newValue) newValue = '-';
    } else if (field === 'phoneNumber') {
      newValue = rawValue.trim() || '-';
    } else if (field === 'callerBirthdate') {
      // rawValue from <input type="date"> is YYYY-MM-DD, accept also empty
      newValue = rawValue || null;
    }

    let oldValue;
    if (field === 'callerName') oldValue = Array.isArray(taskBefore.callerName) ? taskBefore.callerName[0] : taskBefore.callerName;
    else if (field === 'phoneNumber') oldValue = taskBefore.phoneNumber;
    else if (field === 'callerBirthdate') oldValue = taskBefore.callerBirthdate ? String(taskBefore.callerBirthdate).substr(0, 10) : null;
    if (oldValue === newValue) return;

    // Optimistic update
    setTasks(prev => prev.map(t => {
      if (t.id !== taskId) return t;
      if (field === 'callerName') {
        // Preserve __pvs__ marker if present
        const hadPvs = (t._rawCallerName || []).includes('__pvs__');
        const arr = [newValue, ...(hadPvs ? ['__pvs__'] : [])];
        return { ...t, callerName: arr, _rawCallerName: arr };
      }
      if (field === 'phoneNumber') return { ...t, phoneNumber: newValue };
      if (field === 'callerBirthdate') return { ...t, callerBirthdate: newValue };
      return t;
    }));

    try {
      const callerNameForPayload = field === 'callerName'
        ? [newValue, ...((taskBefore._rawCallerName || []).includes('__pvs__') ? ['__pvs__'] : [])]
        : (taskBefore._rawCallerName || taskBefore.callerName);
      const taskData = buildPreservedTaskPayload(taskBefore, {
        caller_name: callerNameForPayload,
        phone_number: field === 'phoneNumber' ? newValue : taskBefore.phoneNumber,
        caller_birthdate: field === 'callerBirthdate' ? newValue : (taskBefore.callerBirthdate || null),
      });
      await axios.put(`${API_BASE_URL}/tasks/${taskId}`, taskData);
    } catch (error) {
      // ROLLBACK
      setTasks(prev => prev.map(t => t.id === taskId ? taskBefore : t));
      setErrorMessage(`Fehler beim Speichern: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Shared primitive: recompose the 3 zones into a full description and PUT it
  // (optimistic + rollback). Used by prose edit AND all note add/edit/remove.
  const commitDescriptionZones = async (taskId, taskBefore, prose, notes, actionRaw) => {
    const oldDescription = taskBefore.description;
    const newDescription = composeDescription(prose, notes, actionRaw);
    if (oldDescription === newDescription) return;
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, description: newDescription } : t));
    try {
      const taskData = buildPreservedTaskPayload(taskBefore, { description: newDescription });
      await axios.put(`${API_BASE_URL}/tasks/${taskId}`, taskData);
    } catch (error) {
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, description: oldDescription } : t));
      if (!handleTaskConflict(error)) {
        setErrorMessage(`Fehler beim Speichern: ${error.response?.data?.detail || error.message}`);
      }
    }
  };

  const saveDescriptionInline = async (taskId) => {
    const taskBefore = tasks.find(t => t.id === taskId);
    if (!taskBefore) return;
    // The edited draft is PROSE ONLY; keep the ORIGINAL notes + action block.
    const { notes, actionRaw } = parseDescriptionZones(taskBefore.description);
    setEditingDescriptionId(null);
    setDescriptionInitialHeight(null);
    setDescriptionInitialCaret(null);
    await commitDescriptionZones(taskId, taskBefore, descriptionDraft, notes, actionRaw);
  };

  // --- Inline practice-note (📌) editing --------------------------------------
  const openNoteEditor = (taskId, index, initialText) => {
    setNoteDraft(initialText || '');
    setEditingNote({ taskId, index });
  };
  const cancelNoteEditor = () => { setEditingNote(null); setNoteDraft(''); };

  const saveNoteInline = async (taskId) => {
    if (!editingNote || editingNote.taskId !== taskId) return; // stale blur after save
    const taskBefore = tasks.find(t => t.id === taskId);
    const editing = editingNote;
    cancelNoteEditor();
    if (!taskBefore) return;
    const { prose, notes, actionRaw } = parseDescriptionZones(taskBefore.description);
    const text = noteDraft.replace(/\s+/g, ' ').trim();
    const next = [...notes];
    if (editing.index === -1) {
      if (!text) return;            // don't add an empty note
      next.push({ text, author: null, ts: null, _new: true }); // stamped below
    } else if (editing.index < next.length) {
      if (!text) next.splice(editing.index, 1);                 // emptied edit → remove
      else next[editing.index] = { ...next[editing.index], text }; // keep author/ts
    }
    // stampNewNotes assigns author + now ONLY to the freshly-added (`_new`) note.
    await commitDescriptionZones(taskId, taskBefore, prose, stampNewNotes(next), actionRaw);
  };

  const removeNoteInline = async (taskId, index) => {
    const taskBefore = tasks.find(t => t.id === taskId);
    if (!taskBefore) return;
    if (editingNote && editingNote.taskId === taskId) cancelNoteEditor();
    const { prose, notes, actionRaw } = parseDescriptionZones(taskBefore.description);
    await commitDescriptionZones(taskId, taskBefore, prose, notes.filter((_, i) => i !== index), actionRaw);
  };

  const handleStatusChange = async (taskId, newStatus) => {
    const taskBefore = tasks.find(task => task.id === taskId);
    if (!taskBefore) return;
    const oldStatus = taskBefore.status;
    if (oldStatus === newStatus) return;

    // OPTIMISTIC update: change UI immediately. Rule 2 on the to-dos: closing
    // leaves them as they are, reopening restores the ones an earlier close ticked.
    setTasks(prev => prev.map(t => t.id === taskId
      ? { ...t, status: newStatus, todos: todosAfterStatusChange(t.todos, oldStatus, newStatus) }
      : t));
    setStatusChangedIds(prev => {
      const next = new Map(prev);
      next.set(taskId, newStatus);
      return next;
    });
    const existingTimer = statusPulseTimersRef.current.get(taskId);
    if (existingTimer) clearTimeout(existingTimer);
    const timerId = setTimeout(() => {
      statusPulseTimersRef.current.delete(taskId);
      setStatusChangedIds(prev => {
        if (!prev.has(taskId)) return prev;
        const next = new Map(prev);
        next.delete(taskId);
        return next;
      });
    }, STATUS_PULSE_CLEANUP_MS);
    statusPulseTimersRef.current.set(taskId, timerId);

    try {
      const taskToUpdate = taskBefore;
      if (!taskToUpdate) return;

      // Prepare update data (use _rawCallerName to preserve __pvs__ marker)
      const taskData = buildPreservedTaskPayload(taskToUpdate, {
        status: newStatus,
      });

      // Send request in background — UI is already updated optimistically
      const response = await axios.put(`${API_BASE_URL}/tasks/${taskId}`, taskData);
      const updatedTask = {
        ...mergePreservingStats(taskToUpdate, formatTask(response.data)),
        createdAt: response.data.created_at || taskToUpdate.createdAt || '',
      };

      // Replace the optimistic entry with the authoritative server response
      // (preserves stats etc.). Always keep the task in the cache regardless of
      // the current status filter: the render-time `filteredTasks` computation
      // hides it when its new status falls outside `statusFilters`. Removing it
      // from the cache here would drop it from the source of truth, so switching
      // the filter to "Alle" (or any preset that includes the new status)
      // wouldn't show it again until the next refetch/refresh.
      setTasks(prev => prev.map(task => task.id === taskId ? updatedTask : task));
      setErrorMessage('');
    } catch (error) {
      console.error('Error updating task status:', error);
      // ROLLBACK optimistic update
      setTasks(prev => prev.map(t => t.id === taskId ? taskBefore : t));
      if (!handleTaskConflict(error)) {
        setErrorMessage(`Fehler bei Status-Änderung: ${error.response?.data?.detail || error.message}`);
      }
    }
  };

  // Tick or untick one to-do. Optimistic (Rule 1 on the status, mirrored from
  // the server), then the server's full task replaces it.
  const handleTodoToggle = async (taskId, todoId, done) => {
    const taskBefore = tasks.find(t => t.id === taskId);
    if (!taskBefore) return;
    const byName = currentUserFirstName();
    setTasks(prev => prev.map(t => (t.id === taskId ? applyTodoTick(t, todoId, done, byName) : t)));
    try {
      const response = await axios.patch(`${API_BASE_URL}/tasks/${taskId}/todos/${todoId}`, { done });
      setTasks(prev => prev.map(t => (t.id === taskId
        ? { ...mergePreservingStats(t, formatTask(response.data)), createdAt: response.data.created_at || t.createdAt }
        : t)));
      setErrorMessage('');
    } catch (error) {
      console.error('Error toggling to-do:', error);
      setTasks(prev => prev.map(t => (t.id === taskId ? taskBefore : t)));
      if (!handleTaskConflict(error)) {
        setErrorMessage(`Fehler beim Abhaken: ${error.response?.data?.detail || error.message}`);
      }
    }
  };

  // Delete one to-do that is no real work (two clicks on its ×, see renderTaskTodos).
  // Optimistic, then the server's full task replaces it.
  const handleTodoDelete = async (taskId, todoId) => {
    setTodoDeleteArmed(null);
    const taskBefore = tasks.find(t => t.id === taskId);
    if (!taskBefore) return;
    setTasks(prev => prev.map(t => (t.id === taskId ? applyTodoDelete(t, todoId) : t)));
    try {
      const response = await axios.delete(`${API_BASE_URL}/tasks/${taskId}/todos/${todoId}`);
      setTasks(prev => prev.map(t => (t.id === taskId
        ? { ...mergePreservingStats(t, formatTask(response.data)), createdAt: response.data.created_at || t.createdAt }
        : t)));
      setErrorMessage('');
    } catch (error) {
      console.error('Error deleting to-do:', error);
      setTasks(prev => prev.map(t => (t.id === taskId ? taskBefore : t)));
      if (!handleTaskConflict(error)) {
        setErrorMessage(`Fehler beim Löschen des To-dos: ${error.response?.data?.detail || error.message}`);
      }
    }
  };

  // Bulk-completes every non-terminal task that shares the phone number of
  // `taskId`. Used by the "Alle abschließen" entry in the inline status menu.
  // See BULK_COMPLETE_EXCLUDED for what is left untouched.
  const handleCompleteAllForPhone = async (taskId) => {
    const seedTask = tasks.find(t => t.id === taskId);
    const key = seedTask && clusterKeyOf(seedTask);
    if (!key) return;
    const targets = tasks.filter(t =>
      clusterKeyOf(t) === key && isBulkCompletable(t.status)
    );
    if (targets.length === 0) return;
    for (const t of targets) {
      // Sequential to keep error handling/rollback per-task simple and to
      // avoid hammering the backend.
      // eslint-disable-next-line no-await-in-loop
      await handleStatusChange(t.id, 'completed');
    }
  };

  // Helper to get assignee display name
  const getAssigneeName = (assigneeId) => {
    if (!assigneeId) return '';
    const user = assignableUsers.find(u => u.id === assigneeId);
    if (user) {
      const firstName = user.firstName || '';
      const lastName = user.lastName || '';
      return `${firstName} ${lastName}`.trim() || user.username || 'Unbekannt';
    }
    return 'Unbekannt';
  };

  const handleAssigneeChange = async (taskId, newAssigneeId) => {
    try {
      const taskToUpdate = tasks.find(task => task.id === taskId);
      if (!taskToUpdate) return;

      // Prepare update data (use _rawCallerName to preserve __pvs__ marker)
      const taskData = buildPreservedTaskPayload(taskToUpdate, {
        assignee_id: newAssigneeId || null,
      });

      // Update the task
      const response = await axios.put(`${API_BASE_URL}/tasks/${taskId}`, taskData);
      console.log('Task assignee updated:', response.data);

      const updatedTask = {
        ...mergePreservingStats(taskToUpdate, formatTask(response.data)),
        createdAt: response.data.created_at || taskToUpdate.createdAt || '',
      };

      // Update the tasks state
      setTasks(tasks.map(task =>
        task.id === taskId ? updatedTask : task
      ));

      setErrorMessage('');
    } catch (error) {
      console.error('Error updating task assignee:', error);
      setErrorMessage(`Error updating assignee: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleDelete = async (taskId) => {
    try {
      await axios.delete(`${API_BASE_URL}/tasks/${taskId}`);
      console.log('Task deleted');
      
      // Remove from the tasks state
      setTasks(tasks.filter(task => task.id !== taskId));
      setErrorMessage('');
    } catch (error) {
      console.error('Error deleting task:', error);
      setErrorMessage(`Error deleting task: ${error.response?.data?.detail || error.message}`);
    }
  };

  // `conversationId` / `sessionId` / `voicemailId` open ONE call of a task's
  // call log (a missed call, or one absorbed into this task); the backend only
  // serves ids that appear on this task's own call log. Without them: the
  // task's own call, as before.
  const handleViewConversation = async (taskId, { conversationId = null, sessionId = null, voicemailId = null } = {}) => {
    setConversationLoading(true);
    setShowConversationModal(true);
    setTaskRecordings([]); // Reset recordings
    const sessionParams = sessionId ? { params: { session_id: sessionId } } : undefined;

    try {
      // Auth comes from the axios interceptor (Authorization header); the
      // legacy ?user_id= query param is no longer read by the backend.
      const response = await axios.get(`${API_BASE_URL}/tasks/${taskId}/conversation`,
        conversationId ? { params: { conversation_id: conversationId } } : undefined);
      // Include taskId (and the call's voicemail, if any) for voicemail playback
      setSelectedConversation({ ...response.data, taskId: taskId, voicemailId });
      setErrorMessage('');

      // Also fetch call recordings for this task
      try {
        const recordingsRes = await axios.get(`${API_BASE_URL}/tasks/${taskId}/recordings`, sessionParams);
        setTaskRecordings(recordingsRes.data || []);
      } catch (recErr) {
        // Recordings are optional - don't fail the modal
        console.log('No recordings found for task:', recErr.response?.status);
      }

      // Also fetch full conversation recording (MixMonitor)
      try {
        const fullRecRes = await axios.get(`${API_BASE_URL}/tasks/${taskId}/full-recording`, sessionParams);
        setFullRecording(fullRecRes.data);
      } catch (fullRecErr) {
        // Full recording is optional - don't fail the modal
        console.log('No full recording found:', fullRecErr.response?.status);
        setFullRecording(null);
      }
    } catch (error) {
      console.error('Error fetching conversation:', error);
      if (error.response && error.response.status === 404) {
        // No conversation history found - likely consent was declined
        setSelectedConversation({
          conversation_data: [],
          message: 'Der Anrufer hat der Aufzeichnung des Gesprächs nicht zugestimmt. Die Unterhaltung wurde aus Datenschutzgründen nicht gespeichert.',
          noConsent: true,  // Flag for special styling
          taskId: taskId,   // Include taskId even for error case
          voicemailId,
        });
        // The 404 IS the consent verdict — latch it so the button greys out now
        // instead of staying clickable until the next full list refetch (which
        // recomputes the same false, so latch and server never diverge).
        // Not for one call of the call log: that verdict is about that call only.
        if (!conversationId) setTasks(prev => prev.map(t => (t.id === taskId ? { ...t, hasConsent: false } : t)));
      } else {
        setErrorMessage(`Error loading conversation: ${error.response?.data?.detail || error.message}`);
        setShowConversationModal(false);
      }
    } finally {
      setConversationLoading(false);
    }
  };

  const handleViewCallerData = (task) => {
    // Set caller data from task object (no API call needed)
    setSelectedCallerData({
      callerName: task.callerName,
      phoneNumber: task.phoneNumber,
      callerBirthdate: task.callerBirthdate,
      callerEmail: task.callerEmail,
      hasVerordnung: task.hasVerordnung,
      verordnungType: task.verordnungType,
      verordnungSessions: task.verordnungSessions,
      verordnungIssueDate: task.verordnungIssueDate,
      insuranceType: task.insuranceType,
      appointmentPreference: task.appointmentPreference  // read-only "Terminwunsch"
    });
    setShowCallerDataModal(true);
  };

  // Play voicemail audio for a task
  // `voicemailId`: one AB message of the task's call log (see handleViewConversation).
  const playVoicemailAudio = async (taskId, voicemailId = null) => {
    // Toggle off if already playing
    if (audioPlaying && audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setAudioPlaying(false);
      return;
    }

    try {
      // Backend requires auth (Session 5) — fetch as blob via axios interceptor,
      // then hand a blob URL to the <audio> element which can't send headers.
      const response = await axios.get(
        `${API_BASE_URL}/tasks/${taskId}/voicemail-audio`,
        { responseType: 'blob', ...(voicemailId ? { params: { voicemail_id: voicemailId } } : {}) }
      );
      const audioUrl = URL.createObjectURL(response.data);
      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onended = () => {
        setAudioPlaying(false);
        audioRef.current = null;
        URL.revokeObjectURL(audioUrl);
      };

      audio.onerror = () => {
        setAudioPlaying(false);
        audioRef.current = null;
        URL.revokeObjectURL(audioUrl);
        setErrorMessage('Fehler beim Abspielen der Voicemail');
      };

      setAudioPlaying(true);
      await audio.play();
    } catch (error) {
      console.error('Error playing voicemail:', error);
      setAudioPlaying(false);
      setErrorMessage('Fehler beim Abspielen der Voicemail');
    }
  };

  // Handle call recording playback
  const handlePlayRecording = async (recordingId) => {
    // If already playing this recording, stop it
    if (recordingPlaying === recordingId && recordingAudioRef.current) {
      recordingAudioRef.current.pause();
      recordingAudioRef.current = null;
      setRecordingPlaying(null);
      return;
    }

    // Stop any currently playing recording
    if (recordingAudioRef.current) {
      recordingAudioRef.current.pause();
      recordingAudioRef.current = null;
    }

    try {
      const response = await axios.get(
        `${API_BASE_URL}/tasks/recordings/${recordingId}/audio`,
        { responseType: 'blob' }
      );
      const audioUrl = URL.createObjectURL(response.data);
      const audio = new Audio(audioUrl);
      recordingAudioRef.current = audio;

      audio.onended = () => {
        setRecordingPlaying(null);
        recordingAudioRef.current = null;
        URL.revokeObjectURL(audioUrl);
      };

      audio.onerror = () => {
        setRecordingPlaying(null);
        recordingAudioRef.current = null;
        URL.revokeObjectURL(audioUrl);
        setErrorMessage('Fehler beim Abspielen der Aufnahme');
      };

      setRecordingPlaying(recordingId);
      await audio.play();
    } catch (error) {
      console.error('Error playing recording:', error);
      setRecordingPlaying(null);
      setErrorMessage('Fehler beim Abspielen der Aufnahme');
    }
  };

  // Handle full conversation recording playback (play/pause toggle)
  const handlePlayFullRecording = async () => {
    if (!fullRecording) return;

    // If audio exists, toggle play/pause
    if (fullRecordingAudioRef.current) {
      if (fullRecordingPlaying) {
        fullRecordingAudioRef.current.pause();
        setFullRecordingPlaying(false);
      } else {
        await fullRecordingAudioRef.current.play();
        setFullRecordingPlaying(true);
      }
      return;
    }

    // Stop any currently playing turn recording
    if (recordingAudioRef.current) {
      recordingAudioRef.current.pause();
      recordingAudioRef.current = null;
      setRecordingPlaying(null);
    }

    try {
      // Backend requires auth — fetch as blob via interceptor. NOTE: range
      // requests for seeking are lost when we download the full blob; the
      // existing seek logic still works against the buffered audio element.
      const response = await axios.get(
        `${API_BASE_URL}/tasks/full-recording/${fullRecording.id}/audio`,
        { responseType: 'blob' }
      );
      const audioUrl = URL.createObjectURL(response.data);
      const audio = new Audio(audioUrl);
      audio.playbackRate = playbackSpeed;
      fullRecordingAudioRef.current = audio;

      // Track duration when metadata loads
      audio.onloadedmetadata = () => {
        setFullRecordingDuration(audio.duration);
      };

      // Update current time during playback
      audio.ontimeupdate = () => {
        if (!isDraggingRef.current) {
          setFullRecordingCurrentTime(audio.currentTime);
        }
      };

      audio.onended = () => {
        setFullRecordingPlaying(false);
        setFullRecordingCurrentTime(0);
      };

      audio.onerror = () => {
        setFullRecordingPlaying(false);
        fullRecordingAudioRef.current = null;
        URL.revokeObjectURL(audioUrl);
        setErrorMessage('Fehler beim Abspielen der vollständigen Aufnahme');
      };

      setFullRecordingPlaying(true);
      await audio.play();
    } catch (error) {
      console.error('Error playing full recording:', error);
      setFullRecordingPlaying(false);
      setErrorMessage('Fehler beim Abspielen der vollständigen Aufnahme');
    }
  };

  // Reference to the progress bar element for drag calculations
  const progressBarRef = useRef(null);

  // Seek to a specific position in the full recording (click on bar)
  const handleFullRecordingSeek = (e) => {
    if (!fullRecordingAudioRef.current) return;

    // Use actual audio duration if available, otherwise fall back to API duration
    const duration = fullRecordingAudioRef.current.duration || (fullRecording?.duration_ms / 1000) || 0;
    if (!duration) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(clickX / rect.width, 1));
    const newTime = percentage * duration;

    fullRecordingAudioRef.current.currentTime = newTime;
    setFullRecordingCurrentTime(newTime);
  };

  // Handle drag start on progress bar handle
  const handleProgressDragStart = (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent click event on parent
    isDraggingRef.current = true;

    // Capture the progress bar rect at drag start
    const progressBar = progressBarRef.current;
    if (!progressBar) return;

    const rect = progressBar.getBoundingClientRect();
    const duration = fullRecordingAudioRef.current?.duration || (fullRecording?.duration_ms / 1000) || 0;
    let currentSeekTime = fullRecordingCurrentTime;

    const handleDrag = (moveEvent) => {
      if (!duration) return;

      const clickX = Math.max(0, Math.min(moveEvent.clientX - rect.left, rect.width));
      const percentage = clickX / rect.width;
      currentSeekTime = percentage * duration;

      setFullRecordingCurrentTime(currentSeekTime);
    };

    const handleDragEnd = () => {
      isDraggingRef.current = false;
      if (fullRecordingAudioRef.current) {
        fullRecordingAudioRef.current.currentTime = currentSeekTime;
      }
      document.removeEventListener('mousemove', handleDrag);
      document.removeEventListener('mouseup', handleDragEnd);
    };

    document.addEventListener('mousemove', handleDrag);
    document.addEventListener('mouseup', handleDragEnd);
  };

  // Close status / Grund multi-select dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (statusFilterRef.current && !statusFilterRef.current.contains(e.target)) {
        setStatusFilterOpen(false);
      }
      if (callReasonFilterRef.current && !callReasonFilterRef.current.contains(e.target)) {
        setCallReasonFilterOpen(false);
      }
      if (assigneeFilterRef.current && !assigneeFilterRef.current.contains(e.target)) {
        setAssigneeFilterOpen(false);
      }
      if (insuranceFilterRef.current && !insuranceFilterRef.current.contains(e.target)) {
        setInsuranceFilterOpen(false);
      }
      if (dayFilterRef.current && !dayFilterRef.current.contains(e.target)) {
        setDayFilterOpen(false);
        // Collapse the drawer too, so reopening the popover doesn't resume an
        // abandoned draft — the drawer re-seeds on every open.
        setDayRangeOpen(false);
      }
      // Close caller popover when click is outside trigger and popover itself
      if (!e.target.closest('.caller-popover, .caller-popover-trigger')) {
        setOpenCallerPopoverId(null);
      }
      if (!e.target.closest('.automation-popover, .automation-popover-trigger')) {
        setOpenAutomationId(null);
      }
      // Same for the per-action retry menu (also portal-rendered).
      if (!e.target.closest('.action-retry-menu, .action-retry-menu-trigger')) {
        closeRetryMenu();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [closeRetryMenu]);

  // Keep portal-rendered caller popover anchored to its trigger on scroll/resize.
  useEffect(() => {
    if (!openCallerPopoverId) return;
    const updateRect = () => {
      const triggerEl = callerPopoverTriggerRefs.current[openCallerPopoverId];
      if (triggerEl) {
        setCallerPopoverRect(triggerEl.getBoundingClientRect());
      }
    };
    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [openCallerPopoverId]);

  // Same anchoring for the automation popover.
  useEffect(() => {
    if (!openAutomationId) return;
    const updateRect = () => {
      const triggerEl = automationTriggerRefs.current[openAutomationId];
      if (triggerEl) setAutomationRect(triggerEl.getBoundingClientRect());
    };
    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [openAutomationId]);

  const toggleAutomationPopover = (taskId) => {
    setOpenAutomationId(prev => (prev === taskId ? null : taskId));
    const known = automationDetails[taskId];
    if (known && known !== 'error') return;
    setAutomationDetails(prev => ({ ...prev, [taskId]: 'loading' }));
    axios.get(`${API_BASE_URL}/tasks/${taskId}/automation`)
      .then(res => setAutomationDetails(prev => ({ ...prev, [taskId]: res.data })))
      .catch(() => setAutomationDetails(prev => ({ ...prev, [taskId]: 'error' })));
  };

  // Keep the portal-rendered per-action retry menu anchored to its trigger too.
  useEffect(() => {
    if (!retryMenuTaskId) return;
    const updateRect = () => {
      const triggerEl = retryMenuTriggerRef.current;
      if (triggerEl) {
        setRetryMenuRect(triggerEl.getBoundingClientRect());
      }
    };
    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [retryMenuTaskId]);

  // Cmd+K is now handled globally by Layout — it focuses the search input via DOM query.

  // Esc to close the slide-over (when an input isn't capturing it)
  useEffect(() => {
    if (!showForm) return;
    const handleEsc = (e) => {
      if (e.key !== 'Escape') return;
      // The sent-message popup is layered ABOVE the slide-over (z-index 1500 vs
      // 1000) and owns Esc while open — otherwise this listener would close the
      // panel underneath it. The global handler below does the actual closing.
      if (sentMessageTask) return;
      // Only close if focus isn't inside an input where Esc has its own meaning
      const ae = document.activeElement;
      const isFormField = ae && ['INPUT', 'TEXTAREA', 'SELECT'].includes(ae.tagName);
      if (!isFormField) {
        setShowForm(false);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [showForm, sentMessageTask]);

  // Global keyboard shortcuts: ?, e, s, n, Esc — only when no input is focused.
  useEffect(() => {
    const handleShortcut = (e) => {
      // The message popup is modal and owns the keyboard, its fields included:
      // only Esc acts (its close); nothing reaches the rows beneath it.
      if (sentMessageTask) {
        if (e.key === 'Escape' && !e.isComposing) { e.preventDefault(); escapeSentMessage(); }
        return;
      }
      const ae = document.activeElement;
      const isFormField = ae && ['INPUT', 'TEXTAREA', 'SELECT'].includes(ae.tagName);
      if (isFormField) return;
      // Modifier keys handled elsewhere (Cmd+K)
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      // ? opens cheat sheet
      if (e.key === '?') {
        e.preventDefault();
        setShowShortcutHelp(prev => !prev);
        return;
      }
      // Esc closes any open overlays (message popup, cheat sheet, slide-over) —
      // topmost first, so the popup never closes the slide-over beneath it.
      if (e.key === 'Escape') {
        if (showShortcutHelp) { setShowShortcutHelp(false); e.preventDefault(); return; }
        if (showForm) { setShowForm(false); e.preventDefault(); return; }
        return;
      }
      // n = new task. Inert while several practices are pooled, for the same
      // reason the button is hidden: there is no single practice to create in.
      if (e.key === 'n' || e.key === 'N') {
        if (allPractices) return;
        e.preventDefault();
        handleAddNew();
        return;
      }
      // The rest require an active row
      if (!activeRowId) return;
      const activeTask = tasks.find(t => t.id === activeRowId);
      if (!activeTask) return;
      // e = edit (open slide-over)
      if (e.key === 'e' || e.key === 'E') {
        e.preventDefault();
        handleEdit(activeTask);
        return;
      }
      // s = open status dropdown for active row (anchored at the badge)
      if (e.key === 's' || e.key === 'S') {
        e.preventDefault();
        const badge = document.querySelector(`tr[data-task-id="${activeRowId}"] .status-badge`);
        if (badge) {
          const rect = badge.getBoundingClientRect();
          const dropdownHeight = 144;
          const spaceBelow = window.innerHeight - rect.bottom;
          const openUpward = spaceBelow < dropdownHeight + 8;
          setStatusDropdownPos({
            top: openUpward ? rect.top - dropdownHeight : rect.bottom + 4,
            left: rect.left,
          });
        }
        setEditingStatusId(activeRowId);
        return;
      }
      // c = call (tel:) for active row
      if (e.key === 'c' || e.key === 'C') {
        const phone = activeTask.phoneNumber;
        if (phone && phone !== '-') {
          e.preventDefault();
          window.location.href = phoneToTelHref(phone);
        }
        return;
      }
      // m = mail (mailto:) for active row
      if (e.key === 'm' || e.key === 'M') {
        e.preventDefault();
        window.location.href = taskMailtoHref(activeTask);
        return;
      }
      // 1-4 = set status of hovered row.
      // Mapping derives from STATUS_CONFIG order (first 4 userSelectable entries):
      //   1 → Abgeschlossen, 2 → In Bearbeitung, 3 → Nicht erreicht, 4 → Ausstehend
      if (e.key >= '1' && e.key <= '4') {
        const idx = parseInt(e.key, 10) - 1;
        const target = STATUS_CONFIG.filter(s => s.userSelectable).filter(visibleTo(isUserSuperAdmin))[idx];
        if (target) {
          e.preventDefault();
          handleStatusChange(activeTask.id, target.value);
        }
        return;
      }
      // 0 = "fertig damit". For clustered rows (≥2 non-terminal tasks on the
      // same phone), bulk-completes the whole cluster. For everything else, it
      // just marks the single task as completed — so the user never has to
      // think "is this a cluster, do I press 0 or 1?".
      if (e.key === '0') {
        e.preventDefault();
        const key = clusterKeyOf(activeTask);
        if (key) {
          const openCount = tasks.filter(t =>
            clusterKeyOf(t) === key && isBulkCompletable(t.status)
          ).length;
          if (openCount >= 2) {
            handleCompleteAllForPhone(activeTask.id);
            return;
          }
        }
        handleStatusChange(activeTask.id, 'completed');
        return;
      }
    };
    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, [activeRowId, tasks, showShortcutHelp, showForm, sentMessageTask, allPractices,
      sentMessageList, sentMessageDrafts, sentMessageConfirmClose]);

  // Detects whether a click on a row should open the slide-over.
  // Returns false when the user clicked on something interactive
  // (link, button, input, dropdown, copy icon, name hover popover, etc.).
  const shouldOpenSlideover = (e) => {
    const interactive = e.target.closest('a, button, input, select, textarea, label, [role="button"], [data-no-row-click]');
    return !interactive;
  };

  // Toggle a status in the multi-select filter
  const toggleStatusFilter = (status) => {
    setStatusFilters(prev =>
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  // Toggle a call-reason in the multi-select Grund filter
  const toggleCallReasonFilter = (reasonKey) => {
    setCallReasonFilters(prev =>
      prev.includes(reasonKey)
        ? prev.filter(r => r !== reasonKey)
        : [...prev, reasonKey]
    );
  };

  // Toggle an assignee in the multi-select filter
  const toggleAssigneeFilter = (id) => {
    setAssigneeFilters(prev =>
      prev.includes(id)
        ? prev.filter(a => a !== id)
        : [...prev, id]
    );
  };

  // Toggle an insurance type or Blanko in the multi-select Patienten hervorheben filter
  const toggleInsuranceFilter = (type) => {
    setInsuranceFilters(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  // Summary text for the closed multi-select dropdown
  const statusFilterLabel = statusFilters.length === 0
    ? (hideMissed ? 'Alle außer verpasste' : 'Alle')
    : statusFilters.length === 1
      ? STATUS_CONFIG.find(o => o.value === statusFilters[0])?.label || statusFilters[0]
      : `${statusFilters.length} ausgewählt`;

  // Summary text for the closed Grund multi-select dropdown
  const callReasonFilterLabel = callReasonFilters.length === 0
    ? 'Alle'
    : callReasonFilters.length === 1
      ? (CALL_REASON_CONFIG[callReasonFilters[0]]?.label || callReasonFilters[0])
      : `${callReasonFilters.length} ausgewählt`;

  // Helper: resolve user id to display name
  const resolveAssigneeName = (id) => {
    if (id === null) return 'Nicht zugewiesen';
    const u = allMembers.find(x => x.id === id);
    if (!u) return id;
    return `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.username;
  };

  // Summary text for the closed Zuständig multi-select dropdown
  const assigneeFilterLabel = assigneeFilters.length === 0
    ? 'Alle'
    : assigneeFilters.length === 1
      ? resolveAssigneeName(assigneeFilters[0])
      : `${assigneeFilters.length} ausgewählt`;

  // Summary text for the closed Patienten hervorheben multi-select dropdown
  const insuranceFilterLabel = insuranceFilters.length === 0
    ? 'Alle'
    : insuranceFilters.length === 1
      ? insuranceFilters[0]
      : `${insuranceFilters.length} ausgewählt`;

  // Format seconds to MM:SS
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Count active filters (not including search). The status filter only
  // counts as "active" when it's a custom selection — both Offen and Alle
  // presets are valid resting states and don't need a Reset button.
  const openSetForCount = openSetFor(isUserSuperAdmin);
  const statusIsOffenPreset = statusFilters.length === openSetForCount.length
    && openSetForCount.every(s => statusFilters.includes(s));
  const statusIsAllePreset = statusFilters.length === 0;
  const statusIsCustom = !statusIsOffenPreset && !statusIsAllePreset;
  const activeFilterCount = [
    statusIsCustom,
    callReasonFilters.length > 0,
    escapeFilters.length > 0,
    assigneeFilters.length > 0,
    insuranceFilters.length > 0
  ].filter(Boolean).length;

  // Reset all filters back to the "Alle" default. Status, Grund and Zuständig
  // clear entirely.
  const resetFilters = () => {
    // The persist effects (keyed on these state vars) write the cleared values
    // back to the scoped keys, so no explicit removeItem is needed.
    setStatusFilters([]);
    setCallReasonFilters([]);
    setEscapeFilters([]);
    setAssigneeFilters([]);
    setInsuranceFilters([]);
  };

  // Filter tasks by status, Grund, Übergabegrund, assignee, insurance type / Blanko, and search term.
  const filteredTasks = tasks.filter(task => {
    // Status filter (empty array = show all). A status this user has no checkbox
    // for can never be in their selection, so it would be filtered out forever
    // with no way back — such rows always pass instead. Scoped to superAdminOnly
    // statuses, so an unrecognised status still filters exactly as before.
    const hiddenFromThisUser = !!statusConfigFor(task.status)?.superAdminOnly && !isUserSuperAdmin;
    // "Verpasste Anrufe ausblenden" hides every missed-call row, also one staff
    // already set to another status, under "Offen" too; only a hand-picked
    // "Verpasster Anruf" selection wins.
    const missedPicked = statusFilters.includes('verpasster_anruf') && !statusIsOffenPreset;
    const matchesStatus = (statusFilters.length === 0 || statusFilters.includes(task.status) || hiddenFromThisUser)
      && !(hideMissed && task.missedRow && !missedPicked);

    // Grund filter (N-to-N): each task's callReasons is an array; match if any
    // selected reason is in the task's reasons. Empty selection = inactive (show all).
    // Tasks with null/empty callReasons fail the filter only when selection is non-empty.
    const taskReasons = Array.isArray(task.callReasons) ? task.callReasons : [];
    const matchesCallReason = callReasonFilters.length === 0
      || taskReasons.some(r => callReasonFilters.includes(r));

    // Übergabegrund filter (1-to-N): a task carries at most one escape, matched on
    // the same key the Dashboard rows use. Tasks that never escaped (escapeCode
    // null -> key null) fail only when the selection is non-empty, same semantics
    // as Grund.
    const matchesEscape = escapeFilters.length === 0
      || escapeFilters.includes(escapeRowKey(task.escapeCode, task.escapeReason));

    // Assignee filter. Normalize to null so a null selection ("Nicht zugewiesen")
    // matches unassigned tasks regardless of whether assigneeId is null/undefined.
    const matchesAssignee = assigneeFilters.length === 0 || assigneeFilters.includes(task.assigneeId ?? null);

    // Patienten hervorheben: exact stored code, or 'Blanko' for a Blankorezept preset
    // task; any selected value matches. Tasks with neither fail only when the selection
    // is non-empty, same semantics as Grund.
    const matchesInsurance = insuranceFilters.length === 0
      || insuranceFilters.includes(task.insuranceType)
      || (task.isBlanko && insuranceFilters.includes('Blanko'));

    // Search filter — splits the query into AND-terms by whitespace; phone numbers
    // are matched against a normalized national-digits form so different formats
    // ("0049-15773 437511" vs "015773437511" vs "+49 15773 437511") match.
    const matchTermInValue = (term, value, isPhone = false) => {
      if (value == null) return false;
      const text = String(value).toLowerCase();
      if (text.includes(term)) return true;
      if (isPhone) {
        const normValue = normalizePhone(value);
        const normTerm = normalizePhone(term);
        if (normValue && normTerm.length > 0 && normValue.includes(normTerm)) return true;
      }
      return false;
    };
    const taskMatchesTerm = (term) => {
      const callerNames = Array.isArray(task.callerName) ? task.callerName : [task.callerName];
      if (callerNames.some(n => matchTermInValue(term, n))) return true;
      if (matchTermInValue(term, task.phoneNumber, true)) return true;
      if (matchTermInValue(term, task.callerIdNumber, true)) return true;
      if (matchTermInValue(term, task.description)) return true;
      if ((task.todos || []).some(td => matchTermInValue(term, td.text))) return true;
      if ((task.callLog || []).some(e => matchTermInValue(term, e.line)
        || (e.annotation?.notes || []).some(n => matchTermInValue(term, n?.text)))) return true;
      if (task.callerBirthdate && matchTermInValue(term, formatBirthdate(task.callerBirthdate))) return true;
      if (matchTermInValue(term, task.callerEmail)) return true;
      // A session id only as a whole pasted id: its random hex would match short
      // searches ("12", "de") on unrelated tasks.
      if (task.sessionId && UUID_RE.test(term) && task.sessionId.toLowerCase() === term) return true;
      if (matchTermInValue(term, task.verordnungType)) return true;
      if (matchTermInValue(term, task.verordnungSessions)) return true;
      if (matchTermInValue(term, task.verordnungIssueDate)) return true;
      if (matchTermInValue(term, getGermanStatus(task.status))) return true;
      if (matchTermInValue(term, resolveAssigneeName(task.assigneeId))) return true;
      if (Array.isArray(task.callReasons)
        && task.callReasons.some(r => matchTermInValue(term, CALL_REASON_CONFIG[r]?.label || r))) return true;
      return false;
    };
    const trimmedSearch = searchTerm.trim().toLowerCase();
    const matchesSearch = trimmedSearch === ''
      || trimmedSearch.split(/\s+/).every(t => taskMatchesTerm(t));

    // Deep link: the linked call's rows only, and the restored filters do not
    // apply. Checked here as well as on the server, so a backend that ignores
    // session_id still shows the right rows and a live push of another call
    // stays out.
    if (linkActive) {
      // A call absorbed into a later task keeps its session and origin row on
      // that task's call-log line, so links to it still find the task.
      const callLog = task.callLog || [];
      const matchesLink = deepLink.session
        ? task.sessionId === deepLink.session || callLog.some(e => e.session_id === deepLink.session)
        : task.id === deepLink.task || callLog.some(e => e.task_id === deepLink.task);
      return matchesLink && matchesSearch;
    }

    return matchesStatus && matchesCallReason && matchesEscape && matchesAssignee && matchesInsurance && matchesSearch;
  });

  // Search forces flat rendering regardless of toggle (without mutating the toggle's stored value)
  const effectiveViewMode = searchTerm !== '' ? 'flat' : viewMode;

  // Lowercase, whitespace-split terms — used for AND-search and highlighting.
  const searchTerms = searchTerm.trim() === ''
    ? []
    : searchTerm.trim().toLowerCase().split(/\s+/);

  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

  // Pattern A: single Esc clears the search term and blurs the input.
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Escape') {
      if (searchTerm) {
        setSearchTerm('');
      } else {
        searchInputRef.current?.blur();
      }
    }
  };

  // Build clusters: group filteredTasks by cluster key, pick visible task per cluster
  const clusterRows = useMemo(() => {
    // Newest call first by its start (callStartOf), the time the date column shows.
    const newestCallFirst = (a, b) => b.sortKey - a.sortKey;
    if (effectiveViewMode === 'flat') {
      return filteredTasks
        .map(task => ({ kind: 'standalone', task, sortKey: callStartMs(task) }))
        .sort(newestCallFirst);
    }

    const groups = new Map(); // clusterKey -> task[]
    const standalone = [];

    for (const task of filteredTasks) {
      const key = clusterKeyOf(task);
      if (!key) {
        standalone.push(task);
        continue;
      }
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(task);
    }

    const rows = [];

    for (const [clusterKey, members] of groups) {
      if (members.length === 1) {
        rows.push({ kind: 'standalone', task: members[0], sortKey: callStartMs(members[0]) });
        continue;
      }

      // filteredTasks already comes from server in created_at DESC order, so members preserve that order
      const openTasks = members.filter(t => isOpenStatus(t.status));
      const terminalTasks = members.filter(t => !isOpenStatus(t.status));
      const visibleTask = openTasks.length > 0 ? openTasks[0] : terminalTasks[0];

      const hiddenOpenCount = openTasks.length > 0 ? openTasks.length - 1 : 0;
      const hiddenTotalCount = members.length - 1;

      const sortKey = Math.max(...members.map(callStartMs));

      const altNamesSet = new Set();
      for (const t of members) {
        if (Array.isArray(t.callerName)) {
          for (let i = 1; i < t.callerName.length; i++) {
            if (t.callerName[i]) altNamesSet.add(t.callerName[i]);
          }
        }
      }
      // Also surface alt names from primary slot of OTHER tasks in the cluster (different spellings between calls)
      const primaryName = Array.isArray(visibleTask.callerName) ? visibleTask.callerName[0] : visibleTask.callerName;
      for (const t of members) {
        const otherPrimary = Array.isArray(t.callerName) ? t.callerName[0] : t.callerName;
        if (otherPrimary && otherPrimary !== primaryName) altNamesSet.add(otherPrimary);
      }

      rows.push({
        kind: 'cluster',
        clusterKey,
        members,
        visibleTask,
        hiddenOpenCount,
        hiddenTotalCount,
        altNames: Array.from(altNamesSet),
        sortKey,
      });
    }

    for (const task of standalone) {
      rows.push({ kind: 'standalone', task, sortKey: callStartMs(task) });
    }

    rows.sort(newestCallFirst);
    return rows;
  }, [filteredTasks, effectiveViewMode, clusterKeyOf]);

  const toggleCluster = (clusterKey) => {
    setExpandedClusters(prev => {
      const next = new Set(prev);
      if (next.has(clusterKey)) {
        // Collapse: keep rendering children with exit animation, then unmount
        next.delete(clusterKey);
        setExitingClusters(prevExiting => {
          const nextExiting = new Set(prevExiting);
          nextExiting.add(clusterKey);
          return nextExiting;
        });
        const existingTimer = exitTimersRef.current.get(clusterKey);
        if (existingTimer) clearTimeout(existingTimer);
        const timer = setTimeout(() => {
          setExitingClusters(prevExiting => {
            const nextExiting = new Set(prevExiting);
            nextExiting.delete(clusterKey);
            return nextExiting;
          });
          exitTimersRef.current.delete(clusterKey);
        }, CLUSTER_EXIT_TOTAL_MS);
        exitTimersRef.current.set(clusterKey, timer);
      } else {
        // Expand: cancel any pending exit cleanup so enter animation plays fresh
        const existingTimer = exitTimersRef.current.get(clusterKey);
        if (existingTimer) {
          clearTimeout(existingTimer);
          exitTimersRef.current.delete(clusterKey);
        }
        setExitingClusters(prevExiting => {
          if (!prevExiting.has(clusterKey)) return prevExiting;
          const nextExiting = new Set(prevExiting);
          nextExiting.delete(clusterKey);
          return nextExiting;
        });
        next.add(clusterKey);
      }
      return next;
    });
  };

  // Cleanup any pending exit timers and status-pulse timers on unmount
  useEffect(() => {
    const exitTimers = exitTimersRef.current;
    const pulseTimers = statusPulseTimersRef.current;
    return () => {
      exitTimers.forEach(t => clearTimeout(t));
      exitTimers.clear();
      pulseTimers.forEach(t => clearTimeout(t));
      pulseTimers.clear();
    };
  }, []);

  // Render-time guard: wait for PracticeContext to resolve before rendering.
  if (practiceLoading) {
    return <Layout><div className="loading">Laden...</div></Layout>;
  }

  return (
    <Layout>
      {/* CSS for animations and tooltips */}
      <style>{`
        @keyframes taskFadeIn {
          0% {
            opacity: 0;
            background-color: #d4edda;
            transform: translateY(-8px);
          }
          30% {
            opacity: 1;
            transform: translateY(0);
          }
          100% {
            background-color: transparent;
          }
        }
        .task-row-new {
          animation: taskFadeIn 0.8s ease-out;
        }
        .task-row-new td {
          background-color: #d4edda !important;
        }
        /* The row a support deep link points at (?task=), while the link is active. */
        .task-row-linked td {
          background-color: color-mix(in srgb, var(--color-secondary) 8%, #fff);
        }
        .task-row-linked td:first-child {
          box-shadow: inset 3px 0 0 var(--color-secondary);
        }
        .name-alt-indicator, .name-pvs-indicator {
          position: relative;
          top: -6px;
          margin-left: 2px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          font-size: 9px;
          font-weight: bold;
          cursor: default;
        }
        .name-alt-indicator {
          background-color: #fef3c7;
          border: 1px solid #f59e0b;
          color: #d97706;
        }
        .name-pvs-indicator {
          background-color: #e3f2fd;
          border: 1px solid var(--color-main);
          color: var(--color-main);
        }
        .name-alt-indicator::after, .name-pvs-indicator::after {
          content: attr(data-tooltip);
          position: absolute;
          top: 50%;
          left: 100%;
          transform: translateY(-50%);
          margin-left: 8px;
          padding: 6px 10px;
          background-color: #333;
          color: white;
          font-size: 13px;
          font-weight: normal;
          white-space: nowrap;
          border-radius: 4px;
          opacity: 0;
          visibility: hidden;
          transition: opacity 0.1s ease-in-out;
          z-index: 1000;
        }
        .name-alt-indicator:hover::after, .name-pvs-indicator:hover::after {
          opacity: 1;
          visibility: visible;
        }
        .cluster-caret {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 14px;
          height: 14px;
          margin-right: 6px;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          color: var(--color-main);
          vertical-align: middle;
        }
        .cluster-caret svg {
          width: 12px;
          height: 12px;
          transition: transform 0.15s ease;
        }
        .cluster-chip {
          display: inline-flex;
          align-items: center;
          margin-left: 8px;
          padding: 2px 8px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 500;
          line-height: 1.4;
          white-space: nowrap;
          vertical-align: middle;
        }
        .cluster-chip-open {
          background-color: rgba(250, 136, 98, 0.15);
          color: var(--color-tertiary);
        }
        .cluster-chip-total {
          background-color: #f3f4f6;
          color: #555;
        }
        .cluster-child-row > td {
          background-color: #fafbfc;
        }
        .cluster-child-row > td:first-child {
          border-left: 3px solid var(--color-main);
        }
        .cluster-child-row > td:last-child {
          border-right: 3px solid var(--color-main);
        }
        .cluster-header-row-expanded > td:first-child,
        .cluster-header-row-collapsing > td:first-child {
          border-left: 3px solid var(--color-main);
        }
        .cluster-header-row-expanded > td:last-child,
        .cluster-header-row-collapsing > td:last-child {
          border-right: 3px solid var(--color-main);
        }
        @keyframes clusterBarFade {
          0% {
            border-left-color: var(--color-main);
            border-right-color: var(--color-main);
          }
          100% {
            border-left-color: transparent;
            border-right-color: transparent;
          }
        }
        .cluster-header-row-collapsing > td:first-child,
        .cluster-header-row-collapsing > td:last-child {
          /* Duration tracks CLUSTER_EXIT_TOTAL_MS (JS) so the bar fade stays
             in sync with the staggered child-row exit cascade. */
          animation: clusterBarFade ${CLUSTER_EXIT_TOTAL_MS}ms ease both;
        }
        .cluster-child-row-exiting > td:first-child,
        .cluster-child-row-exiting > td:last-child {
          animation: clusterBarFade 0.15s ease both;
        }
        @keyframes clusterChildSlideDown {
          0% {
            opacity: 0;
            transform: translateY(-8px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes clusterChildSlideUp {
          0% {
            opacity: 1;
            transform: translateY(0);
          }
          100% {
            opacity: 0;
            transform: translateY(-8px);
          }
        }
        .cluster-child-row {
          /* fill-mode: backwards keeps the 0% state during animation-delay
             (so staggered children stay hidden until their turn), but does
             NOT retain the 100% state after the animation ends. This is
             critical: a non-none transform on the <tr> would create a
             containing block for our position:fixed status dropdown +
             outside-click overlay, trapping them inside the row's rect and
             making the dropdown effectively un-openable on child rows. */
          animation: clusterChildSlideDown 0.15s ease backwards;
        }
        .cluster-child-row-exiting {
          /* exit uses fill-mode forwards + pointer-events none so the retained
             transform's containing-block trap is unreachable. forwards holds
             the 100% state (opacity 0, transform: translateY(-8px)) until JS
             unmounts the row at 390ms, preventing the reappearance flash that
             backwards caused (each row reverted to defaults after its 150ms
             animation, becoming visible again until unmount). The retained
             transform would normally trap the position:fixed status dropdown
             inside the row's containing block, but pointer-events:none makes
             the exiting row unclickable so no dropdown can be opened on it
             during the 390ms exit window. */
          animation: clusterChildSlideUp 0.15s ease forwards;
          pointer-events: none;
        }
        /* Note: do NOT add will-change here. will-change: opacity creates a
           stacking context on the <td>, which traps the position:fixed status
           dropdown's z-index inside the row — later rows then paint over the
           dropdown's lower options, making them appear clipped. */
        .caller-cell {
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 0;
          max-width: 100%;
          position: relative;
          word-break: break-word;
          overflow-wrap: anywhere;
        }
        .caller-cell > div {
          min-width: 0;
          max-width: 100%;
          word-break: break-word;
          overflow-wrap: anywhere;
        }
        .caller-popover {
          /* Rendered via React portal to document.body with position:fixed so
             it escapes the .table-wrapper's implicit overflow-y:hidden clip
             (caused by overflow-x:auto). Coordinates are set inline based on
             the trigger button's getBoundingClientRect(). */
          position: fixed;
          min-width: 260px;
          max-width: 460px;
          width: max-content;
          background: #fff;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
          padding: 12px 14px;
          z-index: 2000;
          font-size: 0.85rem;
          color: var(--color-main);
          animation: callerPopoverIn 0.15s ease-out;
        }
        @keyframes callerPopoverIn {
          from { opacity: 0; transform: translateY(2px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .caller-popover-row {
          display: flex;
          gap: 16px;
          align-items: baseline;
          padding: 3px 0;
          white-space: nowrap;
        }
        .caller-popover-label {
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: #888;
          flex-shrink: 0;
          width: 110px;
        }
        .caller-popover-value {
          color: var(--color-main);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .caller-popover-trigger {
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          opacity: 0;
          transition: opacity 0.15s ease, background 0.15s ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          vertical-align: middle;
        }
        .caller-cell:hover .caller-popover-trigger,
        .caller-popover-trigger:focus-visible {
          opacity: 0.6;
        }
        .caller-popover-trigger:hover {
          opacity: 1 !important;
          background: rgba(0, 0, 0, 0.05);
        }
        .caller-popover-empty {
          color: #888;
          font-style: italic;
          font-size: 0.8rem;
        }
        .automation-popover {
          max-height: 360px;
          overflow-y: auto;
        }
        .automation-popover-trigger {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          background: transparent;
          border: none;
          padding: 2px 0;
          cursor: pointer;
          font: inherit;
          color: var(--color-main);
          white-space: nowrap;
        }
        .automation-score + .automation-score {
          margin-top: 10px;
          padding-top: 8px;
          border-top: 1px solid #eee;
        }
        .automation-intent {
          margin-top: 6px;
        }
        .automation-intent-head,
        .automation-step {
          display: flex;
          justify-content: space-between;
          gap: 16px;
          white-space: nowrap;
        }
        .automation-intent-head {
          font-weight: 600;
          padding: 2px 0;
        }
        .automation-step {
          padding: 1px 0 1px 10px;
        }
        .automation-step--open .automation-step-label {
          color: var(--color-tertiary);
        }
        .automation-step--off {
          color: #999;
        }
        .automation-step-value {
          color: #555;
          font-variant-numeric: tabular-nums;
        }
        .caller-name-line {
          /* Plain inline flow so the copy icon and indicator follow the actual
             end of the name text even when the name wraps. inline-flex would
             anchor children to the right edge of the span, not to the last word. */
        }
        .caller-name-line .caller-copy-btn,
        .caller-name-line .caller-copied-feedback {
          vertical-align: middle;
        }
        .tasks-table tbody tr {
          cursor: pointer;
        }
        /* Read-only and edit views must share IDENTICAL box metrics so the
           cell doesn't shift when toggling between them. Same padding, margin,
           border-width, font-size, line-height, font-family, box-sizing. */
        .description-readonly,
        .description-inline-edit {
          padding: 6px 8px;
          margin: -6px -8px;
          border: 1px solid transparent;
          border-radius: 4px;
          font-family: inherit;
          font-size: inherit;
          line-height: 1.5;
          box-sizing: border-box;
          width: calc(100% + 16px);
          display: block;
        }
        .description-readonly {
          cursor: text;
          transition: border-color 0.15s ease, background 0.15s ease;
          min-height: 1.5em;
          white-space: pre-wrap;
          word-break: break-word;
        }
        .description-readonly:hover {
          border-color: #d0d7e2;
          background: #fafbfc;
        }
        .description-inline-edit {
          border-color: var(--color-secondary);
          background: #fff;
          resize: none;
          box-shadow: 0 0 0 3px rgba(47, 107, 215, 0.12);
          outline: none;
          overflow: hidden;
        }
        /* Prose = the primary human summary; keep it prominent + line breaks. */
        .description-prose {
          white-space: pre-wrap;
          word-break: break-word;
        }
        /* Action notes = a quiet, secondary "audit trail" beneath the prose:
           smaller, muted, scannable. Calm-success tones stay de-emphasized;
           attention (⚠️) notes get a subtle warm tint so staff can spot them. */
        /* Compact ledger. The REAL emoji (✅/⚠️/❓) carries the status + the
           scan-ability, so rows stay flat & muted — no tint/rail needed. Warn
           keeps a faint warm caption tint so it's still easy to pick out. */
        .action-notes {
          --note-warn: color-mix(in srgb, var(--color-tertiary) 78%, #6d2f18);
          margin-top: 6px;
          padding-top: 5px;
          border-top: 1px solid #eef1f5;
          font-size: 0.82em;
          line-height: 1.3;
          white-space: normal;
        }
        .action-notes-caption {
          font-size: 0.72em;
          letter-spacing: 0.07em;
          text-transform: uppercase;
          font-weight: 600;
          color: #b3bbc7;
          margin-bottom: 2px;
          user-select: none;
        }
        .action-note {
          display: flex;
          gap: 6px;
          align-items: baseline;
          padding: 1px 0;
        }
        .action-note + .action-note {
          margin-top: 1px;
        }
        .action-note-glyph {
          flex: 0 0 auto;
          width: 1.4em;
          text-align: center;
          font-size: 1.1em;
          line-height: 1.2;
        }
        .action-note-body {
          min-width: 0;
          word-break: break-word;
        }
        .action-note-label {
          font-weight: 600;
          margin-right: 6px;
          color: #6b7280;
        }
        .action-note-detail {
          color: #828a95;
        }
        .action-note.is-ok .action-note-label { color: #737b86; }
        .action-note.is-warn .action-note-label { color: var(--note-warn); }
        .action-note.is-pending .action-note-label { color: #8a929e; }
        .action-note.is-processing .action-note-label { color: #8a929e; }
        .action-note-spinner {
          display: inline-block;
          width: 0.85em;
          height: 0.85em;
          border: 2px solid #d5dae1;
          border-top-color: var(--color-secondary);
          border-radius: 50%;
          vertical-align: -0.1em;
          animation: spin 0.9s linear infinite;
        }
        .action-note.is-neutral .action-note-label { color: #6b7280; }
        /* "Ansehen" — opens the message stored behind this note. Neutral at
           rest (grey text, hairline border) ON PURPOSE: the coral chips below
           mean "do something" (admin retry), this means "read something", and
           the two must not compete for the same attention. It lifts to the
           interactive blue on approach — hovering anywhere on the note row, or
           the pill itself. The row is only the approach zone and is never
           itself clickable: a tinted clickable row was rejected in review. */
        /* Laid out INLINE, not as an inline-flex box, on purpose. The pill is a
           baseline-aligned flex item of .action-note, so its own baseline decides
           where it lands against the note's text. An inline-flex pill hands the
           parent a baseline synthesized from the bottom edge of its FIRST flex
           item — the icon wrapper, which holds only a replaced element and so has
           no real baseline — and that sat ~2px below the label's own baseline,
           lifting the whole pill above the surrounding receipt lines. As an
           inline-block the pill contributes the label's real text baseline. */
        /* Bare glyph at rest, round tint on hover — a tertiary control in a dense
           receipt, so it stays quiet until approached. The button carries no box
           of its own; display/font-size/line-height are load-bearing and must NOT
           become flex — this line box's strut is what hands the baseline-aligned
           .action-note a real text baseline to sit the glyph on (an inline-flex
           button made the parent align the icon's BOTTOM EDGE to the text
           baseline, which is what tilted it). */
        .action-note-view {
          flex: 0 0 auto;
          display: inline-block;
          padding: 0;
          /* The hover disc is wider than the glyph, so its dead side space reads
             as extra gap on top of .action-note's 6px. Pull back by about that
             much: the glyph sits nearer the text without touching it, and the
             disc still has room. */
          margin-left: -0.25em;
          font-family: inherit;
          font-size: 0.92em;
          line-height: 1.3;
          color: var(--color-main);
          background: none;
          border: 0;
          cursor: pointer;
          transition: color 0.15s ease;
        }
        /* The square IS the hover circle, and the glyph is FLEX-centred in it —
           geometric centring, not typographic. vertical-align: middle would put
           the glyph on the text x-height instead, which sits it visibly high
           inside a round surface. Only this box's placement on the line is
           typographic; what's inside it is exact. */
        .action-note-view-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 1.5em;
          height: 1.5em;
          border-radius: 50%;
          vertical-align: middle;
          transition: background 0.15s ease, box-shadow 0.15s ease;
        }
        .action-note-view-icon svg {
          display: block;
          width: 1.15em;
          height: 1.15em;
        }
        .action-note:hover .action-note-view,
        .action-note-view:hover,
        .action-note-view:focus-visible {
          color: var(--color-secondary);
        }
        .action-note-view:hover .action-note-view-icon,
        .action-note-view:focus-visible .action-note-view-icon {
          background: color-mix(in srgb, var(--color-secondary) 10%, #ffffff);
        }
        /* Focus ring rides the round surface, not the button's invisible box. */
        .action-note-view:focus-visible { outline: none; }
        .action-note-view:focus-visible .action-note-view-icon {
          box-shadow: 0 0 0 3px rgba(47, 107, 215, 0.12);
        }
        /* The pen of a message that can still be sent: the retry buttons' coral
           ("do something") at rest AND on hover, the eye stays navy/blue. */
        .action-note-view.is-edit,
        .action-note:hover .action-note-view.is-edit {
          color: color-mix(in srgb, var(--color-tertiary) 78%, #6d2f18);
        }
        .action-note-view.is-edit:hover .action-note-view-icon,
        .action-note-view.is-edit:focus-visible .action-note-view-icon { background: #fdf6f2; }
        /* Admin-only retry of failed ⚠️ actions (visible to super_admin only,
           and only while the task has replayable failures). Spinner reuses the
           shared \`spin\` keyframes from App.css. */
        .action-retry-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 3px 10px;
          font-size: 0.78em;
          font-weight: 600;
          color: color-mix(in srgb, var(--color-tertiary) 78%, #6d2f18);
          background: #fff;
          border: 1px solid color-mix(in srgb, var(--color-tertiary) 45%, #e5e9ef);
          border-radius: 6px;
          cursor: pointer;
        }
        .action-retry-btn:hover:not(:disabled) { background: #fdf6f2; }
        .action-retry-btn:disabled { opacity: 0.7; cursor: default; }
        .action-retry-icon { font-size: 1.05em; line-height: 1; }
        .action-retry-btn.is-retrying .action-retry-icon {
          display: inline-block;
          animation: spin 1s linear infinite;
        }
        /* Split pair: retry-all + the per-action menu trigger ("Einzeln senden ▾").
           The group owns the top margin so the buttons stay baseline-aligned. */
        .action-retry-group {
          display: inline-flex;
          gap: 6px;
          margin-top: 6px;
        }
        .action-retry-caret { font-size: 0.9em; line-height: 1; }
        /* Staff pair ("… erneut senden" + "Text bearbeiten") wraps in a narrow cell. */
        .action-retry-group--wrap { flex-wrap: wrap; }
        /* Address field of "Benachrichtigungen erneut senden" when the unsent
           e-mail had no valid address. */
        .action-email-fix { margin-top: 6px; max-width: 360px; }
        .action-email-fix-label { display: block; font-size: 0.78em; font-weight: 600; color: #5f6773; margin-bottom: 3px; }
        .action-email-fix-row { display: flex; gap: 6px; align-items: center; }
        .action-email-fix-input {
          flex: 1;
          min-width: 0;
          padding: 3px 8px;
          font-size: 0.82em;
          border: 1px solid color-mix(in srgb, var(--color-tertiary) 45%, #e5e9ef);
          border-radius: 6px;
        }
        .action-email-fix-error { margin-top: 3px; font-size: 0.78em; color: #b3261e; }
        .action-retry-menu-trigger.is-open { background: #fdf6f2; }
        /* The menu panel: portaled to document.body (like .caller-popover) with
           inline top/left, because row transforms trap position:fixed panels. */
        .action-retry-menu {
          position: fixed;
          z-index: 1000;
          min-width: 240px;
          max-width: 380px;
          padding: 4px;
          background: #fff;
          border: 1px solid color-mix(in srgb, var(--color-tertiary) 45%, #e5e9ef);
          border-radius: 8px;
          box-shadow: 0 4px 16px rgba(0,0,0,0.12);
          font-size: 0.82rem;
          line-height: 1.3;
        }
        .action-retry-menu-empty {
          padding: 6px 10px;
          color: #8a929e;
        }
        .action-retry-menu-item {
          display: flex;
          width: 100%;
          gap: 6px;
          align-items: baseline;
          padding: 5px 8px;
          border: none;
          border-radius: 6px;
          background: none;
          text-align: left;
          cursor: pointer;
          font-size: inherit;
          line-height: inherit;
        }
        .action-retry-menu-item:hover:not(:disabled) { background: #fdf6f2; }
        .action-retry-menu-item:hover:not(:disabled) .action-retry-menu-item-send { opacity: 1; }
        .action-retry-menu-item.is-blocked { opacity: 0.55; cursor: default; }
        .action-retry-menu-item-body {
          flex: 1;
          min-width: 0;
          word-break: break-word;
        }
        /* Hover-revealed ↻ send affordance on the row's right edge. */
        .action-retry-menu-item-send {
          flex: 0 0 auto;
          opacity: 0;
          font-weight: 600;
          color: color-mix(in srgb, var(--color-tertiary) 78%, #6d2f18);
        }
        /* Modal preview: a self-contained read-only receipt panel (its own
           form-label supplies the heading, so no internal caption / top rule). */
        .action-notes--modal {
          margin-top: 4px;
          padding: 8px 10px;
          border-top: none;
          border: 1px solid #eef1f5;
          border-radius: 8px;
          background: #fafbfc;
          font-size: 0.9rem;
        }
        /* Read-only receipt shown beneath the prose textarea while editing inline. */
        .action-notes--edit {
          margin-top: 10px;
          cursor: default;
        }
        /* Zu tun: the Aktionen ledger with a checkbox in the glyph column. Open
           items read one step stronger than the receipt (they are the work);
           done items fall back to receipt grey with a quiet byline. */
        .todo-item {
          cursor: pointer;
        }
        .todo-text {
          color: var(--text-primary);
        }
        .todo-check {
          width: 13px;
          height: 13px;
          margin: 0;
          vertical-align: -2px;
          accent-color: var(--color-secondary);
          cursor: pointer;
        }
        .todo-check:focus-visible {
          outline: 2px solid color-mix(in srgb, var(--color-secondary) 45%, transparent);
          outline-offset: 2px;
        }
        .todo-item.is-done .todo-text {
          color: #8a929e;
        }
        .todo-byline {
          color: #b3bbc7;
          font-variant-numeric: tabular-nums;
        }
        /* Delete: a quiet × right after the text that shows on row hover (always
           on touch), and asks "Löschen?" in coral before the second click removes
           the to-do. */
        .todo-delete {
          display: inline-block;
          margin-left: 4px;
          padding: 0 5px;
          border: none;
          border-radius: 4px;
          background: transparent;
          color: #9aa3af;
          font: inherit;
          font-size: 1.2em;
          line-height: 1;
          vertical-align: -1px;
          cursor: pointer;
          opacity: 0;
          transition: opacity 0.12s ease, color 0.12s ease, background 0.12s ease;
        }
        .todo-item:hover .todo-delete,
        .todo-delete:focus-visible,
        .todo-delete.is-armed {
          opacity: 1;
        }
        @media (hover: none) {
          .todo-delete { opacity: 1; }
        }
        .todo-delete:hover {
          color: #6b7280;
          background: #f1f3f6;
        }
        .todo-delete.is-armed {
          color: #ffffff;
          background: var(--color-tertiary);
          font-size: 0.9em;
          font-weight: 600;
          line-height: 1.4;
          vertical-align: 0;
        }
        .todo-delete:focus-visible {
          outline: 2px solid color-mix(in srgb, var(--color-secondary) 45%, transparent);
          outline-offset: 1px;
        }
        /* Anrufe heute: a time column in place of the glyph, then the fixed line. */
        .call-log-time {
          flex: 0 0 auto;
          min-width: 2.9em;
          color: #8a929e;
          font-variant-numeric: tabular-nums;
        }
        .call-log-line.is-self .action-note-detail {
          color: #6b7280;
        }
        /* Practice notes (📌) — human "margin notes". A warm-neutral tinted panel
           with a subtle left rail, a touch more presence than the muted ✅
           receipt (human content). Note-less rows show only a hover-revealed
           "+ Notiz" that takes NO space at rest → they look exactly as before. */
        .task-notes {
          margin-top: 2px;
        }
        .task-notes--empty {
          margin-top: 0;
        }
        .task-note {
          display: flex;
          align-items: flex-start;
          gap: 6px;
          margin-top: 6px;
          padding: 4px 7px;
          font-size: 0.86em;
          line-height: 1.4;
          color: #5f5b57;
          background: color-mix(in srgb, var(--color-tertiary) 8%, #ffffff);
          border-left: 2px solid color-mix(in srgb, var(--color-tertiary) 50%, #ffffff);
          border-radius: 5px;
          white-space: normal;
          word-break: break-word;
        }
        .task-note-pin {
          flex: 0 0 auto;
          font-size: 0.9em;
          line-height: 1.4;
        }
        /* Column wrapper: note text with a muted authorship byline beneath it. */
        .task-note-main {
          flex: 1 1 auto;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .task-note-text {
          min-width: 0;
          cursor: text;
        }
        /* Authorship byline (shared by the row + modal). Two-tone: the author name
           carries slightly more weight/contrast than the muted date·time. */
        .task-note-byline {
          font-size: 0.7em;
          line-height: 1.3;
          letter-spacing: 0.02em;
          font-variant-numeric: tabular-nums;
        }
        .modal-note-byline {
          font-size: 0.72rem;
          line-height: 1.3;
          letter-spacing: 0.02em;
          font-variant-numeric: tabular-nums;
        }
        .task-note-byline .note-byline-author,
        .modal-note-byline .note-byline-author { color: #8c857e; font-weight: 500; }
        .task-note-byline .note-byline-time,
        .modal-note-byline .note-byline-time { color: #ada69f; font-weight: 400; }
        .task-note-byline .note-byline-sep,
        .modal-note-byline .note-byline-sep { color: #ccc6bf; }
        .task-note-remove {
          flex: 0 0 auto;
          background: none;
          border: none;
          padding: 0 2px;
          color: #b9a99c;
          cursor: pointer;
          font-size: 0.9em;
          line-height: 1;
          opacity: 0;
          transition: opacity 0.15s ease, color 0.15s ease;
        }
        .task-note:hover .task-note-remove { opacity: 1; }
        .task-note-remove:hover { color: var(--color-tertiary); }
        .task-note-input {
          flex: 1 1 auto;
          min-width: 0;
          border: 1px solid var(--color-secondary);
          border-radius: 4px;
          padding: 2px 6px;
          font: inherit;
          font-size: 1em;
          color: #333;
          background: #fff;
          outline: none;
          box-shadow: 0 0 0 3px rgba(47, 107, 215, 0.12);
        }
        .task-note-add {
          display: inline-block;
          margin-top: 5px;
          padding: 1px 4px 1px 0;
          background: none;
          border: none;
          color: var(--color-secondary);
          font-size: 0.78em;
          font-weight: 600;
          cursor: pointer;
          opacity: 0.55;
          transition: opacity 0.15s ease;
        }
        .task-note-add:hover { opacity: 1; }
        /* Note-less row: hide the add button entirely at rest (no reserved space);
           reveal it only when the row is hovered. */
        .task-notes--empty .task-note-add { display: none; }
        .tasks-table tbody tr:hover .task-notes--empty .task-note-add {
          display: inline-block;
        }
        /* Modal Notizen section: editable single-line inputs + add/remove, with a
           read-only authorship byline beneath each saved note. */
        .modal-note-row {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          margin-bottom: 8px;
        }
        .modal-note-pin {
          flex: 0 0 auto;
          font-size: 1rem;
          margin-top: 8px;
        }
        .modal-note-main {
          flex: 1 1 auto;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 3px;
        }
        .modal-note-main .form-input {
          width: 100%;
        }
        .modal-note-remove {
          flex: 0 0 auto;
          background: none;
          border: none;
          color: #b9a99c;
          cursor: pointer;
          font-size: 1rem;
          line-height: 1;
          padding: 4px 6px;
          margin-top: 4px;
        }
        .modal-note-remove:hover { color: var(--color-tertiary); }
        .modal-note-add {
          background: none;
          border: 1px dashed #d0d7e2;
          border-radius: 6px;
          color: var(--color-secondary);
          font-size: 0.85rem;
          font-weight: 600;
          padding: 6px 12px;
          cursor: pointer;
          transition: background 0.15s ease, border-color 0.15s ease;
        }
        .modal-note-add:hover {
          background: #f6f8fc;
          border-color: var(--color-secondary);
        }
        .description-cell {
          position: relative;
        }
        /* Hint floats absolutely below the textarea so it doesn't reflow
           the table row when entering / leaving edit mode (no jitter). */
        .description-edit-hint {
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          margin-top: 5px;
          font-size: 0.84rem;
          color: #888;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          flex-wrap: nowrap;
          line-height: 1.4;
          user-select: none;
          pointer-events: none;
          white-space: nowrap;
          z-index: 5;
          background: #fff;
          padding: 3px 8px;
          border-radius: 4px;
        }
        .description-edit-hint kbd {
          background: #f3f4f6;
          border: 1px solid #e5e7eb;
          border-bottom-width: 2px;
          border-radius: 3px;
          padding: 1px 6px;
          font-family: inherit;
          font-size: 0.78rem;
          color: var(--color-main);
          line-height: 1;
        }
        /* Modal chrome shared by the cheat sheet and the sent-message viewer:
           one definition so the two panels cannot drift apart. Only the size
           differs, set per panel below. */
        .shortcut-help-backdrop,
        .sent-message-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.32);
          z-index: 1500;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: backdropFadeIn 0.15s ease-out;
        }
        @keyframes backdropFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .shortcut-help-modal,
        .sent-message-modal {
          background: #fff;
          border-radius: 10px;
          overflow: hidden;
          box-shadow: 0 24px 48px rgba(0, 0, 0, 0.18);
          display: flex;
          flex-direction: column;
        }
        .shortcut-help-modal {
          width: 90vw;
          max-width: 1200px;
          max-height: 90vh;
        }
        .shortcut-help-header,
        .sent-message-header {
          border-bottom: 1px solid #eee;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .shortcut-help-header { padding: 16px 20px; }
        .shortcut-help-close,
        .sent-message-close {
          background: transparent;
          border: none;
          font-size: 1.4rem;
          color: #888;
          cursor: pointer;
          line-height: 1;
        }
        .shortcut-help-close:hover,
        .sent-message-close:hover { color: var(--color-main); }
        .shortcut-help-body {
          padding: 16px 20px;
          overflow-y: auto;
        }
        .shortcut-help-section-title {
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: #888;
          margin: 12px 0 6px;
        }
        .shortcut-help-section-title:first-child { margin-top: 0; }
        .shortcut-help-row {
          display: grid;
          grid-template-columns: 80px 1fr;
          align-items: center;
          column-gap: 14px;
          padding: 6px 0;
          font-size: 0.9rem;
        }
        .shortcut-help-row .keys {
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }
        .shortcut-help-row kbd {
          background: #f0f4f9;
          border: 1px solid #d0d7e2;
          border-bottom-width: 2px;
          border-radius: 5px;
          padding: 2px 8px;
          font-size: 0.75rem;
          font-family: inherit;
          color: var(--color-main);
          min-width: 28px;
          text-align: center;
        }
        .shortcut-help-row span {
          color: #444;
        }
        /* ── Sent-message viewer ──────────────────────────────────────────────
           Backdrop + panel mirror .shortcut-help-* (same fade, radius, shadow)
           at z-index 1500, so it clears the slide-over's 1000 — the pill can be
           clicked from inside the panel's own action preview. Deliberately NOT
           anchored to the pill like .caller-popover / .action-retry-menu: both
           re-anchor on a capturing scroll listener, and a panel that scrolls
           internally would jitter against it. */
        .sent-message-backdrop { padding: 24px; }
        .sent-message-modal {
          width: 100%;
          max-width: 560px;
          max-height: 85vh;
        }
        .sent-message-header { padding: 14px 20px; }
        .sent-message-header h3 {
          margin: 0;
          font-size: 1.05rem;
          font-weight: 600;
          color: var(--color-main);
        }
        .sent-message-close {
          background: transparent;
          border: none;
          font-size: 1.4rem;
          color: #888;
          cursor: pointer;
          line-height: 1;
        }
        .sent-message-close:hover { color: var(--color-main); }
        .sent-message-tabs {
          display: flex;
          gap: 4px;
          padding: 8px 20px 0;
          border-bottom: 1px solid #eee;
        }
        .sent-message-tab {
          padding: 5px 12px 7px;
          font-family: inherit;
          font-size: 0.82rem;
          font-weight: 600;
          color: #8a929e;
          background: none;
          border: none;
          border-bottom: 2px solid transparent;
          cursor: pointer;
        }
        .sent-message-tab:hover { color: var(--color-main); }
        .sent-message-tab.is-active {
          color: var(--color-secondary);
          border-bottom-color: var(--color-secondary);
        }
        /* The panel scrolls INTERNALLY (flex:1 + min-height:0), so a long
           e-mail body never pushes the modal past its 85vh. */
        .sent-message-body {
          flex: 1;
          min-height: 0;
          overflow-y: auto;
          padding: 14px 20px 18px;
        }
        .sent-message-meta {
          margin-bottom: 12px;
          padding-bottom: 10px;
          border-bottom: 1px solid #eef1f5;
        }
        .sent-message-meta-row {
          display: grid;
          grid-template-columns: 92px 1fr;
          column-gap: 12px;
          padding: 2px 0;
          font-size: 0.85rem;
          line-height: 1.4;
        }
        .sent-message-meta-label {
          color: #b3bbc7;
          font-size: 0.72rem;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          font-weight: 600;
          align-self: center;
        }
        .sent-message-meta-value {
          color: var(--text-primary);
          word-break: break-word;
        }
        .sent-message-status {
          margin-left: 8px;
          padding: 1px 8px;
          border-radius: 999px;
          font-size: 0.72rem;
          font-weight: 600;
          white-space: nowrap;
        }
        .sent-message-status.is-delivered,
        .sent-message-status.is-sent {
          color: #3f7a4b;
          background: color-mix(in srgb, var(--color-quaternary) 26%, #ffffff);
        }
        .sent-message-status.is-failed {
          color: color-mix(in srgb, var(--color-tertiary) 78%, #6d2f18);
          background: color-mix(in srgb, var(--color-tertiary) 18%, #ffffff);
        }
        .sent-message-status.is-pending {
          color: #8a929e;
          background: var(--table-header-bg);
        }
        /* The message itself, verbatim. pre-wrap keeps the sender's own line
           breaks — half of what "what did we send?" actually means. */
        .sent-message-text {
          margin: 0;
          padding: 12px 14px;
          font-family: inherit;
          font-size: 0.9rem;
          line-height: 1.55;
          color: var(--text-primary);
          white-space: pre-wrap;
          word-break: break-word;
          background: #fafbfc;
          border: 1px solid #eef1f5;
          border-radius: 8px;
        }
        .sent-message-empty {
          padding: 10px 0;
          color: #8a929e;
          font-size: 0.9rem;
        }
        /* Editor of a sendable message: the read panel's own box (.sent-message-
           text), but white with a border and the inline editor's focus ring, so
           "grey = sent, white = you can type" reads at a glance. */
        .sent-message-lead { margin: 0 0 8px; font-size: 0.85rem; color: #5f6773; }
        .sent-message-editor { display: block; width: 100%; box-sizing: border-box; resize: vertical; background: #fff; border-color: #d0d7e2; }
        .sent-message-input, .sent-message-btn, .sent-message-reset { font-family: inherit; font-size: 0.85rem; }
        .sent-message-input { width: 100%; box-sizing: border-box; padding: 4px 8px; color: var(--text-primary); border: 1px solid #d0d7e2; border-radius: 6px; }
        .sent-message-editor:focus, .sent-message-input:focus {
          outline: none; border-color: var(--color-secondary); box-shadow: 0 0 0 3px rgba(47, 107, 215, 0.12);
        }
        .sent-message-editor[readonly] { background: #fafbfc; color: #5f6773; }
        /* iOS zooms into a focused field under 16px. */
        @media (hover: none) { .sent-message-editor, .sent-message-input { font-size: 16px; } }
        .sent-message-editor-meta {
          display: flex; flex-wrap: wrap; justify-content: space-between; gap: 2px 12px;
          margin-top: 6px; font-size: 0.78rem; color: #8a929e;
        }
        .sent-message-problem { color: #b3261e; }
        .sent-message-counter { margin-left: auto; white-space: nowrap; }
        .sent-message-counter.is-more, .sent-message-note { color: color-mix(in srgb, var(--color-tertiary) 78%, #6d2f18); }
        .sent-message-counter.is-more { font-weight: 600; }
        .sent-message-note {
          margin-top: 10px; padding: 8px 12px; font-size: 0.85rem; border-radius: 6px;
          background: color-mix(in srgb, var(--color-tertiary) 10%, #ffffff);
        }
        .sent-message-footer {
          display: flex; flex-wrap: wrap; align-items: center; justify-content: flex-end; gap: 8px;
          padding: 12px 20px; border-top: 1px solid #eee;
        }
        .sent-message-footer-text { margin-right: auto; font-size: 0.85rem; color: var(--text-primary); }
        .sent-message-reset { margin-right: auto; padding: 0; color: var(--color-secondary); background: none; border: none; cursor: pointer; }
        .sent-message-reset:hover { text-decoration: underline; }
        .sent-message-btn {
          display: inline-flex; align-items: center; gap: 6px; padding: 6px 14px; font-weight: 600;
          color: var(--text-primary); background: #fff; border: 1px solid #d0d7e2; border-radius: 6px; cursor: pointer;
        }
        .sent-message-btn:hover:not(:disabled) { background: #f6f8fb; }
        .sent-message-btn:disabled { opacity: 0.6; cursor: default; }
        .sent-message-btn.is-primary { color: #fff; background: var(--color-secondary); border-color: var(--color-secondary); }
        .sent-message-btn.is-primary:hover:not(:disabled) { background: color-mix(in srgb, var(--color-secondary) 88%, #fff); }
        .sent-message-btn.is-danger { color: #b3261e; border-color: color-mix(in srgb, #b3261e 35%, #ffffff); }
        .sent-message-btn.is-danger:hover { background: #fdf3f2; }
        .sent-message-spin { display: inline-block; animation: spin 1s linear infinite; }
        .tasks-table tbody tr:hover {
          background: #f8fafc !important;
        }
        .action-btn {
          background: none;
          border: none;
          cursor: pointer;
          padding: 8px;
          border-radius: 4px;
          transition: background 0.15s ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          text-decoration: none;
          position: relative;
        }
        .action-btn:hover {
          background: #f0f4ff;
        }
        .action-btn--delete:hover {
          background: #fde2e4;
        }
        .action-btn--email:hover,
        .action-btn--ticket:hover {
          background: color-mix(in srgb, var(--color-secondary) 15%, #fff);
        }
        /* Container stays at default action-btn size so layout doesn't shift.
           A ::before pseudo-element draws the small circle (non-hover) that
           expands to fill the container on hover. */
        .action-btn--recording {
          background: transparent;
          color: #fff;
          width: 34px;
          height: 34px;
          padding: 8px;
          flex-shrink: 0;
          box-sizing: border-box;
          transition: color 0.15s ease;
        }
        .action-btn--recording::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 24px;
          height: 24px;
          background: var(--color-main);
          border-radius: 50%;
          transition: width 0.15s ease, height 0.15s ease, border-radius 0.15s ease;
          z-index: 0;
        }
        .action-btn--recording:hover::before {
          width: 100%;
          height: 100%;
          border-radius: 6px;
        }
        .action-btn--recording:hover {
          background: transparent;
          color: #7bd389;
        }
        .action-btn--recording svg {
          position: relative;
          z-index: 1;
          width: 14px;
          height: 14px;
          transition: width 0.15s ease, height 0.15s ease;
        }
        .action-btn--recording:hover svg {
          width: 18px;
          height: 18px;
        }
        /* Greyed-out variant for tasks without a recording (no session_id).
           If this still appears active in prod despite no recording, the
           backend is likely populating task.session_id for all tasks — check
           the API response and ensure session_id is null when no audio exists. */
        .action-btn--recording-empty {
          cursor: not-allowed;
        }
        .action-btn--recording-empty::before {
          background: #d1d5db;
        }
        .action-btn--recording-empty:hover::before {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #d1d5db;
        }
        .action-btn--recording-empty,
        .action-btn--recording-empty:hover {
          color: #f3f4f6;
        }
        .action-btn--recording-empty:hover svg {
          width: 14px;
          height: 14px;
        }
        .action-btn--disabled {
          opacity: 0.35;
          cursor: not-allowed;
        }
        .action-btn--disabled:hover {
          background: none;
        }
        .action-btn--confirm-delete {
          background: #dc2626;
          padding: 6px 10px;
          animation: confirmDeletePulse 0.4s ease-out;
        }
        .action-btn--confirm-delete:hover {
          background: #b91c1c;
        }
        @keyframes confirmDeletePulse {
          0% { transform: scale(0.92); }
          60% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }
        .action-btn[data-tooltip]::after {
          content: attr(data-tooltip);
          position: absolute;
          bottom: calc(100% + 4px);
          left: 50%;
          transform: translateX(-50%);
          padding: 5px 9px;
          background: #fff;
          color: color-mix(in srgb, var(--color-main) 80%, transparent);
          font-size: 0.75rem;
          font-weight: 400 !important;
          white-space: normal;
          width: max-content;
          max-width: 220px;
          text-align: center;
          border: 1px solid #e0e0e0;
          border-radius: 6px;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
          opacity: 0;
          visibility: hidden;
          transition: opacity 0.1s ease, visibility 0.1s ease;
          z-index: 1000;
          pointer-events: none;
        }
        .action-btn[data-tooltip]:hover::after,
        .action-btn[data-tooltip]:focus-visible::after {
          opacity: 1;
          visibility: visible;
        }
        .action-btn--tooltip-right[data-tooltip]::after {
          left: auto;
          right: 0;
          transform: none;
        }
        .search-mark {
          background: rgba(123, 211, 137, 0.45);
          color: inherit;
          padding: 0;
          border-radius: 2px;
          /* box-shadow gives the visual padding feel without adding layout width */
          box-shadow: 0 0 0 1px rgba(123, 211, 137, 0.45);
        }
        .search-shortcut-hint {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 0.7rem;
          color: #999;
          pointer-events: none;
          background: #f3f4f6;
          padding: 2px 6px;
          border-radius: 4px;
          border: 1px solid #e5e7eb;
        }
        .search-result-count {
          position: absolute;
          right: 36px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 0.75rem;
          color: #666;
          pointer-events: none;
          background: #f3f4f6;
          padding: 2px 6px;
          border-radius: 4px;
        }
        .autocomplete-dropdown {
          position: absolute;
          top: calc(100% + 4px);
          left: 0;
          right: 0;
          background: #fff;
          border: 1px solid #ccc;
          border-radius: 6px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
          z-index: 1000;
          max-height: 280px;
          overflow-y: auto;
          padding: 4px 0;
        }
        .autocomplete-item {
          padding: 8px 12px;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          gap: 2px;
          transition: background 0.1s ease;
        }
        .autocomplete-item:hover,
        .autocomplete-item.is-active {
          background: #f0f4ff;
        }
        .autocomplete-item-name {
          font-size: 0.9rem;
          font-weight: 500;
          color: var(--color-main);
        }
        .autocomplete-item-meta {
          font-size: 0.75rem;
          color: #888;
        }
        .autocomplete-empty {
          padding: 12px;
          text-align: center;
          color: #888;
          font-size: 0.85rem;
        }
        .caller-name {
          font-weight: 500;
          color: inherit;
        }
        .assignee-trigger {
          display: inline-flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          width: 100%;
          max-width: 170px;
          font-size: 0.9rem;
          padding: 5px 10px;
          border-radius: 6px;
          border: 1px solid #d0d7e2;
          background: #fff;
          color: #333;
          cursor: pointer;
          text-align: left;
          transition: border-color 0.12s ease, background 0.12s ease;
        }
        .assignee-trigger:hover {
          border-color: var(--color-secondary);
          background: #f7faff;
        }
        .assignee-trigger.is-open {
          border-color: var(--color-secondary);
          box-shadow: 0 0 0 2px rgba(47, 107, 215, 0.15);
        }
        .assignee-trigger .placeholder {
          color: #999;
        }
        .assignee-trigger .chevron {
          flex-shrink: 0;
          color: #888;
          transition: transform 0.12s ease;
        }
        .assignee-trigger.is-open .chevron {
          transform: rotate(180deg);
        }
        .assignee-panel {
          background: #fff;
          border-radius: 8px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
          padding: 4px;
          z-index: 1000;
          max-height: 220px;
          overflow-y: auto;
          border: 1px solid #e5e7eb;
        }
        .assignee-option {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 7px 10px;
          font-size: 0.9rem;
          color: #333;
          cursor: pointer;
          border-radius: 5px;
          white-space: nowrap;
        }
        .assignee-option:hover {
          background: #f0f4ff;
        }
        .assignee-option.is-active {
          background: rgba(47, 107, 215, 0.12);
          color: var(--color-main);
          font-weight: 500;
        }
        .assignee-option .check {
          width: 14px;
          opacity: 0;
        }
        .assignee-option.is-active .check {
          opacity: 1;
        }
        .caller-inline-edit {
          font: inherit;
          color: inherit;
          background: #fff;
          border: 1px solid #2F6BD7;
          border-radius: 4px;
          padding: 2px 6px;
          width: 100%;
          max-width: 220px;
          box-sizing: border-box;
          outline: none;
        }
        .caller-inline-edit:focus {
          border-color: #1b3f65;
          box-shadow: 0 0 0 2px rgba(47, 107, 215, 0.18);
        }
        .caller-name-edit { font-weight: 500; }
        .caller-clickable {
          cursor: text;
          border-radius: 3px;
          padding: 0 2px;
          margin: 0 -2px;
          transition: background 0.15s ease;
        }
        .caller-clickable:hover {
          background: rgba(47, 107, 215, 0.08);
        }
        .caller-phone {
          color: color-mix(in srgb, var(--color-main) 60%, transparent);
          line-height: 1.2;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          white-space: nowrap;
        }
        .caller-phone-link {
          color: inherit;
          text-decoration: none;
        }
        .caller-phone-link:hover {
          text-decoration: underline;
        }
        /* Stacks the "angegebene Nummer" line and the (only-when-different)
           caller-ID line vertically under the name. */
        .caller-phones {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 2px;
        }
        /* The original caller-ID line is secondary context — slightly dimmer/smaller. */
        .caller-phone--callerid {
          color: color-mix(in srgb, var(--color-main) 45%, transparent);
          font-size: 0.92em;
        }
        /* The PVS-profile mobile line — the number adopted from the patient's
           PVS profile as the confirmation-SMS target. Dimmer color marks it as
           system-derived (not the patient-given 🗣️ callback), but kept at full
           size to match the caller-ID number line. */
        .caller-phone--pvs {
          color: color-mix(in srgb, var(--color-main) 45%, transparent);
        }
        /* The caller's e-mail address (PVS profile or dictated on the call) —
           same dimming as the number lines it stacks under. Wrapping is
           re-enabled (.caller-phone is nowrap) because an address is far longer
           than a phone number; flex-start keeps the ✉ tag and copy button
           aligned to the first line when it does wrap. Set a step below the
           secondary caller-ID line (0.92em) rather than matching it: an address
           is several times the length of a number, so it needs the extra
           reduction to stop dominating the cell and to wrap over fewer lines.
           Everything inside is em-relative (.caller-phone-tag is 0.9em/1, its
           min-height derives from the same em), so the tag geometry below scales
           with this and needs no adjustment. */
        .caller-phone--email {
          color: color-mix(in srgb, var(--color-main) 45%, transparent);
          font-size: 0.85em;
          align-items: flex-start;
          white-space: normal;
          overflow-wrap: anywhere;
        }
        /* …but flex-start alone tops the tag against the CONTAINER, not against
           line one: the tag box is 0.9em tall (its font-size, line-height 1)
           while the value's first line box is 1.2em (.caller-phone line-height),
           so the glyph rode ~0.15em high. Giving the tag a box the height of
           that first line re-centers it there — the same result align-items:
           center produces on the single-line phone rows. Divided by 0.9 because
           em resolves against the tag's own font-size. The copy/mailto buttons
           need no rule: 14px icon + 2px padding ≈ the 1.2em line box already. */
        .caller-phone--email .caller-phone-tag {
          min-height: calc(1.2em / 0.9);
        }
        /* The 📞 / 🗣️ prefix glyph. Carries a hover tooltip explaining which
           number this is (data-tooltip, mirroring .caller-copy-btn). */
        .caller-phone-tag {
          position: relative;
          flex-shrink: 0;
          line-height: 1;
          font-size: 0.9em;
          cursor: default;
        }
        .caller-phone-tag::after {
          content: attr(data-tooltip);
          position: absolute;
          bottom: 100%;
          left: 0;
          margin-bottom: 6px;
          padding: 7px 12px;
          background-color: #fff;
          color: color-mix(in srgb, var(--color-main) 70%, transparent);
          font-size: 0.75rem;
          font-weight: 500;
          white-space: nowrap;
          border: 1px solid #e0e0e0;
          border-radius: 6px;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
          opacity: 0;
          visibility: hidden;
          transition: opacity 0.1s ease-in-out;
          z-index: 1000;
          pointer-events: none;
        }
        .caller-phone-tag:hover::after {
          opacity: 1;
          visibility: visible;
        }
        /* The PVS-mobile tag uses a crisp inline SVG instead of the 📱 emoji,
           which washed out to a grey blob under the dimmed .caller-phone--pvs
           line. The vector inherits currentColor and stays sharp; rendered a
           touch stronger than the dimmed number for legibility, matching the
           secondary caller-ID line's feel. */
        .caller-phone-tag--icon {
          display: inline-flex;
          align-items: center;
          color: color-mix(in srgb, var(--color-main) 62%, transparent);
        }
        .caller-copy-btn {
          background: transparent;
          border: none;
          padding: 2px;
          cursor: pointer;
          color: #888;
          opacity: 0;
          transition: opacity 0.15s ease, color 0.15s ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 3px;
          line-height: 0;
          position: relative;
        }
        .caller-copy-btn::after {
          content: attr(data-tooltip);
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          margin-bottom: 6px;
          padding: 7px 12px;
          background-color: #fff;
          color: color-mix(in srgb, var(--color-main) 60%, transparent);
          font-size: 0.75rem;
          font-weight: 500;
          white-space: nowrap;
          border: 1px solid #e0e0e0;
          border-radius: 6px;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
          opacity: 0;
          visibility: hidden;
          transition: opacity 0.1s ease-in-out;
          z-index: 1000;
          pointer-events: none;
        }
        .caller-copy-btn:hover::after {
          opacity: 1;
          visibility: visible;
        }
        .caller-name-line:hover .caller-copy-btn,
        .caller-phone:hover .caller-copy-btn,
        .caller-birthdate-eyebrow:hover .caller-copy-btn,
        .session-id-line:hover .caller-copy-btn,
        .caller-copy-btn:focus-visible {
          opacity: 1;
        }
        .caller-birthdate-eyebrow .caller-copy-btn {
          margin-left: 8px;
        }
        .caller-name-line .caller-copy-btn {
          margin-left: 6px;
        }
        .caller-copy-btn:hover {
          color: var(--color-main);
          background: rgba(0, 0, 0, 0.05);
        }
        .caller-copied-feedback {
          color: var(--color-quaternary);
          font-size: 0.75rem;
          font-weight: 600;
          margin-left: 6px;
        }
        .caller-birthdate-eyebrow {
          font-size: 13px;
          color: color-mix(in srgb, var(--color-main) 60%, transparent);
          line-height: 1;
          margin-bottom: 2px;
          letter-spacing: 0.02em;
        }
        /* "Termin für": the other person(s) of the call, under the contact's
           numbers. Hairline + caption as on the Aktionen receipt, text at the
           birthdate eyebrow's size and mute. */
        .caller-persons {
          margin-top: 4px;
          padding-top: 4px;
          border-top: 1px solid #eef1f5;
          font-size: 13px;
          line-height: 1.3;
          color: color-mix(in srgb, var(--color-main) 60%, transparent);
        }
        .caller-person + .caller-person { margin-top: 3px; }
        .caller-person-name { font-weight: 500; }
        /* Birthdate / guardian name move to the next line whole, never split. */
        .caller-wrap-unit { display: inline-block; }
        .caller-guardian {
          font-size: 12px;
          line-height: 1.3;
          color: color-mix(in srgb, var(--color-main) 45%, transparent);
        }
        .caller-cluster-meta {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          margin-top: 4px;
        }
        .caller-cluster-meta .cluster-caret {
          margin-right: 0;
        }
        .caller-cluster-meta .cluster-chip {
          margin-left: 0;
        }
        .status-filter-option:hover .status-badge {
          opacity: 1 !important;
        }
        .tasks-heading-row {
          display: grid;
          grid-template-columns: 1fr auto 1fr;
          align-items: center;
          column-gap: 12px;
          margin-bottom: 4px;
        }
        .tasks-heading-row .heading-left {
          display: flex;
          align-items: center;
          gap: 10px;
          justify-self: start;
          min-width: 0;
        }
        .tasks-heading-row .heading-search {
          justify-self: center;
          width: 360px;
          max-width: 100%;
          position: relative;
        }
        .tasks-heading-row .heading-right {
          justify-self: end;
        }
        .tasks-heading-row .heading-search input {
          padding: 10px 70px 10px 38px;
          border-radius: 8px;
          border: 1px solid #d0d7e2;
          background: #fff;
          width: 100%;
          box-sizing: border-box;
          font-size: 0.95rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
          transition: border-color 0.12s ease, box-shadow 0.12s ease;
        }
        .tasks-heading-row .heading-search input:focus {
          border-color: var(--color-secondary);
          box-shadow: 0 0 0 3px rgba(47, 107, 215, 0.14);
          outline: none;
        }
        /* Status quick-filter ("Offen" / "Alle"): light segmented control, 24px tall so it fits the shared filter label
           row (FILTER_LABEL_STYLE): the active pill is white on a grey track. */
        .status-quick-toggle {
          display: inline-flex;
          background: #eef0f3;
          border: 1px solid #e0e3e8;
          border-radius: 999px;
          padding: 2px;
          gap: 2px;
        }
        .status-quick-toggle-pill {
          appearance: none;
          background: transparent;
          border: none;
          padding: 1px 10px;
          font-size: 0.75rem;
          line-height: 16px;
          font-weight: 600;
          letter-spacing: 0.02em;
          color: color-mix(in srgb, var(--color-main) 55%, transparent);
          cursor: pointer;
          border-radius: 999px;
          position: relative;
          transition: color 0.2s ease, background 0.25s ease, box-shadow 0.25s ease;
        }
        .status-quick-toggle-pill:hover {
          color: var(--color-main);
        }
        .status-quick-toggle-pill.is-active {
          background: #fff;
          color: var(--color-main);
          box-shadow: 0 1px 2px rgba(27, 63, 101, 0.18);
        }
        .shortcuts-help-btn {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 22px;
          height: 22px;
          padding: 0;
          margin-left: 2px;
          border: 1px solid #d0d7de;
          background: #fff;
          color: #666;
          border-radius: 50%;
          font-size: 0.78rem;
          font-weight: 600;
          line-height: 1;
          cursor: pointer;
          transition: color 0.1s ease, border-color 0.1s ease, background 0.1s ease;
        }
        .shortcuts-help-btn:hover,
        .shortcuts-help-btn:focus-visible {
          color: var(--color-main);
          border-color: var(--color-main);
          background: #f3f7ff;
          outline: none;
        }
        .shortcuts-help-btn[data-tooltip]::after {
          content: attr(data-tooltip);
          position: absolute;
          bottom: calc(100% + 6px);
          left: 50%;
          transform: translateX(-50%);
          padding: 5px 9px;
          background: #fff;
          color: color-mix(in srgb, var(--color-main) 80%, transparent);
          font-size: 0.75rem;
          font-weight: 500;
          white-space: nowrap;
          border: 1px solid #e0e0e0;
          border-radius: 6px;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
          opacity: 0;
          visibility: hidden;
          transition: opacity 0.1s ease, visibility 0.1s ease;
          z-index: 1000;
          pointer-events: none;
        }
        .shortcuts-help-btn[data-tooltip]:hover::after,
        .shortcuts-help-btn[data-tooltip]:focus-visible::after {
          opacity: 1;
          visibility: visible;
        }
        .view-mode-switch-wrap {
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .view-mode-label {
          font-size: 0.9rem;
          color: #666;
          font-weight: 500;
          min-width: 64px;
          text-align: left;
          transition: color 0.15s ease;
        }
        .view-mode-label.active {
          color: var(--color-main);
        }
        .ios-switch {
          position: relative;
          display: inline-block;
          width: 44px;
          height: 24px;
          flex-shrink: 0;
        }
        .ios-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        .ios-switch-slider {
          position: absolute;
          inset: 0;
          background-color: #e9e9eb;
          border-radius: 999px;
          cursor: pointer;
          transition: background-color 0.2s ease;
        }
        .ios-switch-slider::before {
          content: "";
          position: absolute;
          height: 18px;
          width: 18px;
          left: 3px;
          top: 3px;
          background-color: #fff;
          border-radius: 50%;
          transition: transform 0.2s ease;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
        }
        .ios-switch input:checked + .ios-switch-slider {
          background-color: var(--color-secondary);
        }
        .ios-switch input:checked + .ios-switch-slider::before {
          transform: translateX(20px);
        }
        .ios-switch input:focus-visible + .ios-switch-slider {
          box-shadow: 0 0 0 3px rgba(47, 107, 215, 0.25);
        }
        .ios-switch.is-disabled .ios-switch-slider {
          cursor: not-allowed;
          opacity: 0.55;
        }
        @keyframes taskStatusPulse {
          0% {
            background-color: transparent;
          }
          30% {
            background-color: rgba(123, 211, 137, 0.32);
          }
          100% {
            background-color: transparent;
          }
        }
        @keyframes taskStatusPulseNeutral {
          0% {
            background-color: transparent;
          }
          30% {
            background-color: rgba(47, 107, 215, 0.14);
          }
          100% {
            background-color: transparent;
          }
        }
        .task-row-status-changed > td {
          animation: taskStatusPulseNeutral 1.2s ease-in-out;
        }
        .task-row-status-changed-completed > td {
          animation: taskStatusPulse 1.2s ease-in-out;
        }
      `}</style>
      <div className={`tasks-page${allPractices ? ' is-aggregate' : ''}`}>
        {/* paddingRight: clear of the absolutely positioned practice logo, as .dash-head
            does; the reception widget moved here from that row. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 16, paddingRight: 190 }}>
          <h1 style={{ margin: 0 }}>Aufgaben</h1>
          <button
            type="button"
            className="shortcuts-help-btn"
            data-tooltip="Tastenkürzel anzeigen (?)"
            aria-label="Tastenkürzel anzeigen"
            onClick={() => setShowShortcutHelp(true)}
          >
            ?
          </button>

          {/* Praxis-Umfang: the SAME control the Dashboard renders, writing the
              same shared flag — flip it there and it is already flipped here.
              Only offered when there is more than one practice to pool. */}
          <PracticeScopeToggle
            scope={scope}
            onSelect={() => setErrorMessage('')}
            loadedCount={taskScopeLoad?.loadedCount}
            scopeCount={taskScopeLoad?.scopeCount}
          />

          {/* Not for a task_manager: every closures route is verify_practice_access,
              which refuses that role, so the switch would only ever 403. */}
          {!allPractices && (practiceRole !== 'task_manager' || isUserSuperAdmin) && (
            <ReceptionStatusWidget practiceId={practiceId} />
          )}
        </div>

        {errorMessage && (
          <div className="error-message">{errorMessage}</div>
        )}

        {/* Non-blocking connection notice: the live update path can't reach the
            backend, so the list on screen may be stale. The user can keep
            working; this just prevents an empty board from looking like
            "all caught up". Clears itself the moment the connection recovers. */}
        {connectionLost && (
          <div
            role="status"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 12, margin: '0 0 16px', padding: '10px 14px',
              background: '#FFF4E5', border: '1px solid #FFD8A8', borderRadius: 8,
              color: '#8A4B00', fontSize: 14,
            }}
          >
            <span>Verbindung unterbrochen – die Liste ist möglicherweise nicht aktuell.</span>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                flexShrink: 0, padding: '6px 12px', border: '1px solid #E8A33D',
                background: '#fff', color: '#8A4B00', borderRadius: 6,
                cursor: 'pointer', fontSize: 14, fontWeight: 600,
              }}
            >
              Neu laden
            </button>
          </div>
        )}

        {/* Header with filters */}
        <div style={{ marginBottom: 28 }}>
          {/* Main row: Filters on left, button + search on right */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
            {/* Left side - Filter panel */}
            <div style={{
              background: '#f8f9fa',
              border: '1px solid #e0e0e0',
              borderRadius: 8,
              padding: 16,
              minWidth: 0,
              maxWidth: '100%'
            }}>
            <div style={{
              display: 'flex',
              flexDirection: 'row',
              gap: 16,
              alignItems: 'flex-end',
              flexWrap: 'wrap'
            }}>
              {/* Time window filter — single-select dropdown, mirrors multi-select pattern */}
              {(() => {
                const dayOptions = PERIOD_OPTIONS;
                // Trigger shows the dates once a custom range is applied — what
                // you're looking at, not which menu entry produced it.
                const dayFilterLabel = dayFilter === 'range'
                  ? formatDeRange(dayRange.from, dayRange.to)
                  : (dayOptions.find(o => o.value === dayFilter)?.label || dayFilter);
                // Opening the drawer with no committed range pre-fills the active
                // preset's window so the user edits rather than starts blank.
                // setDate() is calendar arithmetic, so it stays correct across a
                // DST switch — subtracting milliseconds would not.
                const seedRange = activeWindow();
                return (
                  <div style={{ minWidth: 172, position: 'relative' }} ref={dayFilterRef}>
                    <label style={FILTER_LABEL_STYLE}>
                      Zeitraum
                    </label>
                    <select
                      style={FILTER_SELECT_STYLE}
                      value="_custom"
                      title={dayFilter === 'range' ? formatDeRangeFull(dayRange.from, dayRange.to) : undefined}
                      onClick={(e) => { e.preventDefault(); setDayFilterOpen(prev => !prev); }}
                      onMouseDown={(e) => e.preventDefault()}
                      readOnly
                    >
                      <option value="_custom">{dayFilterLabel}</option>
                    </select>

                    {dayFilterOpen && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        minWidth: '100%',
                        // Fixed widths (not 'max-content') so the popover can
                        // animate as it unfolds — you can't transition to or
                        // from an intrinsic width.
                        width: dayRangeOpen ? 316 : 208,
                        transition: 'width 220ms cubic-bezier(0.16,1,0.3,1)',
                        marginTop: 4,
                        background: '#fff',
                        border: '1px solid #ccc',
                        borderRadius: 6,
                        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                        zIndex: 1000,
                        padding: '6px 0'
                      }}>
                        {dayOptions.map(opt => (
                          <div
                            key={opt.value}
                            onClick={() => { setDayFilter(opt.value); setDayRangeOpen(false); setDayFilterOpen(false); }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 10,
                              padding: '5px 16px',
                              cursor: 'pointer',
                              fontSize: '0.95rem',
                              whiteSpace: 'nowrap',
                              transition: 'background 0.15s ease',
                              background: dayFilter === opt.value ? '#f0f4ff' : 'transparent',
                              fontWeight: dayFilter === opt.value ? 600 : 400,
                              color: dayFilter === opt.value ? 'var(--color-secondary)' : 'inherit'
                            }}
                            onMouseEnter={e => { if (dayFilter !== opt.value) e.currentTarget.style.background = '#f8f9fa'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = dayFilter === opt.value ? '#f0f4ff' : 'transparent'; }}
                          >
                            {opt.label}
                          </div>
                        ))}

                        <DateRangeDrawer
                          compact
                          open={dayRangeOpen}
                          committed={dayFilter === 'range' ? dayRange : null}
                          seed={seedRange}
                          onToggle={() => setDayRangeOpen(prev => !prev)}
                          onApply={(range) => {
                            setDayRange(range);
                            setDayFilter('range');
                            setDayRangeOpen(false);
                            setDayFilterOpen(false);
                          }}
                        />
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Grund (call-reason) filter — multi-select dropdown, mirrors Status filter pattern */}
              <div style={{ minWidth: 150, position: 'relative' }} ref={callReasonFilterRef}>
                <label style={FILTER_LABEL_STYLE}>
                  Grund
                </label>
                <select
                  style={FILTER_SELECT_STYLE}
                  value="_custom"
                  onClick={(e) => { e.preventDefault(); setCallReasonFilterOpen(prev => !prev); }}
                  onMouseDown={(e) => e.preventDefault()}
                  readOnly
                >
                  <option value="_custom">{callReasonFilterLabel}</option>
                </select>

                {callReasonFilterOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    minWidth: '100%',
                    width: 'max-content',
                    marginTop: 4,
                    background: '#fff',
                    border: '1px solid #ccc',
                    borderRadius: 6,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                    zIndex: 1000,
                    padding: '6px 0'
                  }}>
                    {Object.entries(CALL_REASON_CONFIG).map(([key, cfg]) => (
                      <label
                        key={key}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '5px 16px',
                          cursor: 'pointer',
                          fontSize: '0.95rem',
                          whiteSpace: 'nowrap',
                          transition: 'background 0.15s ease',
                          background: callReasonFilters.includes(key) ? '#f0f4ff' : 'transparent'
                        }}
                        onMouseEnter={e => { if (!callReasonFilters.includes(key)) e.currentTarget.style.background = '#f8f9fa'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = callReasonFilters.includes(key) ? '#f0f4ff' : 'transparent'; }}
                      >
                        <input
                          type="checkbox"
                          checked={callReasonFilters.includes(key)}
                          onChange={() => toggleCallReasonFilter(key)}
                          style={{
                            width: 16,
                            height: 16,
                            accentColor: 'var(--color-secondary)',
                            cursor: 'pointer',
                            flexShrink: 0
                          }}
                        />
                        {/* Render the option as the actual Grund pill so the brand
                            colors match the badges on each task row. */}
                        <span
                          className="grund-badge"
                          style={{ backgroundColor: cfg.bg, color: cfg.fg }}
                        >
                          {cfg.label}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Assignee filter — multi-select dropdown, mirrors Status/Grund pattern */}
              <div style={{ minWidth: 150, position: 'relative' }} ref={assigneeFilterRef}>
                <label style={FILTER_LABEL_STYLE}>
                  Zuständig
                </label>
                <select
                  style={FILTER_SELECT_STYLE}
                  value="_custom"
                  onClick={(e) => { e.preventDefault(); setAssigneeFilterOpen(prev => !prev); }}
                  onMouseDown={(e) => e.preventDefault()}
                  readOnly
                >
                  <option value="_custom">{assigneeFilterLabel}</option>
                </select>

                {assigneeFilterOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    minWidth: '100%',
                    width: 'max-content',
                    marginTop: 4,
                    background: '#fff',
                    border: '1px solid #ccc',
                    borderRadius: 6,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                    zIndex: 1000,
                    padding: '6px 0'
                  }}>
                    {/* "Nicht zugewiesen" — filter to tasks with no assignee (assigneeId === null).
                        The rest of the assignee-filter machinery (toggle, chips, matching,
                        persistence) already treats null as a first-class value. */}
                    <label
                      key="unassigned"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '5px 16px',
                        cursor: 'pointer',
                        fontSize: '0.95rem',
                        whiteSpace: 'nowrap',
                        fontStyle: 'italic',
                        color: '#555',
                        borderBottom: '1px solid #eee',
                        transition: 'background 0.15s ease',
                        background: assigneeFilters.includes(null) ? '#f0f4ff' : 'transparent'
                      }}
                      onMouseEnter={e => { if (!assigneeFilters.includes(null)) e.currentTarget.style.background = '#f8f9fa'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = assigneeFilters.includes(null) ? '#f0f4ff' : 'transparent'; }}
                    >
                      <input
                        type="checkbox"
                        checked={assigneeFilters.includes(null)}
                        onChange={() => toggleAssigneeFilter(null)}
                        style={{
                          width: 16,
                          height: 16,
                          accentColor: 'var(--color-secondary)',
                          cursor: 'pointer',
                          flexShrink: 0
                        }}
                      />
                      <span>Nicht zugewiesen</span>
                    </label>
                    {[...assignableUsers].sort((a, b) => (a.firstName || '').localeCompare(b.firstName || '')).map(user => (
                      <label
                        key={user.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '5px 16px',
                          cursor: 'pointer',
                          fontSize: '0.95rem',
                          whiteSpace: 'nowrap',
                          transition: 'background 0.15s ease',
                          background: assigneeFilters.includes(user.id) ? '#f0f4ff' : 'transparent'
                        }}
                        onMouseEnter={e => { if (!assigneeFilters.includes(user.id)) e.currentTarget.style.background = '#f8f9fa'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = assigneeFilters.includes(user.id) ? '#f0f4ff' : 'transparent'; }}
                      >
                        <input
                          type="checkbox"
                          checked={assigneeFilters.includes(user.id)}
                          onChange={() => toggleAssigneeFilter(user.id)}
                          style={{
                            width: 16,
                            height: 16,
                            accentColor: 'var(--color-secondary)',
                            cursor: 'pointer',
                            flexShrink: 0
                          }}
                        />
                        <span>{`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Patienten hervorheben (insurance type / Blanko) filter — multi-select dropdown, mirrors Grund pattern */}
              <div style={{ minWidth: 150, position: 'relative' }} ref={insuranceFilterRef}>
                <label style={FILTER_LABEL_STYLE}>
                  Patienten hervorheben
                </label>
                <select
                  style={FILTER_SELECT_STYLE}
                  value="_custom"
                  onClick={(e) => { e.preventDefault(); setInsuranceFilterOpen(prev => !prev); }}
                  onMouseDown={(e) => e.preventDefault()}
                  readOnly
                >
                  <option value="_custom">{insuranceFilterLabel}</option>
                </select>

                {insuranceFilterOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    minWidth: '100%',
                    width: 'max-content',
                    marginTop: 4,
                    background: '#fff',
                    border: '1px solid #ccc',
                    borderRadius: 6,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                    zIndex: 1000,
                    padding: '6px 0'
                  }}>
                    {PATIENT_HIGHLIGHT_OPTIONS.map(type => (
                      <label
                        key={type}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '5px 16px',
                          cursor: 'pointer',
                          fontSize: '0.95rem',
                          whiteSpace: 'nowrap',
                          transition: 'background 0.15s ease',
                          background: insuranceFilters.includes(type) ? '#f0f4ff' : 'transparent'
                        }}
                        onMouseEnter={e => { if (!insuranceFilters.includes(type)) e.currentTarget.style.background = '#f8f9fa'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = insuranceFilters.includes(type) ? '#f0f4ff' : 'transparent'; }}
                      >
                        <input
                          type="checkbox"
                          checked={insuranceFilters.includes(type)}
                          onChange={() => toggleInsuranceFilter(type)}
                          style={{
                            width: 16,
                            height: 16,
                            accentColor: 'var(--color-secondary)',
                            cursor: 'pointer',
                            flexShrink: 0
                          }}
                        />
                        <span>{type}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Status filter - multi-select dropdown + Offen/Alle pill toggle */}
              {(() => {
                const openSet = openSetFor(isUserSuperAdmin);
                const isOffen = statusFilters.length === openSet.length
                  && openSet.every(s => statusFilters.includes(s));
                const isAlle = statusFilters.length === 0;
                return (
                  <div style={{ minWidth: 170, position: 'relative' }} ref={statusFilterRef}>
                    <div style={{ ...FILTER_LABEL_STYLE, justifyContent: 'space-between', gap: 8 }}>
                      <label>Status</label>
                      <div className="status-quick-toggle" role="tablist" aria-label="Schnellfilter">
                        <button
                          type="button"
                          role="tab"
                          aria-selected={isOffen}
                          className={`status-quick-toggle-pill${isOffen ? ' is-active' : ''}`}
                          onClick={() => setStatusFilters(openSet)}
                        >
                          Offen
                        </button>
                        <button
                          type="button"
                          role="tab"
                          aria-selected={isAlle}
                          className={`status-quick-toggle-pill${isAlle ? ' is-active' : ''}`}
                          onClick={() => setStatusFilters([])}
                        >
                          Alle
                        </button>
                      </div>
                    </div>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setStatusFilterOpen(prev => !prev)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setStatusFilterOpen(prev => !prev); } }}
                  style={{
                    width: '100%',
                    padding: '8px 28px 8px 12px',
                    borderRadius: 6,
                    border: '1px solid #ccc',
                    background: '#fff',
                    fontSize: '0.95rem',
                    cursor: 'pointer',
                    userSelect: 'none',
                    backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'><path d=\'M2 4l4 4 4-4\' stroke=\'%23555\' stroke-width=\'1.5\' fill=\'none\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/></svg>")',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 10px center',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    lineHeight: 'normal'
                  }}
                >
                  {typeof statusFilterLabel === 'string' ? renderStatusLabel(statusFilterLabel, { verticalAlign: '-5px' }) : statusFilterLabel}
                </div>

                {statusFilterOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    minWidth: '100%',
                    width: 'max-content',
                    marginTop: 4,
                    background: '#fff',
                    border: '1px solid #ccc',
                    borderRadius: 6,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                    zIndex: 1000,
                    padding: '6px 0'
                  }}>
                    {FILTER_ORDER
                      .map(v => STATUS_CONFIG.find(s => s.value === v))
                      .filter(option => option && option.filterable)
                      .filter(visibleTo(isUserSuperAdmin))
                      .map(option => {
                      const isChecked = statusFilters.includes(option.value);
                      return (
                      <label
                        key={option.value}
                        className="status-filter-option"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '5px 16px',
                          cursor: 'pointer',
                          fontSize: '0.95rem',
                          whiteSpace: 'nowrap',
                          transition: 'background 0.15s ease',
                          background: isChecked ? '#f0f4ff' : 'transparent'
                        }}
                        onMouseEnter={e => { if (!isChecked) e.currentTarget.style.background = '#f8f9fa'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = isChecked ? '#f0f4ff' : 'transparent'; }}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleStatusFilter(option.value)}
                          style={{
                            width: 16,
                            height: 16,
                            accentColor: 'var(--color-secondary)',
                            cursor: 'pointer',
                            flexShrink: 0
                          }}
                        />
                        <span
                          className={`status-badge ${option.className}`}
                          style={{ opacity: 1 }}
                        >
                          {renderStatusLabel(option.label, { verticalAlign: '-4px' })}
                        </span>
                      </label>
                      );
                    })}
                    {/* Standing view preference, below a hairline: it is not a
                        status choice and survives "Zurücksetzen". */}
                    <div style={{ borderTop: '1px solid #eef1f5', marginTop: 6, paddingTop: 6 }}>
                      <label
                        className="status-filter-option"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '5px 16px',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          color: 'var(--text-secondary)',
                          whiteSpace: 'nowrap',
                          transition: 'background 0.15s ease'
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#f8f9fa'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        <input
                          type="checkbox"
                          checked={hideMissed}
                          onChange={() => setHideMissed(prev => !prev)}
                          style={{
                            width: 16,
                            height: 16,
                            accentColor: 'var(--color-secondary)',
                            cursor: 'pointer',
                            flexShrink: 0
                          }}
                        />
                        Verpasste Anrufe ausblenden
                      </label>
                    </div>
                  </div>
                )}
                  </div>
                );
              })()}

              {/* Reset button - only show when filters are active */}
              {activeFilterCount > 0 && (
                <button
                  onClick={resetFilters}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 6,
                    border: 'none',
                    background: 'transparent',
                    color: '#666',
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    transition: 'background 0.15s ease, color 0.15s ease',
                    height: 'fit-content',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = '#f0f0f0';
                    e.currentTarget.style.color = '#222';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#666';
                  }}
                >
                  <span style={{ fontSize: '1.1rem', lineHeight: 1 }}>×</span>
                  Zurücksetzen
                </button>
              )}
            </div>

            {/* Active filter chips — shows what's filtered + lets user remove individual filters */}
            {activeFilterCount > 0 && (
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 6,
                marginTop: 12,
                alignItems: 'center'
              }}>
                {(() => {
                  // Hide individual status chips when the selection exactly
                  // matches the "Offen" preset — the pill toggle already
                  // communicates that state and 4 chips would be redundant.
                  const openSet = openSetFor(isUserSuperAdmin);
                  const isOffenPreset = statusFilters.length === openSet.length
                    && openSet.every(s => statusFilters.includes(s));
                  if (isOffenPreset) return null;
                  return statusFilters.map(s => {
                    const cfg = STATUS_CONFIG.find(o => o.value === s);
                    if (!cfg) return null;
                    return (
                      <span
                        key={`status-chip-${s}`}
                        className={`status-badge ${cfg.className}`}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}
                      >
                        {renderStatusLabel(cfg.label, { verticalAlign: '-4px' })}
                        <button
                          type="button"
                          onClick={() => setStatusFilters(prev => prev.filter(x => x !== s))}
                          aria-label={`Status ${cfg.label} entfernen`}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'inherit',
                            cursor: 'pointer',
                            padding: 0,
                            fontSize: '1.05rem',
                            lineHeight: 1,
                            opacity: 0.7
                          }}
                          onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
                          onMouseLeave={e => { e.currentTarget.style.opacity = '0.7'; }}
                        >×</button>
                      </span>
                    );
                  });
                })()}
                {callReasonFilters.map(r => {
                  const cfg = CALL_REASON_CONFIG[r];
                  if (!cfg) return null;
                  return (
                    <span
                      key={`reason-chip-${r}`}
                      className="grund-badge"
                      style={{
                        backgroundColor: cfg.bg,
                        color: cfg.fg,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6
                      }}
                    >
                      {cfg.label}
                      <button
                        type="button"
                        onClick={() => setCallReasonFilters(prev => prev.filter(x => x !== r))}
                        aria-label={`Grund ${cfg.label} entfernen`}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: 'inherit',
                          cursor: 'pointer',
                          padding: 0,
                          fontSize: '1.05rem',
                          lineHeight: 1,
                          opacity: 0.7
                        }}
                        onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = '0.7'; }}
                      >×</button>
                    </span>
                  );
                })}
                {/* Übergabegrund chips. No colour catalogue exists for escape
                    reasons, and a second set of coloured pills would compete with
                    the Grund badges, so all of them share the neutral treatment the
                    Zuständig chips already use. */}
                {escapeFilters.map(k => {
                  const label = escapeLabelFromKey(k);
                  return (
                    <span
                      key={`escape-chip-${k}`}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '4px 10px',
                        borderRadius: 12,
                        background: '#eef0f3',
                        color: '#333',
                        fontSize: '0.8rem',
                        fontWeight: 500,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {label}
                      <button
                        type="button"
                        onClick={() => setEscapeFilters(prev => prev.filter(x => x !== k))}
                        aria-label={`Übergabegrund ${label} entfernen`}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: 'inherit',
                          cursor: 'pointer',
                          padding: 0,
                          fontSize: '1.05rem',
                          lineHeight: 1,
                          opacity: 0.7
                        }}
                        onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = '0.7'; }}
                      >×</button>
                    </span>
                  );
                })}
                {assigneeFilters.map(a => (
                  <span
                    key={`assignee-chip-${a ?? 'unassigned'}`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '4px 10px',
                      borderRadius: 12,
                      background: '#eef0f3',
                      color: '#333',
                      fontSize: '0.8rem',
                      fontWeight: 500,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {resolveAssigneeName(a)}
                    <button
                      type="button"
                      onClick={() => setAssigneeFilters(prev => prev.filter(x => x !== a))}
                      aria-label={`Zuständig ${resolveAssigneeName(a)} entfernen`}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'inherit',
                        cursor: 'pointer',
                        padding: 0,
                        fontSize: '1.05rem',
                        lineHeight: 1,
                        opacity: 0.7
                      }}
                      onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
                      onMouseLeave={e => { e.currentTarget.style.opacity = '0.7'; }}
                    >×</button>
                  </span>
                ))}
                {insuranceFilters.map(type => (
                  <span
                    key={`insurance-chip-${type}`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '4px 10px',
                      borderRadius: 12,
                      background: '#eef0f3',
                      color: '#333',
                      fontSize: '0.8rem',
                      fontWeight: 500,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {type}
                    <button
                      type="button"
                      onClick={() => setInsuranceFilters(prev => prev.filter(x => x !== type))}
                      aria-label={`Patienten hervorheben ${type} entfernen`}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'inherit',
                        cursor: 'pointer',
                        padding: 0,
                        fontSize: '1.05rem',
                        lineHeight: 1,
                        opacity: 0.7
                      }}
                      onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
                      onMouseLeave={e => { e.currentTarget.style.opacity = '0.7'; }}
                    >×</button>
                  </span>
                ))}
              </div>
            )}
            </div>

          </div>
        </div>

        <div className="tasks-heading-row">
            <div className="heading-left">
              <h2 style={{ margin: 0 }}>Aufgabenliste</h2>
              <div
                className="view-mode-switch-wrap"
                title={searchTerm !== '' ? 'Während der Suche ist nur die flache Ansicht aktiv' : undefined}
              >
                <label className={`ios-switch ${searchTerm !== '' ? 'is-disabled' : ''}`}>
                  <input
                    type="checkbox"
                    checked={effectiveViewMode === 'cluster'}
                    onChange={(e) => setViewMode(e.target.checked ? 'cluster' : 'flat')}
                    disabled={searchTerm !== ''}
                    aria-label="Ansicht: Gruppiert oder Flach"
                  />
                  <span className="ios-switch-slider" />
                </label>
                <span
                  className={`view-mode-label ${effectiveViewMode === 'cluster' ? 'active' : ''}`}
                  aria-live="polite"
                >
                  {effectiveViewMode === 'cluster' ? 'Gruppiert' : 'Flach'}
                </span>
              </div>
              {linkActive && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '4px 10px',
                    borderRadius: 12,
                    background: 'color-mix(in srgb, var(--color-secondary) 12%, #fff)',
                    color: 'var(--color-main)',
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    whiteSpace: 'nowrap'
                  }}
                >
                  {deepLink.session ? 'Nur dieser Anruf' : 'Nur diese Aufgabe'}
                  <button
                    type="button"
                    onClick={clearDeepLink}
                    aria-label="Anruf-Filter entfernen"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'inherit',
                      cursor: 'pointer',
                      padding: 0,
                      fontSize: '1.05rem',
                      lineHeight: 1,
                      opacity: 0.7
                    }}
                    onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
                    onMouseLeave={e => { e.currentTarget.style.opacity = '0.7'; }}
                  >×</button>
                </span>
              )}
            </div>
            <div className="heading-search">
              <svg
                width="18"
                height="18"
                fill="none"
                stroke="#999"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                viewBox="0 0 24 24"
                style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
              >
                <circle cx="11" cy="11" r="8"/>
                <path d="M21 21l-4.35-4.35"/>
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Suchen..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                onKeyDown={handleSearchKeyDown}
              />
              {/* Result count when searching, otherwise show shortcut hint */}
              {searchTerm ? (
                <span className="search-result-count" title={`${filteredTasks.length} Treffer`}>
                  {filteredTasks.length}
                </span>
              ) : (
                <span className="search-shortcut-hint">{isMac ? '⌘K' : 'Strg+K'}</span>
              )}
              {searchTerm && (
                <svg
                  width="16"
                  height="16"
                  fill="none"
                  stroke="#999"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  viewBox="0 0 24 24"
                  onClick={() => { setSearchTerm(''); searchInputRef.current?.focus(); }}
                  style={{
                    position: 'absolute',
                    right: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.stroke = '#666'}
                  onMouseLeave={(e) => e.currentTarget.style.stroke = '#999'}
                >
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              )}
            </div>
            <div className="heading-right">
              {/* Creating a task POSTs to ONE practice, and while several are
                  pooled there is no practice on screen to mean. Hidden rather than
                  guessed at (the Dashboard hides its Empfang widget for the same
                  reason); switching back to a single practice is one click. */}
              {!allPractices && (
                <button
                  className="button add-button"
                  onClick={handleAddNew}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  + Neue Aufgabe
                </button>
              )}
            </div>
          </div>

        {isLoading ? (
          <div className="loading">Aufgaben werden geladen...</div>
        ) : (
          <div className="tasks-list">
            {tasks.length === 0 ? (
              <div className="no-tasks">Keine Aufgaben gefunden. Fügen Sie eine neue Aufgabe hinzu, um zu beginnen.</div>
            ) : (
              <div className="table-wrapper">
                <table className="tasks-table" style={{ tableLayout: 'fixed', minWidth: 1250, width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ width: '210px', minWidth: '210px' }}>Anrufer</th>
                    <th style={{ width: '130px', minWidth: '130px' }}>Grund</th>
                    <th style={{ width: '41%' }}>Beschreibung</th>
                    <th style={{ width: '7%' }}>Datum</th>
                    <th style={{ width: '11%' }}>Zuständig</th>
                    <th style={{ width: '160px', minWidth: '160px', paddingLeft: '1.5rem' }}>Status</th>
                    <th style={{ width: extraActions ? `${actionsWidth + 32}px` : '11%', textAlign: 'right' }}>
                      <span style={{ display: 'inline-block', width: actionsWidth, textAlign: 'left' }}>Aktionen</span>
                    </th>
                    {userIsAdmin && <th style={{ width: '60px' }}>Stats</th>}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const renderTaskRow = (task, opts = {}) => {
                      const {
                        rowKey,
                        rowClassName = '',
                        rowStyle = null,
                        nameCellPrefix = null,
                        nameCellExtras = null,
                        altNamesOverride = null,
                      } = opts;
                      const altNames = altNamesOverride !== null
                        ? altNamesOverride
                        : (Array.isArray(task.callerName) ? task.callerName.slice(1) : []);
                      const callStart = callStartOf(task);
                      const persons = parseCallPersons(task.description,
                        Array.isArray(task.callerName) ? task.callerName[0] : task.callerName);
                      return (
                    <tr
                      key={rowKey || task.id}
                      data-task-id={task.id}
                      className={`${rowClassName} ${activeRowId === task.id ? 'is-keyboard-active' : ''} ${linkedTaskId === task.id ? 'task-row-linked' : ''}`.trim()}
                      style={rowStyle || undefined}
                      onClick={(e) => {
                        // Slide-over no longer opens via row click — all editing is inline.
                        // The only purpose of the row click is to dismiss any open dropdown
                        // / inline editor when clicking on a non-interactive area.
                        if (!shouldOpenSlideover(e)) return;
                        if (editingStatusId || editingDescriptionId || editingFieldKey || editingAssigneeId) {
                          setEditingStatusId(null);
                          setEditingDescriptionId(null);
                          setEditingFieldKey(null);
                          setEditingAssigneeId(null);
                        }
                      }}
                      onMouseEnter={() => setActiveRowId(task.id)}
                    >
                      <td>
                        <div className="caller-cell">
                          {(task.callerBirthdate || editingFieldKey === `${task.id}:birthdate`) && (
                            <div className="caller-birthdate-eyebrow">
                              {editingFieldKey === `${task.id}:birthdate` ? (
                                <input
                                  type="date"
                                  className="caller-inline-edit"
                                  data-no-row-click
                                  autoFocus
                                  value={editingFieldDraft}
                                  onChange={(e) => setEditingFieldDraft(e.target.value)}
                                  onBlur={() => saveFieldInline(task.id, 'callerBirthdate', editingFieldDraft)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') { e.preventDefault(); saveFieldInline(task.id, 'callerBirthdate', editingFieldDraft); }
                                    else if (e.key === 'Escape') { e.preventDefault(); setEditingFieldKey(null); }
                                  }}
                                />
                              ) : (
                                <>
                                  <span
                                    className="caller-clickable"
                                    data-no-row-click
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingFieldDraft(task.callerBirthdate ? String(task.callerBirthdate).substr(0, 10) : '');
                                      setEditingFieldKey(`${task.id}:birthdate`);
                                    }}
                                    title="Klick zum Bearbeiten"
                                  >
                                    {formatBirthdate(task.callerBirthdate)}
                                  </span>
                                  {copiedItem === `${task.id}:birthdate` ? (
                                    <span className="caller-copied-feedback">Kopiert</span>
                                  ) : (
                                    <button
                                      type="button"
                                      className="caller-copy-btn"
                                      data-tooltip="Kopieren"
                                      aria-label="Geburtsdatum kopieren"
                                      onClick={() => handleCopy(formatBirthdate(task.callerBirthdate), `${task.id}:birthdate`)}
                                    >
                                      {actionIcons.copy}
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                          <div className="caller-name-line">
                            {editingFieldKey === `${task.id}:name` ? (
                              <input
                                type="text"
                                className="caller-inline-edit caller-name-edit"
                                data-no-row-click
                                autoFocus
                                value={editingFieldDraft}
                                onChange={(e) => setEditingFieldDraft(e.target.value)}
                                onBlur={() => saveFieldInline(task.id, 'callerName', editingFieldDraft)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') { e.preventDefault(); saveFieldInline(task.id, 'callerName', editingFieldDraft); }
                                  else if (e.key === 'Escape') { e.preventDefault(); setEditingFieldKey(null); }
                                }}
                              />
                            ) : (() => {
                              const fullName = Array.isArray(task.callerName) ? task.callerName[0] : task.callerName;
                              const indicatorEl = task.isExistingPatient ? (
                                <span className="name-pvs-indicator" data-tooltip="Bestandspatient">!</span>
                              ) : (altNames.length > 0 ? (
                                <span className="name-alt-indicator" data-tooltip={`Alternativ: ${altNames.join(', ')}`}>!</span>
                              ) : null);
                              const startEdit = (e) => {
                                e.stopPropagation();
                                setEditingFieldDraft(fullName && fullName !== '-' ? fullName : '');
                                setEditingFieldKey(`${task.id}:name`);
                              };
                              if (!fullName || fullName === '-') {
                                const emptyShowTrigger = !(
                                  editingFieldKey === `${task.id}:name` ||
                                  editingFieldKey === `${task.id}:phone` ||
                                  editingFieldKey === `${task.id}:birthdate`
                                );
                                const emptyTriggerEl = emptyShowTrigger ? (
                                  <button
                                    type="button"
                                    ref={(el) => {
                                      if (el) callerPopoverTriggerRefs.current[task.id] = el;
                                      else delete callerPopoverTriggerRefs.current[task.id];
                                    }}
                                    className="caller-popover-trigger"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setOpenCallerPopoverId(prev => prev === task.id ? null : task.id);
                                    }}
                                    data-no-row-click
                                    aria-label="Anruferdaten anzeigen"
                                    data-tooltip="Details"
                                  >
                                    {actionIcons.user}
                                  </button>
                                ) : null;
                                return (
                                  <span className="caller-name">
                                    <span className="caller-clickable" data-no-row-click onClick={startEdit} title="Klick zum Bearbeiten">
                                      {highlightMatch(fullName, searchTerms)}
                                    </span>
                                    <span style={{ whiteSpace: 'nowrap' }}>{indicatorEl}{emptyTriggerEl}</span>
                                  </span>
                                );
                              }
                              const lastSpaceIdx = fullName.lastIndexOf(' ');
                              const head = lastSpaceIdx >= 0 ? fullName.slice(0, lastSpaceIdx + 1) : '';
                              const tail = lastSpaceIdx >= 0 ? fullName.slice(lastSpaceIdx + 1) : fullName;
                              const headHL = highlightMatch(head, searchTerms);
                              const tailHL = highlightMatch(tail, searchTerms);
                              const copyEl = copiedItem === `${task.id}:name` ? (
                                <span className="caller-copied-feedback">Kopiert</span>
                              ) : (
                                <button
                                  type="button"
                                  className="caller-copy-btn"
                                  data-tooltip="Kopieren"
                                  aria-label="Name kopieren"
                                  onClick={() => handleCopy(fullName, `${task.id}:name`)}
                                >
                                  {actionIcons.copy}
                                </button>
                              );
                              const showPopoverTrigger = !(
                                editingFieldKey === `${task.id}:name` ||
                                editingFieldKey === `${task.id}:phone` ||
                                editingFieldKey === `${task.id}:birthdate`
                              );
                              const popoverTriggerEl = showPopoverTrigger ? (
                                <button
                                  type="button"
                                  ref={(el) => {
                                    if (el) callerPopoverTriggerRefs.current[task.id] = el;
                                    else delete callerPopoverTriggerRefs.current[task.id];
                                  }}
                                  className="caller-popover-trigger"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenCallerPopoverId(prev => prev === task.id ? null : task.id);
                                  }}
                                  data-no-row-click
                                  aria-label="Anruferdaten anzeigen"
                                  data-tooltip="Details"
                                >
                                  {actionIcons.user}
                                </button>
                              ) : null;
                              return (
                                <span className="caller-name">
                                  {head && (
                                    <span className="caller-clickable" data-no-row-click onClick={startEdit} title="Klick zum Bearbeiten">
                                      {headHL}
                                    </span>
                                  )}
                                  {/* Tail (last word) + indicators + icons share one nowrap unit
                                      so the icons never get orphaned onto their own line. */}
                                  <span style={{ whiteSpace: 'nowrap' }}>
                                    <span className="caller-clickable" data-no-row-click onClick={startEdit} title="Klick zum Bearbeiten">
                                      {tailHL}
                                    </span>
                                    {indicatorEl}{copyEl}{popoverTriggerEl}
                                  </span>
                                </span>
                              );
                            })()}
                          </div>
                          {persons.cardGuardian && renderGuardianLine(persons.cardGuardian)}
                          {/* Which practice this row came from. Only while several are
                              pooled — with one practice on screen the page header
                              already says which, and the badge would be noise. */}
                          {allPractices && (
                            <div className="task-practice-badge">
                              {practiceNameById.get(task.practiceId) || 'Unbekannte Praxis'}
                            </div>
                          )}
                          {(() => {
                            // Two-number display: the number the patient GAVE (🗣️,
                            // editable — this is task.phoneNumber) and, only when it
                            // meaningfully differs, the ORIGINAL caller ID (📞, read-only).
                            // When they match (or no caller ID exists) a single 📞 line shows.
                            // When no real number was given (phone_number is the
                            // "Nicht angegeben" placeholder) but a genuine caller ID
                            // exists, fall back to that caller ID as the single line — so
                            // trust-OFF + hangup renders identically to trust-ON instead of
                            // the "🗣️ Nicht angegeben  +  📞 <number>" two-liner.
                            // Only a real, digit-bearing caller ID is displayable. Raw
                            // anonymous placeholders ("anonymous"/"unknown"/…) — written
                            // unguarded into caller_id_number by pre-7d5da5a rows — must
                            // never surface as the number; they fall back to "Nicht
                            // angegeben" (German) like any call with no usable number.
                            const hasCallerId = isClusterablePhone(task.callerIdNumber);
                            const realPhone = isClusterablePhone(task.phoneNumber) ? task.phoneNumber : null;
                            const displayNumber = realPhone || (hasCallerId ? task.callerIdNumber : null);
                            const callerIdIsDifferent = realPhone ? callerIdDiffers(task.callerIdNumber, realPhone) : false;
                            const givenTag = callerIdIsDifferent ? '🗣️' : '📞';
                            const givenTip = callerIdIsDifferent
                              ? 'Vom Patienten angegebene Rückrufnummer'
                              : (hasCallerId ? 'Anrufer-ID (Nummer, von der angerufen wurde)' : 'Telefonnummer');
                            // 📱 = the mobile used as the SMS/WhatsApp target — spoken by
                            // the caller or adopted from the patient's PVS profile
                            // (task.pvsMobileNumber). Placement:
                            //  · no other displayable number → it REPLACES the "Nicht
                            //    angegeben" placeholder as the primary line (anonymous
                            //    caller, mobile pulled from the profile).
                            //  · a caller-ID / given number is already shown → it stacks
                            //    BENEATH the existing line(s).
                            // Skipped when it duplicates a number already on screen (same
                            // last-9 significant digits, via callerIdDiffers).
                            const pvsMobile = isClusterablePhone(task.pvsMobileNumber) ? task.pvsMobileNumber : null;
                            const shownNumbers = [];
                            if (displayNumber && displayNumber !== '-') shownNumbers.push(displayNumber);
                            if (callerIdIsDifferent) shownNumbers.push(task.callerIdNumber);
                            const showPvsLine = !!pvsMobile && !shownNumbers.some((n) => !callerIdDiffers(pvsMobile, n));
                            const pvsMobileLine = pvsMobile ? (
                              <div className="caller-phone caller-phone--pvs">
                                <span className="caller-phone-tag caller-phone-tag--icon" role="img" aria-label="aus PVS-Profil" data-tooltip="aus PVS-Profil" data-no-row-click>{actionIcons.mobile}</span>
                                <span className="caller-phone-callerid-value">{highlightMatch(pvsMobile, searchTerms)}</span>
                                <a
                                  className="caller-copy-btn"
                                  href={phoneToTelHref(pvsMobile)}
                                  data-tooltip="Anrufen"
                                  aria-label="PVS-Mobilnummer anrufen"
                                  data-no-row-click
                                >
                                  {actionIcons.phoneCall}
                                </a>
                                {copiedItem === `${task.id}:pvsmobile` ? (
                                  <span className="caller-copied-feedback">Kopiert</span>
                                ) : (
                                  <button
                                    type="button"
                                    className="caller-copy-btn"
                                    data-tooltip="Kopieren"
                                    aria-label="PVS-Mobilnummer kopieren"
                                    onClick={() => handleCopy(pvsMobile, `${task.id}:pvsmobile`)}
                                  >
                                    {actionIcons.copy}
                                  </button>
                                )}
                              </div>
                            ) : null;
                            return (
                            <div className="caller-phones">
                          {editingFieldKey === `${task.id}:phone` ? (
                            <div className="caller-phone">
                              <input
                                type="tel"
                                className="caller-inline-edit"
                                data-no-row-click
                                autoFocus
                                value={editingFieldDraft}
                                onChange={(e) => setEditingFieldDraft(e.target.value)}
                                onBlur={() => saveFieldInline(task.id, 'phoneNumber', editingFieldDraft)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') { e.preventDefault(); saveFieldInline(task.id, 'phoneNumber', editingFieldDraft); }
                                  else if (e.key === 'Escape') { e.preventDefault(); setEditingFieldKey(null); }
                                }}
                              />
                            </div>
                          ) : displayNumber && displayNumber !== '-' ? (
                            <div className="caller-phone">
                              <span className="caller-phone-tag" role="img" aria-label={givenTip} data-tooltip={givenTip} data-no-row-click>{givenTag}</span>
                              <span
                                className="caller-clickable"
                                data-no-row-click
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingFieldDraft(realPhone || '');
                                  setEditingFieldKey(`${task.id}:phone`);
                                }}
                                title="Klick zum Bearbeiten"
                              >
                                {highlightMatch(displayNumber, searchTerms)}
                              </span>
                              <a
                                className="caller-copy-btn"
                                href={phoneToTelHref(displayNumber)}
                                data-tooltip="Anrufen"
                                aria-label="Anrufen"
                                data-no-row-click
                              >
                                {actionIcons.phoneCall}
                              </a>
                              {copiedItem === `${task.id}:phone` ? (
                                <span className="caller-copied-feedback">Kopiert</span>
                              ) : (
                                <button
                                  type="button"
                                  className="caller-copy-btn"
                                  data-tooltip="Kopieren"
                                  aria-label="Telefonnummer kopieren"
                                  onClick={() => handleCopy(displayNumber, `${task.id}:phone`)}
                                >
                                  {actionIcons.copy}
                                </button>
                              )}
                            </div>
                          ) : showPvsLine ? (
                            pvsMobileLine
                          ) : (
                            <div className="caller-phone">
                              <span className="caller-phone-tag" role="img" aria-label={givenTip} data-tooltip={givenTip} data-no-row-click>{givenTag}</span>
                              <span
                                className="caller-clickable"
                                data-no-row-click
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingFieldDraft('');
                                  setEditingFieldKey(`${task.id}:phone`);
                                }}
                                title="Klick zum Hinzufügen"
                                style={{ color: '#bbb', fontStyle: 'italic' }}
                              >
                                {task.phoneNumber || 'Telefon hinzufügen'}
                              </span>
                            </div>
                          )}
                            {callerIdIsDifferent && (
                              <div className="caller-phone caller-phone--callerid">
                                <span
                                  className="caller-phone-tag"
                                  role="img"
                                  aria-label="Anrufer-ID (Nummer, von der angerufen wurde)"
                                  data-tooltip="Anrufer-ID (Nummer, von der angerufen wurde)"
                                  data-no-row-click
                                >📞</span>
                                <span className="caller-phone-callerid-value">{highlightMatch(task.callerIdNumber, searchTerms)}</span>
                                <a
                                  className="caller-copy-btn"
                                  href={phoneToTelHref(task.callerIdNumber)}
                                  data-tooltip="Anrufen"
                                  aria-label="Anrufer-ID anrufen"
                                  data-no-row-click
                                >
                                  {actionIcons.phoneCall}
                                </a>
                                {copiedItem === `${task.id}:callerid` ? (
                                  <span className="caller-copied-feedback">Kopiert</span>
                                ) : (
                                  <button
                                    type="button"
                                    className="caller-copy-btn"
                                    data-tooltip="Kopieren"
                                    aria-label="Anrufer-ID kopieren"
                                    onClick={() => handleCopy(task.callerIdNumber, `${task.id}:callerid`)}
                                  >
                                    {actionIcons.copy}
                                  </button>
                                )}
                              </div>
                            )}
                            {displayNumber && displayNumber !== '-' && showPvsLine && pvsMobileLine}
                            {task.callerEmail && (
                              <div className="caller-phone caller-phone--email">
                                <span className="caller-phone-tag caller-phone-tag--icon" role="img" aria-label="E-Mail-Adresse" data-tooltip="E-Mail-Adresse" data-no-row-click>{actionIcons.mailTag}</span>
                                <span className="caller-phone-callerid-value">{highlightMatch(task.callerEmail, searchTerms)}</span>
                                <a
                                  className="caller-copy-btn"
                                  href={`mailto:${task.callerEmail}`}
                                  data-tooltip="E-Mail schreiben"
                                  aria-label="E-Mail schreiben"
                                  data-no-row-click
                                >
                                  {actionIcons.mailSend}
                                </a>
                                {copiedItem === `${task.id}:email` ? (
                                  <span className="caller-copied-feedback">Kopiert</span>
                                ) : (
                                  <button
                                    type="button"
                                    className="caller-copy-btn"
                                    data-tooltip="Kopieren"
                                    aria-label="E-Mail-Adresse kopieren"
                                    onClick={() => handleCopy(task.callerEmail, `${task.id}:email`)}
                                  >
                                    {actionIcons.copy}
                                  </button>
                                )}
                              </div>
                            )}
                            </div>
                            );
                          })()}
                          {renderCallPersons(persons)}
                          {(nameCellPrefix || nameCellExtras) && (
                            <div className="caller-cluster-meta">
                              {nameCellPrefix}
                              {nameCellExtras}
                            </div>
                          )}
                          {openCallerPopoverId === task.id && callerPopoverRect && (() => {
                            const rows = [];
                            // Name (all variations if multiple)
                            const allNames = Array.isArray(task.callerName)
                              ? task.callerName.filter(n => n && n !== '__pvs__')
                              : (task.callerName ? [task.callerName] : []);
                            if (allNames.length > 0) {
                              rows.push(['Name', allNames.join(' / ')]);
                            }
                            if (task.isExistingPatient) rows.push(['Status', 'Bestandspatient']);
                            if (task.callerBirthdate) rows.push(['Geburtsdatum', formatBirthdate(task.callerBirthdate)]);
                            // Mirror the cell's display logic: only a real given number
                            // is "Telefon"; a "Nicht angegeben" placeholder falls through so
                            // the genuine caller ID shows once as "Anrufnummer".
                            const popoverRealPhone = isClusterablePhone(task.phoneNumber) ? task.phoneNumber : null;
                            if (popoverRealPhone) rows.push(['Telefon', popoverRealPhone]);
                            if (callerIdDiffers(task.callerIdNumber, popoverRealPhone)) { rows.push(['Anrufnummer', task.callerIdNumber]); }
                            // The mobile used as the SMS/WhatsApp target — spoken by the
                            // caller or adopted from the PVS profile. Same no-overlap dedup
                            // as the under-name line: show only when it's a real number
                            // that differs from both the given "Telefon" and the caller-ID.
                            if (isClusterablePhone(task.pvsMobileNumber)
                                && callerIdDiffers(task.pvsMobileNumber, popoverRealPhone)
                                && callerIdDiffers(task.pvsMobileNumber, task.callerIdNumber)) {
                              rows.push(['Mobilnummer', task.pvsMobileNumber]);
                            }
                            if (task.callerEmail) rows.push(['E-Mail', task.callerEmail]);
                            if (task.callerWhatsappOptin !== null && task.callerWhatsappOptin !== undefined) {
                              rows.push(['WhatsApp', task.callerWhatsappOptin ? 'Ja' : 'Nein']);
                            }
                            if (task.hasVerordnung === true) {
                              const verordnungParts = [];
                              if (task.verordnungType) verordnungParts.push(task.verordnungType);
                              if (task.verordnungSessions) verordnungParts.push(`${task.verordnungSessions} Sitzungen`);
                              rows.push(['Verordnung', verordnungParts.length ? verordnungParts.join(', ') : 'Ja']);
                              if (task.verordnungIssueDate) rows.push(['Ausgestellt', formatIssueDate(task.verordnungIssueDate)]);
                            } else if (task.hasVerordnung === false) {
                              rows.push(['Verordnung', 'Nein']);
                            }
                            if (task.insuranceType) rows.push(['Verordnungstyp', task.insuranceType]);
                            // Terminwunsch: caller's stated time preference (read-only); hidden when empty.
                            if (task.appointmentPreference) rows.push(['Terminwunsch', task.appointmentPreference]);
                            // Frequenz: recurring-booking cadence, carried as a hidden action line in the
                            // description (shown only here, filtered out of the visible Aktionen receipt).
                            const hiddenNotes = parseActionSummary(task.description).notes;
                            const freqNote = hiddenNotes.find(n => n.caption === 'Frequenz');
                            if (freqNote && freqNote.detail) rows.push(['Frequenz', freqNote.detail]);
                            // Anliegen: what the caller described when a treatment was expected, carried as a
                            // hidden action line in the description (same idiom as Frequenz above).
                            const anliegenNote = hiddenNotes.find(n => n.caption === 'Anliegen');
                            if (anliegenNote && anliegenNote.detail) rows.push(['Anliegen', anliegenNote.detail]);

                            // Viewport-aware positioning. Estimate popover height
                            // from row count (~22px each + ~26px padding); flip
                            // below the trigger if there isn't enough room above.
                            const r = callerPopoverRect;
                            const estimatedHeight = Math.max(60, rows.length * 22 + 26);
                            const spaceAbove = r.top;
                            const spaceBelow = window.innerHeight - r.bottom;
                            const openUpward = spaceAbove >= estimatedHeight + 12 || spaceAbove >= spaceBelow;
                            const popoverStyle = openUpward
                              ? { top: Math.max(8, r.top - estimatedHeight - 6), left: r.left }
                              : { top: r.bottom + 6, left: r.left };
                            // Keep horizontal position inside viewport (max-width is 460px).
                            const maxLeft = Math.max(8, window.innerWidth - 460 - 8);
                            if (popoverStyle.left > maxLeft) popoverStyle.left = maxLeft;
                            if (popoverStyle.left < 8) popoverStyle.left = 8;

                            return createPortal(
                              <div className="caller-popover" role="tooltip" style={popoverStyle}>
                                {rows.length === 0 ? (
                                  <div className="caller-popover-empty">Keine Daten erfasst</div>
                                ) : rows.map(([label, value], i) => (
                                  <div key={i} className="caller-popover-row">
                                    <span className="caller-popover-label">{label}</span>
                                    <span className="caller-popover-value">{value}</span>
                                  </div>
                                ))}
                              </div>,
                              document.body
                            );
                          })()}
                        </div>
                      </td>
                      <td>
                        {task.callReasons && task.callReasons.length > 0
                          ? <div className="grund-badge-cluster">{renderCallReasonBadges(task.callReasons)}</div>
                          : <span style={{ color: '#999' }}>—</span>}
                      </td>
                      <td style={{ wordBreak: 'break-word' }} className="description-cell">
                        {editingDescriptionId === task.id ? (
                          <div style={{ position: 'relative' }}>
                            <textarea
                              ref={(el) => {
                                if (!el) return;
                                if (el.dataset.descInited === '1') return;
                                el.dataset.descInited = '1';
                                const len = el.value.length;
                                const pos = descriptionInitialCaret == null
                                  ? len
                                  : Math.max(0, Math.min(descriptionInitialCaret, len));
                                el.focus({ preventScroll: true });
                                try { el.setSelectionRange(pos, pos); } catch { /* noop */ }
                                // Auto-fit to the full prose on OPEN so nothing is clipped.
                                // The captured prose height is a starting point; the
                                // textarea's own box (padding/line-height) can need a hair
                                // more, so grow to scrollHeight — matches the read view,
                                // no clip, no scrollbar (overflow:hidden on the class).
                                el.style.height = 'auto';
                                el.style.height = el.scrollHeight + 'px';
                              }}
                              value={descriptionDraft}
                              style={descriptionInitialHeight ? { height: `${descriptionInitialHeight}px` } : undefined}
                              onChange={(e) => setDescriptionDraft(e.target.value)}
                              onInput={(e) => {
                                e.target.style.height = 'auto';
                                e.target.style.height = e.target.scrollHeight + 'px';
                              }}
                              onClick={(e) => e.stopPropagation()}
                              onBlur={() => saveDescriptionInline(task.id)}
                              onKeyDown={(e) => {
                                if (e.key === 'Escape') {
                                  e.preventDefault();
                                  setEditingDescriptionId(null);
                                  setDescriptionInitialHeight(null);
                                  setDescriptionInitialCaret(null);
                                } else if (e.key === 'Enter' && !e.shiftKey) {
                                  // Enter (alone or with Cmd/Ctrl) saves; Shift+Enter inserts a newline.
                                  e.preventDefault();
                                  saveDescriptionInline(task.id);
                                }
                              }}
                              data-no-row-click
                              className="description-inline-edit"
                            />
                            {(() => {
                              // Read-only receipt stays visible while editing the prose —
                              // the ✅/⚠️ block is machine-generated and non-editable.
                              const { notes } = parseActionSummary(task.description);
                              return notes.length > 0
                                ? renderActionNotesGroup(notes, {
                                    terms: searchTerms, className: 'action-notes--edit',
                                    smsTarget: smsTargetForTask(task),
                                    sentMessages: task.sentMessages,
                                    onOpenMessage: (id, el) => openSentMessage(task, id, el),
                                    processingLines: inFlightLines(task),
                                  })
                                : null;
                            })()}
                            <div className="description-edit-hint" data-no-row-click>
                              <kbd>Enter</kbd> speichern · <kbd>Shift</kbd>+<kbd>Enter</kbd> Zeilenumbruch · <kbd>Esc</kbd> abbrechen
                            </div>
                          </div>
                        ) : (
                          <div
                            className="description-readonly"
                            onClick={(e) => {
                              e.stopPropagation();
                              const container = e.currentTarget;
                              // Size the textarea to the PROSE portion only (the receipt
                              // renders separately below) so the read↔edit toggle barely
                              // shifts. Prose present → its height; block-only (no prose,
                              // just a receipt) → natural textarea height (null); plain
                              // text with no block → full cell height.
                              const proseEl = container.querySelector('.description-prose');
                              const notesEl = container.querySelector('.action-notes');
                              setDescriptionInitialHeight(
                                proseEl ? proseEl.offsetHeight : (notesEl ? null : container.offsetHeight)
                              );
                              // Compute character offset at click coordinates.
                              let caret = null;
                              try {
                                let pos;
                                if (document.caretPositionFromPoint) {
                                  pos = document.caretPositionFromPoint(e.clientX, e.clientY);
                                  if (pos) pos = { node: pos.offsetNode, offset: pos.offset };
                                } else if (document.caretRangeFromPoint) {
                                  const r = document.caretRangeFromPoint(e.clientX, e.clientY);
                                  if (r) pos = { node: r.startContainer, offset: r.startOffset };
                                }
                                if (pos && pos.node && container.contains(pos.node)) {
                                  const range = document.createRange();
                                  range.setStart(container, 0);
                                  range.setEnd(pos.node, pos.offset);
                                  caret = range.toString().length;
                                }
                              } catch { /* ignore — fallback to end of text */ }
                              setDescriptionInitialCaret(caret);
                              // Edit PROSE ONLY — notes + action block spliced back on save.
                              const { prose } = parseDescriptionZones(task.description);
                              setDescriptionDraft(prose && prose !== '-' ? prose : '');
                              setEditingDescriptionId(task.id);
                            }}
                            data-no-row-click
                            title="Klick zum Bearbeiten"
                          >
                            {renderTaskDescription(task)}
                          </div>
                        )}
                        {/* To-dos, the call log and 📌 notes render below the read box
                            (hidden during prose edit so the absolute edit-hint chip
                            can't overlap them). */}
                        {editingDescriptionId !== task.id && renderTaskTodos(task)}
                        {editingDescriptionId !== task.id && renderCallLog(task)}
                        {editingDescriptionId !== task.id && renderTaskNotes(task)}
                      </td>
                      <td>
                        {callStart ? (
                          <>
                            <div>{new Date(callStart).toLocaleDateString('de-DE')}</div>
                            <div style={{ fontSize: '0.85rem', color: '#666' }}>
                              {formatClock(callStart)}
                            </div>
                          </>
                        ) : ''}
                      </td>
                      <td>
                        {(() => {
                          // assignableFor, not the union: while several practices
                          // are pooled this row may only offer ITS OWN practice's
                          // members (see the helper).
                          const sortedUsers = [...assignableFor(task)].sort((a, b) => (a.firstName || '').localeCompare(b.firstName || ''));
                          // Label off the FULL list: a task assigned before its
                          // assignee was made non-assignable still shows the name
                          // (the dropdown just no longer offers that person).
                          const current = allMembers.find(u => u.id === task.assigneeId);
                          const currentLabel = current ? (`${current.firstName || ''} ${current.lastName || ''}`.trim() || current.username) : '';
                          const isOpen = editingAssigneeId === task.id;
                          return (
                            <>
                              <button
                                type="button"
                                className={`assignee-trigger${isOpen ? ' is-open' : ''}`}
                                data-no-row-click
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingAssigneeId(prev => prev === task.id ? null : task.id);
                                }}
                              >
                                <span className={current ? '' : 'placeholder'}>
                                  {currentLabel || '-'}
                                </span>
                                <svg className="chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="6 9 12 15 18 9"/>
                                </svg>
                              </button>
                              {isOpen && (
                                <>
                                  <div
                                    style={{ position: 'fixed', inset: 0, zIndex: 999 }}
                                    onClick={() => setEditingAssigneeId(null)}
                                  />
                                  <div
                                    className="assignee-panel"
                                    data-no-row-click
                                    style={{ position: 'fixed', top: assigneeDropdownPos.top, left: assigneeDropdownPos.left, minWidth: assigneeDropdownPos.width }}
                                  >
                                    <div
                                      className={`assignee-option${!task.assigneeId ? ' is-active' : ''}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleAssigneeChange(task.id, '');
                                        setEditingAssigneeId(null);
                                      }}
                                    >
                                      <svg className="check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12"/>
                                      </svg>
                                      <span style={{ color: '#999', fontStyle: 'italic' }}>-</span>
                                    </div>
                                    {sortedUsers.map(user => {
                                      const label = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username;
                                      const active = task.assigneeId === user.id;
                                      return (
                                        <div
                                          key={user.id}
                                          className={`assignee-option${active ? ' is-active' : ''}`}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleAssigneeChange(task.id, user.id);
                                            setEditingAssigneeId(null);
                                          }}
                                        >
                                          <svg className="check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="20 6 9 17 4 12"/>
                                          </svg>
                                          <span>{label}</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </>
                              )}
                            </>
                          );
                        })()}
                      </td>
                      <td style={{ paddingLeft: '1.5rem' }}>
                        {/* Custom status dropdown with badge styling */}
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                          <span
                            className={`status-badge ${getStatusClassName(shownStatus(task))}`}
                            onClick={(e) => {
                              if (editingStatusId === task.id) {
                                setEditingStatusId(null);
                              } else {
                                const rect = e.currentTarget.getBoundingClientRect();
                                const dropdownHeight = 144;
                                const spaceBelow = window.innerHeight - rect.bottom;
                                const openUpward = spaceBelow < dropdownHeight + 8;
                                setStatusDropdownPos({
                                  top: openUpward ? rect.top - dropdownHeight : rect.bottom + 4,
                                  left: rect.left
                                });
                                setEditingStatusId(task.id);
                              }
                            }}
                            style={{ cursor: 'pointer' }}
                            tabIndex={0}
                            aria-label="Status ändern"
                            data-no-row-click
                          >
                            {renderStatusLabel(getGermanStatus(shownStatus(task)), { verticalAlign: '-4px' })}
                          </span>

                          {editingStatusId === task.id && (
                            <>
                              {/* Invisible overlay to catch outside clicks */}
                              <div
                                style={{
                                  position: 'fixed',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  zIndex: 999
                                }}
                                onClick={() => setEditingStatusId(null)}
                              />
                              {/* Dropdown options - fixed position to avoid overflow clipping */}
                              <div style={{
                                position: 'fixed',
                                top: statusDropdownPos.top,
                                left: statusDropdownPos.left,
                                background: '#fff',
                                borderRadius: 8,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                padding: 6,
                                zIndex: 1000,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 4,
                                minWidth: 130
                              }}>
                                {(() => {
                                  // Show "Alle abschließen" entry only when the phone clusters
                                  // (i.e. has 2+ non-terminal tasks on the same number).
                                  const key = clusterKeyOf(task);
                                  if (!key) return null;
                                  const openCount = tasks.filter(t =>
                                    clusterKeyOf(t) === key && isBulkCompletable(t.status)
                                  ).length;
                                  if (openCount < 2) return null;
                                  return (
                                    <>
                                      <span
                                        className="status-badge completed"
                                        title="Alle Aufgaben dieser Person abschließen"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleCompleteAllForPhone(task.id);
                                          setEditingStatusId(null);
                                        }}
                                        style={{
                                          cursor: 'pointer',
                                          opacity: 1,
                                          transition: 'transform 0.15s ease',
                                          textAlign: 'center',
                                        }}
                                        onMouseEnter={(e) => { e.target.style.transform = 'scale(1.02)'; }}
                                        onMouseLeave={(e) => { e.target.style.transform = 'scale(1)'; }}
                                      >
                                        Alle abschließen
                                      </span>
                                      <div style={{ height: 1, background: '#e5e7eb', margin: '4px 0' }} />
                                    </>
                                  );
                                })()}
                                {STATUS_CONFIG.filter(s => s.userSelectable).filter(visibleTo(isUserSuperAdmin)).map(option => {
                                  const isActive = task.status === option.value ||
                                    (task.status === 'completed_by_deskbot' && option.value === 'completed');
                                  return (
                                  <span
                                    key={option.value}
                                    className={`status-badge ${option.className}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleStatusChange(task.id, option.value);
                                      setEditingStatusId(null);
                                    }}
                                    style={{
                                      cursor: 'pointer',
                                      opacity: 1,
                                      transform: isActive ? 'scale(1.02)' : 'scale(1)',
                                      transition: 'all 0.15s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.transform = 'scale(1.02)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.transform = isActive ? 'scale(1.02)' : 'scale(1)';
                                    }}
                                  >
                                    {option.label}
                                  </span>
                                  );
                                })}
                              </div>
                            </>
                          )}
                        </div>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', width: actionsWidth, gap: '4px', alignItems: 'center', justifyContent: confirmingDeleteId === task.id ? 'flex-end' : 'flex-start', flexWrap: 'nowrap', marginLeft: -8 }}>
                          {confirmingDeleteId !== task.id && (
                            <>
                              {(() => {
                                // A missed-call row stands for several calls: its
                                // transcripts open per call in "Anrufe heute". A message on
                                // the Digital-AB has no session but its own transcript.
                                const noTranscript = task.missedRow || (!task.sessionId && task.hasConsent !== true);
                                const noTranscriptText = task.missedRow && (task.callLog || []).some(e => e.conversation_id)
                                  ? 'Transkripte bei den einzelnen Anrufen'
                                  : 'Keine Aufzeichnung vorhanden';
                                const disabled = noTranscript || task.hasConsent === false;
                                return (
                                  <button
                                    className={`action-btn action-btn--recording${disabled ? ' action-btn--recording-empty' : ''}`}
                                    data-tooltip={noTranscript ? noTranscriptText : task.hasConsent === false ? "Der Anrufer hat der Aufzeichnung des Gesprächs nicht zugestimmt." : "Aufzeichnung und Transkript"}
                                    onClick={disabled ? undefined : () => handleViewConversation(task.id)}
                                    aria-label={noTranscript ? noTranscriptText : task.hasConsent === false ? "Der Anrufer hat der Aufzeichnung des Gesprächs nicht zugestimmt." : "Aufzeichnung und Transkript anzeigen"}
                                    aria-disabled={disabled}
                                  >
                                    {actionIcons.chat}
                                  </button>
                                );
                              })()}
                              <a
                                href={taskMailtoHref(task)}
                                className="action-btn action-btn--email"
                                data-tooltip="Per E-Mail weiterleiten"
                                aria-label="Per E-Mail weiterleiten"
                              >
                                {actionIcons.email}
                              </a>
                              {/* Hidden while pooled and while a delete waits for its confirmation. */}
                              {showSupportIcon && task.practiceId === practiceId && (
                                <button
                                  type="button"
                                  className="action-btn action-btn--frage action-btn--tooltip-right"
                                  data-tooltip="Problem melden: schreibt dem DeskBot-Team"
                                  aria-label="Zu diesem Vorgang dem DeskBot-Team schreiben"
                                  data-no-row-click
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openSupportForTask(task);
                                  }}
                                >
                                  {actionIcons.supportChat}
                                </button>
                              )}
                              {showTicketIcon && (
                                <button
                                  type="button"
                                  className="action-btn action-btn--ticket action-btn--tooltip-right"
                                  data-tooltip="Ticket anlegen: öffnet den Tracker mit dem Link zu diesem Anruf"
                                  aria-label="Ticket zu diesem Anruf im Tracker anlegen"
                                  data-no-row-click
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openTrackerTicket(task);
                                  }}
                                >
                                  {actionIcons.ticket}
                                </button>
                              )}
                            </>
                          )}
                          {confirmingDeleteId === task.id ? (
                            <button
                              className="action-btn action-btn--confirm-delete action-btn--tooltip-right"
                              data-tooltip="Klick zum Bestätigen"
                              onClick={() => {
                                if (confirmDeleteTimerRef.current) {
                                  clearTimeout(confirmDeleteTimerRef.current);
                                  confirmDeleteTimerRef.current = null;
                                }
                                handleDelete(task.id);
                                setConfirmingDeleteId(null);
                              }}
                              onMouseLeave={() => {
                                if (confirmDeleteTimerRef.current) clearTimeout(confirmDeleteTimerRef.current);
                                confirmDeleteTimerRef.current = setTimeout(() => setConfirmingDeleteId(null), 2500);
                              }}
                              aria-label="Löschen bestätigen"
                            >
                              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#fff', whiteSpace: 'nowrap' }}>Wirklich?</span>
                            </button>
                          ) : (
                            <button
                              className="action-btn action-btn--delete action-btn--tooltip-right"
                              data-tooltip="Löschen"
                              onClick={() => {
                                if (confirmDeleteTimerRef.current) clearTimeout(confirmDeleteTimerRef.current);
                                setConfirmingDeleteId(task.id);
                                confirmDeleteTimerRef.current = setTimeout(() => setConfirmingDeleteId(null), 4000);
                              }}
                              aria-label="Aufgabe löschen"
                            >
                              {actionIcons.delete}
                            </button>
                          )}
                        </div>
                      </td>
                      {userIsAdmin && (
                        <td style={{ fontSize: '0.8rem', lineHeight: 1.4, color: '#555' }}>
                          {task.conversationQuality && (
                            <div><strong>Q:</strong> {task.conversationQuality}/10</div>
                          )}
                          {task.perceivedLatencyMean && (
                            <div>
                              <strong>Lat:</strong> {(task.perceivedLatencyMean / 1000).toFixed(1)}s
                              {task.perceivedLatencyMax && (
                                <span style={{ color: '#888' }}> (max: {(task.perceivedLatencyMax / 1000).toFixed(1)}s)</span>
                              )}
                            </div>
                          )}
                          {isUserSuperAdmin && task.automationPct != null && (
                            <button
                              type="button"
                              className="automation-popover-trigger"
                              ref={(el) => {
                                if (el) automationTriggerRefs.current[task.id] = el;
                                else delete automationTriggerRefs.current[task.id];
                              }}
                              onClick={(e) => { e.stopPropagation(); toggleAutomationPopover(task.id); }}
                              title="Automatisierung: Schritte anzeigen"
                            >
                              <AutomationRing pct={task.automationPct} />
                              <span>{Math.round(task.automationPct)} %</span>
                            </button>
                          )}
                          {openAutomationId === task.id && automationRect && (() => {
                            const d = automationDetails[task.id];
                            const r = automationRect;
                            // Opens to the left of the trigger (the Stats column is the last one),
                            // below it when there is room, else above.
                            const style = { left: Math.max(8, r.right - 460) };
                            if (window.innerHeight - r.bottom >= 260) style.top = r.bottom + 6;
                            else style.bottom = Math.max(8, window.innerHeight - r.top + 6);
                            let body;
                            if (!d || d === 'loading') body = <div className="caller-popover-empty">Lädt …</div>;
                            else if (d === 'error') body = <div className="caller-popover-empty">Konnte nicht geladen werden</div>;
                            else if (!d.scores || d.scores.length === 0) body = <div className="caller-popover-empty">Kein Score gespeichert</div>;
                            else body = d.scores.map((sc) => {
                              const detail = sc.detail || {};
                              const steps = Array.isArray(detail.steps) ? detail.steps : [];
                              const notes = [];
                              if (d.scores.length > 1 && sc.call_date) notes.push(moment(sc.call_date).format('DD.MM.'));
                              if (detail.rueckruf && detail.rueckruf.hit) notes.push('Rückruf, Nenner 110');
                              if (Array.isArray(detail.rules_applied) && detail.rules_applied.length) notes.push(`Regel ${detail.rules_applied.join(', ')}`);
                              return (
                                <div key={sc.score_key} className="automation-score">
                                  <div className="automation-intent-head">
                                    <span>Automatisierung{notes.length ? ` · ${notes.join(' · ')}` : ''}</span>
                                    <span>{Math.round(sc.pct)} %</span>
                                  </div>
                                  {Object.entries(detail.intent_pct || {}).map(([intent, ipct]) => (
                                    <div key={intent} className="automation-intent">
                                      <div className="automation-intent-head">
                                        <span>{(d.intent_labels && d.intent_labels[intent]) || intent}</span>
                                        <span>{Math.round(ipct)} %</span>
                                      </div>
                                      {steps.filter((st) => st.intent === intent && st.state !== 'n/a').map((st, i) => (
                                        <div
                                          key={`${st.id}-${i}`}
                                          className={`automation-step automation-step--${st.state === 'config_off' ? 'off' : st.state}`}
                                          title={st.why || undefined}
                                        >
                                          <span className="automation-step-label">{st.label_de}</span>
                                          <span className="automation-step-value">
                                            {st.state === 'config_off' ? 'aus' : `${fmtAutomationPoints(st.got)} / ${fmtAutomationPoints(st.weight)}`}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  ))}
                                </div>
                              );
                            });
                            return createPortal(
                              <div className="caller-popover automation-popover" role="dialog" style={style}
                                data-no-row-click onClick={(e) => e.stopPropagation()}>
                                {body}
                              </div>,
                              document.body
                            );
                          })()}
                          {!task.conversationQuality && !task.perceivedLatencyMean
                            && !(isUserSuperAdmin && task.automationPct != null) && (
                            <span style={{ color: '#999' }}>—</span>
                          )}
                        </td>
                      )}
                    </tr>
                      );
                    };

                    const statusAnimClass = (taskId) => {
                      if (effectiveViewMode !== 'cluster') return '';
                      const newStatus = statusChangedIds.get(taskId);
                      if (!newStatus) return '';
                      return newStatus === 'completed'
                        ? 'task-row-status-changed-completed'
                        : 'task-row-status-changed';
                    };

                    return clusterRows.flatMap(row => {
                      if (row.kind === 'standalone') {
                        const classes = [
                          newTaskIds.has(row.task.id) ? 'task-row-new' : '',
                          statusAnimClass(row.task.id),
                        ].filter(Boolean).join(' ');
                        return [renderTaskRow(row.task, {
                          rowClassName: classes,
                        })];
                      }

                      const expanded = expandedClusters.has(row.clusterKey);
                      const exiting = exitingClusters.has(row.clusterKey);
                      // Animation triggers if any member task is freshly arrived
                      const hasNewMember = row.members.some(m => newTaskIds.has(m.id));
                      const clusterStatusAnim = statusAnimClass(row.visibleTask.id);

                      const caret = (
                        <button
                          type="button"
                          className="cluster-caret"
                          onClick={(e) => { e.stopPropagation(); toggleCluster(row.clusterKey); }}
                          aria-label={expanded ? 'Cluster einklappen' : 'Cluster ausklappen'}
                          aria-expanded={expanded}
                        >
                          <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                            <polyline points="4,2 9,6 4,10" />
                          </svg>
                        </button>
                      );

                      const chip = row.hiddenOpenCount > 0 ? (
                        <span className="cluster-chip cluster-chip-open">
                          {`${row.hiddenOpenCount} ausstehend ausgeblendet`}
                        </span>
                      ) : row.hiddenTotalCount > 0 ? (
                        <span className="cluster-chip cluster-chip-total">
                          {`+${row.hiddenTotalCount} weitere`}
                        </span>
                      ) : null;

                      const clusterRowClasses = [
                        hasNewMember ? 'task-row-new' : '',
                        clusterStatusAnim,
                        expanded ? 'cluster-header-row-expanded' : '',
                        exiting ? 'cluster-header-row-collapsing' : '',
                      ].filter(Boolean).join(' ');

                      const clusterRow = renderTaskRow(row.visibleTask, {
                        rowKey: `cluster-${row.clusterKey}`,
                        rowClassName: clusterRowClasses,
                        nameCellPrefix: caret,
                        nameCellExtras: chip,
                        altNamesOverride: row.altNames,
                      });

                      // Render children while expanded OR while exit-cascade is still playing
                      if (!expanded && !exiting) return [clusterRow];

                      // Expansion order: open first (DESC), then terminal (DESC); exclude visible task
                      const childMembers = row.members.filter(m => m.id !== row.visibleTask.id);
                      const openChildren = childMembers.filter(m => isOpenStatus(m.status));
                      const terminalChildren = childMembers.filter(m => !isOpenStatus(m.status));
                      const orderedChildren = [...openChildren, ...terminalChildren];

                      const childRows = orderedChildren.map((child, idx) => renderTaskRow(child, {
                        rowKey: `child-${child.id}`,
                        rowClassName: [
                          'cluster-child-row',
                          exiting ? 'cluster-child-row-exiting' : '',
                          statusAnimClass(child.id),
                        ].filter(Boolean).join(' '),
                        rowStyle: { animationDelay: `${Math.min(idx, CLUSTER_STAGGER_MAX_IDX) * CLUSTER_STAGGER_MS}ms` },
                      }));

                      return [clusterRow, ...childRows];
                    });
                  })()}
                </tbody>
              </table>
              </div>
            )}
          </div>
        )}
        
        {showForm && (
          <div
            className="task-form-modal"
            onClick={(e) => { if (e.target === e.currentTarget) setShowForm(false); }}
          >
            <div className="card task-form" onClick={(e) => e.stopPropagation()}>
              <h3>{currentTask.id ? 'Aufgabe bearbeiten' : 'Neue Aufgabe'}</h3>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="form-label">Anrufername</label>
                  <input
                    type="text"
                    name="callerName"
                    value={Array.isArray(currentTask.callerName) ? currentTask.callerName[0] || '' : currentTask.callerName || ''}
                    onChange={(e) => setCurrentTask({...currentTask, callerName: [e.target.value]})}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Telefonnummer</label>
                  <input
                    type="text"
                    name="phoneNumber"
                    value={currentTask.phoneNumber}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Geburtsdatum</label>
                  <input
                    type="date"
                    name="callerBirthdate"
                    value={currentTask.callerBirthdate ? String(currentTask.callerBirthdate).substr(0, 10) : ''}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Beschreibung</label>
                  <textarea
                    name="description"
                    value={currentTask.description}
                    onChange={handleInputChange}
                    className="form-input"
                    rows="3"
                  />
                </div>

                {(() => {
                  // Read-only, display-only preview of the action block (the textarea
                  // edits prose only; the block lives on the stashed transient field).
                  const { notes } = parseActionSummary(currentTask._descriptionActionRaw || '');
                  if (!notes.length) return null;
                  return (
                    <div className="form-group">
                      <label className="form-label">Ausgeführte Aktionen</label>
                      {renderActionNotesGroup(notes, {
                        showCaption: false, className: 'action-notes--modal',
                        smsTarget: smsTargetForTask(currentTask),
                        sentMessages: currentTask.sentMessages,
                        onOpenMessage: (id, el) => openSentMessage(currentTask, id, el),
                        processingLines: inFlightLines(currentTask),
                      })}
                    </div>
                  );
                })()}

                {(() => {
                  // To-dos of the LIVE task (currentTask is a copy taken on open),
                  // so a tick here shows at once.
                  const liveTask = currentTask.id && tasks.find(t => t.id === currentTask.id);
                  if (!liveTask || !(liveTask.todos || []).length) return null;
                  return (
                    <div className="form-group">
                      <label className="form-label">Zu tun</label>
                      {renderTaskTodos(liveTask, { terms: [], showCaption: false, className: 'action-notes--modal' })}
                    </div>
                  );
                })()}

                <div className="form-group">
                  <label className="form-label">Notizen</label>
                  {(currentTask._descriptionNotes || []).map((note, i) => (
                    <div key={i} className="modal-note-row">
                      <span className="modal-note-pin" aria-hidden="true">📌</span>
                      <div className="modal-note-main">
                        {renderNoteByline(note, 'modal-note-byline')}
                        <input
                          type="text"
                          className="form-input"
                          value={note.text || ''}
                          placeholder="Notiz…"
                          maxLength={280}
                          onChange={(e) => setCurrentTask(prev => {
                            const next = [...(prev._descriptionNotes || [])];
                            next[i] = { ...next[i], text: e.target.value };
                            return { ...prev, _descriptionNotes: next };
                          })}
                        />
                      </div>
                      {canDeleteNotes && (
                        <button
                          type="button"
                          className="modal-note-remove"
                          title="Notiz entfernen"
                          onClick={() => setCurrentTask(prev => ({
                            ...prev,
                            _descriptionNotes: (prev._descriptionNotes || []).filter((_, j) => j !== i),
                          }))}
                        >✕</button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    className="modal-note-add"
                    onClick={() => setCurrentTask(prev => ({
                      ...prev,
                      _descriptionNotes: [...(prev._descriptionNotes || []), { text: '', author: null, ts: null, _new: true }],
                    }))}
                  >+ Notiz hinzufügen</button>
                </div>

                <div className="form-group">
                  <label className="form-label">Datum</label>
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                    <input
                      type="text"
                      value={currentTask.dueDate ? moment(currentTask.dueDate).format('DD.MM.YYYY') : ''}
                      readOnly
                      className="form-input"
                      onClick={() => dueDateRef.current && dueDateRef.current.showPicker()}
                      style={{ cursor: 'pointer' }}
                    />
                    <span
                      style={{
                        position: 'absolute',
                        right: '12px',
                        cursor: 'pointer',
                        fontSize: '18px',
                        pointerEvents: 'none'
                      }}
                    >
                      📅
                    </span>
                    <input
                      type="date"
                      ref={dueDateRef}
                      name="dueDate"
                      value={currentTask.dueDate}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                      style={{ 
                        opacity: 0,
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        cursor: 'pointer'
                      }}
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    name="status"
                    value={currentTask.status}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    {/* Derived, not hardcoded: a controlled select whose value
                        matches no option silently displays option[0], so a task
                        in a status missing here would READ as "Abgeschlossen"
                        and one stray click would downgrade it. The current status
                        is shown as a disabled option whenever this user cannot
                        set it (𐤃 statuses, weitergeleitet) — visible truth, and
                        still not selectable. */}
                    {!STATUS_CONFIG.filter(s => s.userSelectable).filter(visibleTo(isUserSuperAdmin))
                      .some(s => s.value === currentTask.status) && statusConfigFor(currentTask.status) && (
                      <option value={currentTask.status} disabled>{statusConfigFor(currentTask.status).label}</option>
                    )}
                    {STATUS_CONFIG.filter(s => s.userSelectable).filter(visibleTo(isUserSuperAdmin)).map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-actions">
                  <button type="submit" className="button">
                    {currentTask.id ? 'Aktualisieren' : 'Speichern'}
                  </button>
                  <button 
                    type="button" 
                    className="button cancel-button"
                    onClick={() => setShowForm(false)}
                  >
                    Abbrechen
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showConversationModal && (() => {
          // Shared close handler so the X button AND a backdrop click both
          // run the same cleanup (stop audio, drop state).
          const closeConversationModal = () => {
            setShowConversationModal(false);
            setSelectedConversation(null);
            setTaskRecordings([]);
            if (audioRef.current) {
              audioRef.current.pause();
              audioRef.current = null;
            }
            setAudioPlaying(false);
            if (recordingAudioRef.current) {
              recordingAudioRef.current.pause();
              recordingAudioRef.current = null;
            }
            setRecordingPlaying(null);
            if (fullRecordingAudioRef.current) {
              fullRecordingAudioRef.current.pause();
              fullRecordingAudioRef.current = null;
            }
            setFullRecording(null);
            setFullRecordingPlaying(false);
            setFullRecordingCurrentTime(0);
            setFullRecordingDuration(0);
          };
          return (
          <div
            className="conversation-modal"
            onClick={(e) => { if (e.target === e.currentTarget) closeConversationModal(); }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
          >
            <div className="conversation-modal-content" style={{
              backgroundColor: 'white',
              borderRadius: 8,
              maxWidth: '80%',
              maxHeight: '80%',
              width: 600,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '24px 24px 16px 24px', borderBottom: '1px solid #eee', flexShrink: 0 }}>
                <h3 style={{ margin: 0 }}>Vollständige Unterhaltung</h3>
                <button
                  onClick={closeConversationModal}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: 24,
                    cursor: 'pointer',
                    color: '#666'
                  }}
                >
                  ×
                </button>
              </div>

              {/* Full Conversation Recording - Audio Player (fixed in header area) */}
              {!conversationLoading && selectedConversation && !selectedConversation.message &&
                selectedConversation.conversation_data && selectedConversation.conversation_data.length > 0 &&
                fullRecording && (
                <div style={{
                  padding: '12px 24px',
                  backgroundColor: '#f8fafc',
                  borderBottom: '1px solid #e2e8f0',
                  flexShrink: 0
                }}>
                  {/* Top row: Icon, title, play button */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: 10 }}>
                    {/* Audio icon */}
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--color-main)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
                        <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                        <line x1="12" x2="12" y1="19" y2="22"/>
                      </svg>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '0.95rem' }}>
                        Vollständige Gesprächsaufnahme
                      </div>
                    </div>
                    {/* Speed toggle (1× → 1.5× → 2× → 1×) */}
                    <button
                      onClick={cyclePlaybackSpeed}
                      title="Wiedergabegeschwindigkeit"
                      style={{
                        height: '32px',
                        minWidth: '40px',
                        padding: '0 10px',
                        borderRadius: '16px',
                        border: '1px solid #e2e8f0',
                        background: playbackSpeed === 1 ? '#fff' : 'var(--color-secondary)',
                        color: playbackSpeed === 1 ? '#475569' : '#fff',
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        transition: 'all 0.15s ease',
                        flexShrink: 0,
                        marginRight: 8
                      }}
                    >
                      {playbackSpeed}×
                    </button>
                    {/* Play/Pause button */}
                    <button
                      onClick={handlePlayFullRecording}
                      title={fullRecordingPlaying ? 'Pause' : 'Abspielen'}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: fullRecordingPlaying ? '#fef3c7' : '#dcfce7',
                        transition: 'all 0.2s ease',
                        flexShrink: 0
                      }}
                    >
                      {fullRecordingPlaying ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="#d97706">
                          <rect x="6" y="4" width="4" height="16" rx="1" />
                          <rect x="14" y="4" width="4" height="16" rx="1" />
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="#16a34a">
                          <path d="M8 5.14v14.72a1 1 0 0 0 1.5.86l11-7.36a1 1 0 0 0 0-1.72l-11-7.36a1 1 0 0 0-1.5.86z" />
                        </svg>
                      )}
                    </button>
                  </div>

                  {/* Progress bar row */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {/* Current time */}
                    <span style={{ fontSize: '0.75rem', color: '#64748b', minWidth: '36px', textAlign: 'right' }}>
                      {formatTime(fullRecordingCurrentTime)}
                    </span>

                    {/* Progress bar container */}
                    <div
                      ref={progressBarRef}
                      onClick={handleFullRecordingSeek}
                      style={{
                        flex: 1,
                        height: '8px',
                        backgroundColor: '#e2e8f0',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        position: 'relative'
                      }}
                    >
                      {/* Progress fill */}
                      {(() => {
                        const duration = fullRecordingDuration || (fullRecording?.duration_ms / 1000) || 0;
                        const progress = duration > 0 ? (fullRecordingCurrentTime / duration) * 100 : 0;
                        return (
                          <>
                            <div
                              style={{
                                width: `${progress}%`,
                                height: '100%',
                                backgroundColor: 'var(--color-main)',
                                borderRadius: '4px',
                                transition: isDraggingRef.current ? 'none' : 'width 0.1s ease'
                              }}
                            />
                            {/* Draggable handle */}
                            <div
                              onMouseDown={handleProgressDragStart}
                              style={{
                                position: 'absolute',
                                top: '50%',
                                left: `${progress}%`,
                                transform: 'translate(-50%, -50%)',
                                width: '14px',
                                height: '14px',
                                backgroundColor: 'var(--color-main)',
                                borderRadius: '50%',
                                cursor: 'grab',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                transition: isDraggingRef.current ? 'none' : 'left 0.1s ease'
                              }}
                            />
                          </>
                        );
                      })()}
                    </div>

                    {/* Duration */}
                    <span style={{ fontSize: '0.75rem', color: '#64748b', minWidth: '36px' }}>
                      {formatTime(fullRecordingDuration || (fullRecording.duration_ms / 1000))}
                    </span>
                  </div>
                </div>
              )}

              <div style={{ overflow: 'auto', padding: '16px 24px 24px 24px', flex: 1 }}>
              {conversationLoading ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <p>Unterhaltung wird geladen...</p>
                </div>
              ) : selectedConversation ? (
                <div className="conversation-content">
                  {selectedConversation.message ? (
                    <div style={{
                      textAlign: 'center',
                      padding: '24px',
                      backgroundColor: selectedConversation.noConsent ? '#fff3cd' : '#f5f5f5',
                      borderRadius: '8px',
                      border: selectedConversation.noConsent ? '1px solid #ffc107' : '1px solid #e0e0e0'
                    }}>
                      {selectedConversation.noConsent && (
                        <div style={{
                          fontSize: '48px',
                          marginBottom: '12px'
                        }}>
                          🔒
                        </div>
                      )}
                      <p style={{
                        color: selectedConversation.noConsent ? '#856404' : '#666',
                        fontStyle: 'italic',
                        margin: 0,
                        fontSize: '0.95rem'
                      }}>
                        {selectedConversation.message}
                      </p>
                    </div>
                  ) : selectedConversation.conversation_data && selectedConversation.conversation_data.length > 0 ? (
                    <>
                      <div className="conversation-messages">
                        {selectedConversation.conversation_data.map((message, index) => {
                          // The SmartAB-switch note renders as a subtle centered
                          // line between bubbles (like the muted Session-ID footer),
                          // not a full message box.
                          if (message.role === 'system_note') {
                            return (
                              <div key={index} style={{
                                textAlign: 'left',
                                margin: '6px 0',
                                padding: '0 12px',
                                fontSize: '0.78rem',
                                color: '#94a3b8'
                              }}>
                                {message.content}
                              </div>
                            );
                          }
                          return (
                        <div
                          key={index}
                          className={`message ${message.role}`}
                          style={{
                            marginBottom: 16,
                            padding: 12,
                            borderRadius: 8,
                            backgroundColor: message.role === 'user' ? 'var(--message-user-bg)' :
                                           message.role === 'assistant' ? 'var(--message-assistant-bg)' : '#f5f5f5',
                            border: `1px solid ${message.role === 'user' ? '#bbdefb' :
                                                message.role === 'assistant' ? '#e0e0e0' : '#e0e0e0'}`,
                            maxWidth: '100%',
                            width: '100%'
                          }}
                        >
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 4
                          }}>
                            <div style={{
                              fontWeight: 'bold',
                              fontSize: '0.9rem',
                              color: message.role === 'user' ? 'var(--message-user-text)' :
                                     message.role === 'assistant' ? 'var(--message-assistant-text)' : '#666',
                              textTransform: 'capitalize'
                            }}>
                              {message.role === 'user' ? 'Anrufer' :
                               message.role === 'assistant' ? 'Assistent' :
                               (message.role === 'system' || message.role === 'system_note') ? 'System' : message.role}
                            </div>

                            {/* Play button for voicemail messages */}
                            {isVoicemailMessage(message) && (
                              <button
                                onClick={() => playVoicemailAudio(selectedConversation.taskId, selectedConversation.voicemailId)}
                                className={`voicemail-play-btn ${audioPlaying ? 'playing' : 'paused'}`}
                                title={audioPlaying ? "Stoppen" : "Voicemail abspielen"}
                              >
                                {audioPlaying ? '■' : '▶'}
                              </button>
                            )}

                            {/* Play button for call recordings (non-voicemail user messages) */}
                            {message.role === 'user' && !isVoicemailMessage(message) && (() => {
                              // Skip silence markers - no recording exists for these
                              const content = message.content?.trim() || '';
                              if (content === '...' || content === '') return null;

                              const turnNumber = getUserTurnNumber(selectedConversation.conversation_data, index);
                              const recording = getRecordingForTurn(turnNumber);
                              if (!recording || !recording.recording_file_path) return null;
                              const isPlaying = recordingPlaying === recording.id;
                              return (
                                <button
                                  onClick={() => handlePlayRecording(recording.id)}
                                  className={`voicemail-play-btn ${isPlaying ? 'playing' : 'paused'}`}
                                  title={isPlaying ? "Stoppen" : `Aufnahme abspielen (${recording.duration_ms ? Math.round(recording.duration_ms / 1000) + 's' : '?'})`}
                                >
                                  {isPlaying ? '■' : '▶'}
                                </button>
                              );
                            })()}
                          </div>
                          {(() => {
                            // Caller turn: the text without its recovery marker, then faint lines
                            // for how it was transcribed and for the second transcription the
                            // assistant got with it (stt_alt / stt_plain, see annotate_caller_stt).
                            const recovered = recoveredTurn(message);
                            const faintLine = {
                              fontSize: '0.78rem',
                              color: 'var(--text-secondary)',
                              opacity: 0.8,
                              marginTop: 4,
                              lineHeight: 1.4
                            };
                            return (
                              <>
                                <div style={{ lineHeight: 1.5 }}>
                                  {recovered ? recovered.text : message.content}
                                </div>
                                {recovered && <div style={faintLine}>{recovered.caption}</div>}
                                {message.role === 'user' && message.stt_alt && (
                                  <div style={faintLine}>
                                    {message.stt_alt_label || 'Alternative Transkription'}: „{message.stt_alt}“
                                  </div>
                                )}
                                {message.role === 'user' && message.stt_plain && (
                                  <div style={faintLine}>Ohne Praxisvokabular: „{message.stt_plain}“</div>
                                )}
                              </>
                            );
                          })()}
                          {/* Latency indicator - admin only */}
                          {userIsAdmin && message.role === 'assistant' && message.latency_ms && (
                            <div style={{
                              textAlign: 'right',
                              fontSize: '0.75rem',
                              color: '#888',
                              marginTop: 4
                            }}>
                              {(message.latency_ms / 1000).toFixed(1)}s
                            </div>
                          )}
                        </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <p style={{ textAlign: 'center', color: '#666', fontStyle: 'italic' }}>
                      Keine Unterhaltungsnachrichten gefunden.
                    </p>
                  )}

                  {(() => {
                    const convTask = tasks.find(t => t.id === selectedConversation.taskId);
                    const callResult = convTask ? getCallResultNote(convTask.description) : null;
                    if (!callResult) return null;
                    return (
                      <div style={{
                        marginTop: 12,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: '0.85rem',
                        color: '#64748b'
                      }}>
                        <span aria-hidden="true">{NOTE_GLYPH[callResult.status] || '•'}</span>
                        <span><strong style={{ fontWeight: 600 }}>Anrufergebnis:</strong> {callResult.detail}</span>
                      </div>
                    );
                  })()}

                  {selectedConversation.created_at && (
                    <div style={{
                      marginTop: 20,
                      paddingTop: 16,
                      borderTop: '1px solid #eee',
                      fontSize: '0.9rem',
                      color: '#666'
                    }}>
                      <strong>Unterhaltung aufgezeichnet:</strong> {new Date(selectedConversation.created_at).toLocaleString('de-DE')}
                    </div>
                  )}

                  {(() => {
                    const convTask = tasks.find(t => t.id === selectedConversation.taskId);
                    const sessionId = convTask?.sessionId;
                    if (!sessionId) return null;
                    const phoneNumber = isClusterablePhone(convTask?.phoneNumber)
                      ? convTask.phoneNumber
                      : (isClusterablePhone(convTask?.callerIdNumber) ? convTask.callerIdNumber : null);
                    const practiceName = currentPractice?.practice_name || null;
                    // The same text goes into a tracker ticket about this call (openTrackerTicket).
                    const copyText = conversationCopyText({
                      recordedAt: selectedConversation.created_at, sessionId, phoneNumber, practiceName,
                    });
                    return (
                      <>
                        <div className="session-id-line" style={{
                          marginTop: 8,
                          fontSize: '0.78rem',
                          color: '#94a3b8',
                          display: 'flex',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 6
                        }}>
                          <span>Session ID:</span>
                          <span style={{ fontFamily: 'monospace' }}>{sessionId}</span>
                          {copiedItem === `session:${sessionId}` ? (
                            <span className="caller-copied-feedback">Kopiert</span>
                          ) : (
                            <button
                              type="button"
                              className="caller-copy-btn"
                              data-tooltip="Kopieren"
                              aria-label="Datum und Session ID kopieren"
                              onClick={() => handleCopy(copyText, `session:${sessionId}`)}
                            >
                              {actionIcons.copy}
                            </button>
                          )}
                        </div>
                        {phoneNumber && (
                          <div className="session-id-line" style={{
                            marginTop: 4,
                            fontSize: '0.78rem',
                            color: '#94a3b8',
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 6
                          }}>
                            <span>Telefonnummer:</span>
                            <span style={{ fontFamily: 'monospace' }}>{phoneNumber}</span>
                          </div>
                        )}
                        {practiceName && (
                          <div className="session-id-line" style={{
                            marginTop: 4,
                            fontSize: '0.78rem',
                            color: '#94a3b8',
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 6
                          }}>
                            <span>Praxis:</span>
                            <span>{practiceName}</span>
                          </div>
                        )}
                      </>
                    );
                  })()}

                </div>
              ) : (
                <p style={{ textAlign: 'center', color: '#666' }}>
                  Keine Unterhaltungsdaten verfügbar.
                </p>
              )}
              </div>
            </div>
          </div>
          );
        })()}

        {showCallerDataModal && (
          <div className="caller-data-modal" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div className="caller-data-modal-content" style={{
              backgroundColor: 'white',
              borderRadius: 8,
              padding: 24,
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h3 style={{ margin: 0 }}>Anrufer Daten</h3>
                <button
                  onClick={() => {
                    setShowCallerDataModal(false);
                    setSelectedCallerData(null);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: 24,
                    cursor: 'pointer',
                    color: '#666'
                  }}
                >
                  ×
                </button>
              </div>

              {selectedCallerData ? (
                <div className="caller-data-content" style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px'
                }}>
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    border: '1px solid #e0e0e0'
                  }}>
                    <div style={{
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                      color: '#666',
                      marginBottom: '4px'
                    }}>
                      Name
                    </div>
                    <div style={{
                      fontSize: '1rem',
                      color: '#333'
                    }}>
                      {(Array.isArray(selectedCallerData.callerName) ? selectedCallerData.callerName.join(' / ') : selectedCallerData.callerName) || 'Nicht verfügbar'}
                    </div>
                  </div>

                  <div style={{
                    padding: '12px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    border: '1px solid #e0e0e0'
                  }}>
                    <div style={{
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                      color: '#666',
                      marginBottom: '4px'
                    }}>
                      Telefonnummer
                    </div>
                    <div style={{
                      fontSize: '1rem',
                      color: '#333'
                    }}>
                      {selectedCallerData.phoneNumber || 'Nicht verfügbar'}
                    </div>
                  </div>

                  <div style={{
                    padding: '12px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    border: '1px solid #e0e0e0'
                  }}>
                    <div style={{
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                      color: '#666',
                      marginBottom: '4px'
                    }}>
                      Geburtsdatum
                    </div>
                    <div style={{
                      fontSize: '1rem',
                      color: '#333'
                    }}>
                      {selectedCallerData.callerBirthdate
                        ? new Date(selectedCallerData.callerBirthdate).toLocaleDateString('de-DE')
                        : 'Nicht verfügbar'}
                    </div>
                  </div>

                  <div style={{
                    padding: '12px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    border: '1px solid #e0e0e0'
                  }}>
                    <div style={{
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                      color: '#666',
                      marginBottom: '4px'
                    }}>
                      E-Mail
                    </div>
                    <div style={{
                      fontSize: '1rem',
                      color: '#333'
                    }}>
                      {selectedCallerData.callerEmail || 'Nicht verfügbar'}
                    </div>
                  </div>

                  {/* Verordnung row: shown ONLY when the prescription question was
                      actually answered (true/false). null = never discussed → hidden. */}
                  {(selectedCallerData.hasVerordnung === true || selectedCallerData.hasVerordnung === false) && (
                  <div style={{
                    padding: '12px',
                    backgroundColor: 'var(--message-user-bg)',
                    borderRadius: '6px',
                    border: '1px solid #90caf9'
                  }}>
                    <div style={{
                      fontWeight: 'bold',
                      fontSize: '0.95rem',
                      color: 'var(--message-user-text)',
                      marginBottom: '8px'
                    }}>
                      Verordnung
                    </div>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px'
                    }}>
                      <div style={{ fontSize: '0.9rem', color: '#333' }}>
                        <strong>Verordnung:</strong> {selectedCallerData.hasVerordnung ? 'Ja' : 'Nein'}
                      </div>
                      {selectedCallerData.hasVerordnung ? (
                        <>
                          <div style={{ fontSize: '0.9rem', color: '#333' }}>
                            <strong>Behandlungsart:</strong> {selectedCallerData.verordnungType || 'Nicht angegeben'}
                          </div>
                          <div style={{ fontSize: '0.9rem', color: '#333' }}>
                            <strong>Sitzungen:</strong> {selectedCallerData.verordnungSessions || 'Nicht angegeben'}
                          </div>
                          {selectedCallerData.verordnungIssueDate && (
                            <div style={{ fontSize: '0.9rem', color: '#333' }}>
                              <strong>Ausstellungsdatum:</strong> {formatIssueDate(selectedCallerData.verordnungIssueDate)}
                            </div>
                          )}
                        </>
                      ) : (
                        selectedCallerData.insuranceType && (
                          <div style={{ fontSize: '0.9rem', color: '#333' }}>
                            <strong>Verordnungstyp:</strong> {selectedCallerData.insuranceType}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                  )}

                  {/* Terminwunsch row: caller's stated time preference (read-only).
                      Hidden when empty/absent, like the Verordnung block. */}
                  {selectedCallerData.appointmentPreference && (
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    border: '1px solid #e0e0e0'
                  }}>
                    <div style={{
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                      color: '#666',
                      marginBottom: '4px'
                    }}>
                      Terminwunsch
                    </div>
                    <div style={{
                      fontSize: '1rem',
                      color: '#333'
                    }}>
                      {selectedCallerData.appointmentPreference}
                    </div>
                  </div>
                  )}
                </div>
              ) : (
                <p style={{ textAlign: 'center', color: '#666' }}>
                  Keine Anruferdaten verfügbar.
                </p>
              )}
            </div>
          </div>
        )}

        {sentMessageTask && renderSentMessageModal()}

        {showShortcutHelp && (
          <div
            className="shortcut-help-backdrop"
            onClick={(e) => { if (e.target === e.currentTarget) setShowShortcutHelp(false); }}
          >
            <div className="shortcut-help-modal" onClick={(e) => e.stopPropagation()}>
              <div className="shortcut-help-header">
                <h3 style={{ margin: 0 }}>Tastenkürzel</h3>
                <button
                  type="button"
                  className="shortcut-help-close"
                  onClick={() => setShowShortcutHelp(false)}
                  aria-label="Schließen"
                >×</button>
              </div>
              <div className="shortcut-help-body">
                <div className="shortcut-help-columns">
                  <div>
                    <div className="shortcut-help-column-title">Allgemein</div>
                    <div className="shortcut-help-row"><span className="keys"><kbd>{isMac ? '⌘' : 'Strg'}</kbd><kbd>K</kbd></span><span>Aufgabensuche von überall öffnen</span></div>
                    <div className="shortcut-help-row"><span className="keys"><kbd>?</kbd></span><span>Diese Übersicht ein-/ausblenden</span></div>
                    <div className="shortcut-help-row"><span className="keys"><kbd>Esc</kbd></span><span>Dialog / Panel schließen, Suche leeren</span></div>
                    <div className="shortcut-help-row"><span className="keys"><kbd>N</kbd></span><span>Neue Aufgabe anlegen</span></div>
                  </div>
                  <div>
                    <div className="shortcut-help-column-title">Mit Maus auf einer Zeile</div>
                    <div className="shortcut-help-row"><span className="keys"><kbd>0</kbd></span><span>Abschließen: Cluster komplett, sonst nur diese Aufgabe</span></div>
                    <div className="shortcut-help-row"><span className="keys"><kbd>1</kbd></span><span>Status: Abgeschlossen</span></div>
                    <div className="shortcut-help-row"><span className="keys"><kbd>2</kbd></span><span>Status: In Bearbeitung</span></div>
                    <div className="shortcut-help-row"><span className="keys"><kbd>3</kbd></span><span>Status: Nicht erreicht</span></div>
                    <div className="shortcut-help-row"><span className="keys"><kbd>4</kbd></span><span>Status: Ausstehend</span></div>
                    <div className="shortcut-help-row"><span className="keys"><kbd>E</kbd></span><span>Aufgabe öffnen / bearbeiten</span></div>
                    <div className="shortcut-help-row"><span className="keys"><kbd>S</kbd></span><span>Status-Dropdown öffnen</span></div>
                    <div className="shortcut-help-row"><span className="keys"><kbd>C</kbd></span><span>Anrufer anrufen</span></div>
                    <div className="shortcut-help-row"><span className="keys"><kbd>M</kbd></span><span>E-Mail weiterleiten</span></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </Layout>
  );
};

export default TasksPage;